import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { useLang, Lang } from "../i18n/LanguageContext";
import { requestPay } from "../utils/pay";
import { saveReport, isInAppBrowser } from "../utils/savePdf";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FBF6EA; font-family: 'Pretendard', "Noto Sans KR", sans-serif; }
  @media print {
    @page { size: A4; margin: 12mm 15mm; }
    body {
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    /* 숨길 요소 */
    .no-print { display: none !important; }
    /* 접힌 대운 섹션 강제 펼침 */
    * {
      max-height: none !important;
      overflow: visible !important;
      animation: none !important;
      transition: none !important;
    }
    /* fixed/sticky 요소 제거 */
    [style*="position: fixed"],
    [style*="position:fixed"] {
      display: none !important;
    }
    /* 페이지 나눔 방지 */
    section, article, .card, div[class*="Card"], div[class*="Block"] {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    /* 배경색·그라디언트 인쇄 허용 */
    div, span, p, td, th {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const DancheongBarR = styled.div`
  height: 6px;
  width: 100%;
  background: #FBF6EA;
`;

const Wrap = styled.div`
  min-height: 100vh;
  padding: 0 0 80px;
  max-width: 520px;
  margin: 0 auto;
  color: #2B2A26;
  background: #FBF6EA;
`;

/* ── 헤더 ── */
const HeroWrap = styled.div`
  position: relative;
  background: #2B2A26;
  padding: 36px 24px 32px;
  text-align: center;
  overflow: hidden;
  border-radius: 0 0 28px 28px;
  &::before {
    content: "";
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 40% at 50% 0%, rgba(200,144,30,0.3) 0%, transparent 70%);
  }
`;
const glow = keyframes`0%,100%{opacity:.85}50%{opacity:1}`;
const HeroCharImg = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 50%;
  border: 2.5px solid rgba(200,144,30,0.7);
  background: rgba(255,248,230,0.15);
  margin-bottom: 12px;
  animation: ${glow} 3s ease-in-out infinite;
  position: relative;
  z-index: 1;
`;
const HeroOrb = styled.div`font-size:2.5rem;margin-bottom:10px;animation:${glow} 3s ease-in-out infinite;position:relative;z-index:1;`;
const HeroTitle = styled.h1`
  font-size:1.4rem;font-weight:900;color:#FBEFCF;letter-spacing:-0.01em;margin-bottom:6px;
  position:relative;z-index:1;
`;
const HeroSub = styled.p`font-size:0.82rem;color:rgba(255,255,255,0.6);position:relative;z-index:1;`;
const HeroBadge = styled.div<{$compat?:boolean}>`
  display:inline-block;
  margin-top:12px;
  padding:5px 16px;
  border-radius:20px;
  font-size:12px;
  font-weight:700;
  background:rgba(200,144,30,0.25);
  border:1px solid rgba(200,144,30,0.6);
  color:#f0d4a8;
  position:relative;z-index:1;
`;

/* ── 공통 카드 ── */
const Card = styled.div`
  background:#fff;border:1.5px solid #2B2A26;border-radius:16px;
  padding:20px;margin:16px 16px 0;
`;
const CardLabel = styled.div`
  font-size:.7rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
  color:#8C8677;margin-bottom:14px;
`;
const IGrid = styled.div`display:grid;grid-template-columns:auto 1fr;gap:8px 16px;`;
const IK = styled.div`font-size:.82rem;color:#8C8677;`;
const IV = styled.div`font-size:.85rem;color:#2B2A26;font-weight:600;`;

/* ── 오행 비교 다이어그램 ── */
const OeCompWrap = styled.div`
  background:#fff;border:1px solid #2B2A26;border-radius:16px;
  margin:12px 16px 0;padding:18px;
`;
const OeCompTitle = styled.div`font-size:.7rem;font-weight:700;letter-spacing:.1em;color:#8C8677;margin-bottom:14px;text-transform:uppercase;`;
const HjScoreGrid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px;`;
const HjCell = styled.div`
  background:#FBF6EA;border:1px solid #2B2A26;border-radius:10px;padding:10px;text-align:center;
`;
const HjLabel = styled.div`font-size:.68rem;color:#8C8677;margin-bottom:4px;`;
const HjVal = styled.div<{$good:boolean}>`font-size:.88rem;font-weight:700;color:${p=>p.$good?"#5E8A4E":"#8C8677"};`;

/* ── 궁합 전용 ── */
const CompatHero = styled.div`
  background:#2B2A26;
  border:1px solid rgba(192,154,107,0.3);
  border-radius:16px;
  margin:16px 16px 0;
  padding:20px;
  text-align:center;
`;
const CompatNames = styled.div`
  font-size:1.5rem;font-weight:900;color:#FBEFCF;
  display:flex;align-items:center;justify-content:center;gap:14px;margin-bottom:8px;
`;
const CompatArrow = styled.span`font-size:1rem;color:#B8862A;`;
const TotalScoreWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;margin:16px 0 8px;
`;
const TotalScoreNum = styled.div<{$score:number}>`
  font-size:3.5rem;font-weight:900;
  color:${p=>p.$score>=80?"#7eefc4":p.$score>=60?"#ffd97d":"#ff9090"};
  line-height:1;
  text-shadow:0 2px 12px rgba(0,0,0,0.3);
`;
const TotalScoreLabel = styled.div`font-size:.75rem;color:rgba(255,255,255,0.5);margin-top:4px;`;

const CompatItemCard = styled.div<{$checked:boolean}>`
  background:${p=>p.$checked?"#fff":"#FBF6EA"};
  border:1px solid ${p=>p.$checked?"#EAE3D2":"#ece5dd"};
  border-radius:14px;
  margin:12px 16px 0;
  overflow:hidden;
  opacity:${p=>p.$checked?1:0.5};
`;
const CompatItemHeader = styled.div`
  display:flex;align-items:center;gap:12px;padding:14px 18px;
`;
const CompatItemIcon = styled.div`font-size:1.2rem;`;
const CompatItemName = styled.div`flex:1;font-size:.9rem;font-weight:700;color:#2B2A26;`;
const ScoreBadge = styled.div<{$score:number}>`
  font-size:.75rem;font-weight:700;padding:3px 10px;border-radius:12px;
  background:${p=>p.$score>=80?"#E7F0E2":p.$score>=60?"#fef3e2":"#FBE0D9"};
  border:1px solid ${p=>p.$score>=80?"#E7F0E2":p.$score>=60?"#f0c070":"#f0a0a0"};
  color:${p=>p.$score>=80?"#5E8A4E":p.$score>=60?"#b87a20":"#D0654F"};
`;
const CompatBarWrap = styled.div`height:4px;background:#FBF6EA;margin:0 18px 0;border-radius:2px;overflow:hidden;`;
const CompatBar = styled.div<{$score:number}>`
  height:100%;width:${p=>p.$score}%;
  background:${p=>p.$score>=80?"#4a9d6f":p.$score>=60?"#B8862A":"#c06050"};
  border-radius:2px;transition:width 1.2s ease;
`;
const CompatItemBody = styled.div`padding:14px 18px;border-top:1px solid #2B2A26;`;
const CompatItemText = styled.div`font-size:.82rem;color:#8C8677;line-height:1.8;white-space:pre-wrap;`;

const DangerItem = styled.div`
  display:flex;gap:10px;padding:8px 0;
`;
const DangerYear = styled.div`font-size:.85rem;font-weight:700;color:#D0654F;min-width:60px;`;
const DangerInfo = styled.div`font-size:.8rem;color:#8C8677;line-height:1.6;`;

const DeepCard = styled.div`
  background:#fff;border:1px solid #2B2A26;border-radius:14px;
  padding:16px 18px;margin:12px 16px 0;
`;
const DeepLabel = styled.div`font-size:.7rem;font-weight:700;color:#8C8677;text-transform:uppercase;letter-spacing:.08em;margin-bottom:8px;`;
const DeepText = styled.div`font-size:.82rem;color:#5a4a3a;line-height:1.8;margin-top:4px;white-space:pre-wrap;`;

/* ── 대운 (일반 운세용) ── */
const DaeunCard = styled.div<{open:boolean}>`
  background:#fff;border:1px solid ${p=>p.open?"#B8862A":"#EAE3D2"};
  border-radius:16px;margin:12px 16px 0;overflow:hidden;
  transition:box-shadow .25s,border-color .25s;
`;
const DaeunHeader = styled.div<{open:boolean;isGil:boolean}>`
  display:flex;align-items:center;gap:14px;padding:16px 18px;cursor:pointer;
  background:${p=>p.open?"#fdf6ee":"transparent"};
`;
const AgeTag = styled.div<{isGil:boolean}>`
  min-width:52px;text-align:center;
  background:${p=>p.isGil?"#E7F0E2":"#FBE0D9"};
  border:1px solid ${p=>p.isGil?"#E7F0E2":"#f0a0a0"};
  border-radius:8px;padding:5px 8px;font-size:.7rem;font-weight:700;
  color:${p=>p.isGil?"#5E8A4E":"#D0654F"};line-height:1.3;
`;
const DaeunInfo = styled.div`flex:1;`;
const DaeunGanji = styled.div`font-size:1.1rem;font-weight:800;color:#2B2A26;letter-spacing:.04em;`;
const DaeunOe = styled.div`font-size:.72rem;color:#8C8677;margin-top:2px;`;
const ChevronIcon = styled.div<{open:boolean}>`
  font-size:.9rem;color:#B8862A;
  transform:rotate(${p=>p.open?"180deg":"0deg"});transition:transform .25s;
  @media print { display:none; }
`;
const PrintBtn = styled.button`
  position:fixed;bottom:28px;right:20px;
  background:#2B2A26;color:#FBEFCF;
  border:none;border-radius:50px;
  padding:12px 22px;font-size:.85rem;font-weight:700;
  cursor:pointer;
  z-index:200;letter-spacing:.03em;
  &:hover{background:#2B2A26;}
  @media print{display:none;}
`;
/* 리포트 보관 안내 — 재발급이 안 되므로 저장을 강하게 안내 */
const SaveNotice = styled.div`
  margin:0 16px 12px;
  padding:14px 16px;
  background:#FFF3BF;
  border:2px solid #E8590C;
  border-radius:12px;
  color:#7C2D12;
  @media print{display:none;}
`;
const SaveNoticeTitle = styled.div`
  font-size:.92rem;font-weight:900;letter-spacing:-.01em;margin-bottom:4px;
`;
const SaveNoticeBody = styled.div`
  font-size:.8rem;line-height:1.6;
`;
const SaveNoticeBtn = styled.button`
  margin-top:10px;width:100%;
  background:#E8590C;color:#fff;
  border:none;border-radius:10px;
  padding:11px 16px;font-size:.85rem;font-weight:800;
  cursor:pointer;
  &:hover{background:#D9480F;}
`;
const ExpireBanner = styled.div<{$urgent:boolean}>`
  margin:0 16px 14px;
  padding:12px 16px;
  background:${p=>p.$urgent?"#fff3cd":"#e8f4e8"};
  border:1.5px solid ${p=>p.$urgent?"#ffc107":"#b8d8b8"};
  border-radius:10px;
  font-size:.8rem;
  color:${p=>p.$urgent?"#7d5a00":"#2d5a2d"};
  line-height:1.5;
  @media print{display:none;}
`;
const DaeunBody = styled.div<{open:boolean}>`
  max-height:${p=>p.open?"2400px":"0"};overflow:hidden;transition:max-height .45s ease;
  @media print {
    max-height:none !important;
    overflow:visible !important;
    display:block !important;
    height:auto !important;
  }
`;
const DaeunInner = styled.div`padding:0 18px 18px;display:flex;flex-direction:column;gap:10px;`;
const ThemeBlock = styled.div`background:#FBF6EA;border-radius:12px;padding:14px;border:1px solid #2B2A26;`;
const ThemeHeader2 = styled.div`display:flex;align-items:center;gap:10px;margin-bottom:10px;`;
const GradeTag = styled.div<{score:number}>`
  font-size:.68rem;font-weight:700;padding:2px 8px;border-radius:20px;
  background:${p=>p.score>=80?"#E7F0E2":p.score>=65?"#edf5e8":p.score>=50?"#fef3e2":p.score>=35?"#FBE0D9":"#fde8e8"};
  border:1px solid ${p=>p.score>=80?"#E7F0E2":p.score>=65?"#b0d098":p.score>=50?"#f0c070":p.score>=35?"#D0654F":"#e88080"};
  color:${p=>p.score>=80?"#5E8A4E":p.score>=65?"#3a7030":p.score>=50?"#8b5e10":p.score>=35?"#903020":"#7a1010"};
`;
const ScoreNum2 = styled.div<{score:number}>`
  font-size:1.1rem;font-weight:900;min-width:32px;text-align:right;
  color:${p=>p.score>=80?"#5E8A4E":p.score>=65?"#3a7030":p.score>=50?"#8b5e10":p.score>=35?"#D0654F":"#8a1010"};
`;
const BarWrap = styled.div`height:5px;background:#FBF6EA;border-radius:3px;overflow:hidden;margin-bottom:10px;`;
const Bar = styled.div<{score:number}>`
  height:100%;width:${p=>p.score}%;
  background:${p=>p.score>=80?"#4a9d6f":p.score>=65?"#5a9040":p.score>=50?"#B8862A":p.score>=35?"#c06050":"#a03030"};
  border-radius:3px;transition:width 1.2s ease;
`;
const EventList = styled.ul`list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:5px;`;
const EventItem = styled.li<{isGood?:boolean}>`
  font-size:.78rem;color:#8C8677;padding-left:14px;position:relative;line-height:1.5;
  &::before{content:"•";position:absolute;left:0;color:${p=>p.isGood?"#5E8A4E":"#D0654F"};font-size:.9rem;}
`;

const CommentBox = styled.div`font-size:.85rem;color:#5a4a3a;line-height:1.8;white-space:pre-wrap;`;
const LoadingWrap = styled.div`display:flex;align-items:center;justify-content:center;height:60vh;font-size:.95rem;color:#8C8677;`;

/* ── 세운 카드 ── */
const SaeunWrap = styled.div`margin:8px 16px 0;`;
const SaeunSectionTitle = styled.div`
  font-size:.68rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;
  color:#8C8677;margin:20px 0 8px;padding:0 2px;
`;
const SaeunCard2 = styled.div<{$yong:boolean;$gi:boolean}>`
  background:${p=>p.$yong&&!p.$gi?"#E7F0E2":p.$gi&&!p.$yong?"#fdf0ee":"#fff"};
  border:1px solid ${p=>p.$yong&&!p.$gi?"#E7F0E2":p.$gi&&!p.$yong?"#D0654F":"#EAE3D2"};
  border-radius:14px;padding:14px 16px;margin-bottom:10px;
`;
const SaeunYear2 = styled.div`font-size:.88rem;font-weight:800;color:#2B2A26;margin-bottom:8px;`;
const SaeunScoreBar = styled.div`display:flex;flex-direction:column;gap:6px;margin-bottom:8px;`;
const SaeunScoreRow2 = styled.div`display:flex;align-items:center;gap:8px;`;
const SaeunScoreLabel2 = styled.div`font-size:.7rem;color:#8C8677;min-width:40px;`;
const SaeunBar2 = styled.div<{$score:number}>`
  flex:1;height:5px;border-radius:3px;background:#FBF6EA;overflow:hidden;
  &::after{content:"";display:block;height:100%;width:${p=>p.$score}%;border-radius:3px;
  background:${p=>p.$score>=70?"#5E8A4E":p.$score>=50?"#B8862A":"#D0654F"};transition:width 1s}
`;
const SaeunScoreVal2 = styled.div<{$score:number}>`
  font-size:.72rem;font-weight:700;min-width:24px;text-align:right;
  color:${p=>p.$score>=70?"#5E8A4E":p.$score>=50?"#8C8677":"#D0654F"};
`;
const SaeunEvents2 = styled.ul`list-style:none;padding:0;margin:0;`;
const SaeunEvent2 = styled.li`font-size:.76rem;color:#8C8677;padding-left:12px;position:relative;line-height:1.5;
  &::before{content:"·";position:absolute;left:0;color:#B8862A;}`;

/* ── 월운 카드 ── */
const WolunGrid = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:8px;`;
const WolunCell = styled.div<{$yong:boolean;$gi:boolean}>`
  background:${p=>p.$yong&&!p.$gi?"#E7F0E2":p.$gi&&!p.$yong?"#fdf0ee":"#FBF6EA"};
  border:1px solid ${p=>p.$yong&&!p.$gi?"#E7F0E2":p.$gi&&!p.$yong?"#D0654F":"#ede6dc"};
  border-radius:10px;padding:10px 8px;text-align:center;
`;
const WolunLabel2 = styled.div`font-size:.68rem;color:#8C8677;margin-bottom:5px;`;
const WolunScores = styled.div`display:flex;flex-direction:column;gap:2px;`;
const WolunScore = styled.div<{$score:number}>`
  font-size:.65rem;
  color:${p=>p.$score>=70?"#5E8A4E":p=>p.$score>=50?"#8C8677":"#D0654F"};
`;

/* ── 스토리텔링 인트로 카드 ── */
const StoryCard = styled.div<{$color:string}>`
  background: ${p=>p.$color}12;
  border: 1.5px solid ${p=>p.$color}40;
  border-radius: 16px;
  padding: 18px 20px;
  margin: 12px 16px 0;
`;
const StoryScenarioTitle = styled.div`
  font-size: .78rem; font-weight: 800; color: #2B2A26;
  margin-bottom: 10px; letter-spacing: -.01em;
`;
const StoryScenarioItem = styled.div`
  display: flex; gap: 10px; margin-bottom: 8px;
  font-size: .78rem; line-height: 1.6; color: #2B2A26;
`;
const StoryScenarioLabel = styled.span`
  min-width: 70px; font-weight: 700; color: #8C8677; flex-shrink: 0;
`;

/* ── 날짜 분석 (결제 블러) ── */
const DateAnalysisWrap = styled.div`
  margin: 12px 16px 0;
  background: #fff;
  border: 1.5px solid #2B2A26;
  border-radius: 16px;
  overflow: hidden;
`;
const DateAnalysisHeader = styled.div<{$color:string}>`
  background: ${p=>p.$color}18;
  border-bottom: 2px solid ${p=>p.$color}30;
  padding: 14px 18px 12px;
`;
const DateAnalysisTitle = styled.div<{$color:string}>`
  font-size: .82rem; font-weight: 800; color: ${p=>p.$color};
  margin-bottom: 2px;
`;
const DateAnalysisSub = styled.div`font-size: .72rem; color: #8C8677;`;
const DateAnalysisBody = styled.div`padding: 14px 18px 18px;`;
const DateRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 0; border-bottom: 1px solid #2B2A26;
  &:last-child { border-bottom: none; }
`;
const DateRank = styled.div<{$color:string}>`
  min-width: 24px; height: 24px; border-radius: 50%;
  background: ${p=>p.$color}20; border: 1.5px solid ${p=>p.$color}60;
  display: flex; align-items: center; justify-content: center;
  font-size: .72rem; font-weight: 800; color: ${p=>p.$color}; flex-shrink: 0;
`;
const DateContent = styled.div`flex: 1;`;
const DateLabel = styled.div`font-size: .78rem; font-weight: 700; color: #2B2A26; margin-bottom: 2px;`;
const DateSub = styled.div`font-size: .7rem; color: #8C8677;`;
const DateBlurWrap = styled.div<{$locked:boolean}>`
  display: inline-block;
  ${p => p.$locked ? `filter: blur(5px); user-select: none; pointer-events: none;` : ""}
  transition: filter 0.4s;
`;
const PayCTA = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  background: #fff9f3;
  border: 1.5px dashed #B8862A;
  border-radius: 12px;
  text-align: center;
`;
const PayCTAText = styled.div`font-size: .78rem; color: #2B2A26; line-height: 1.6; margin-bottom: 10px;`;
const PayBtn = styled.button<{$color:string}>`
  background: ${p=>p.$color};
  color: #fff;
  border: 2px solid #2B2A26;
  border-radius: 14px;
  padding: 10px 24px;
  font-size: .82rem; font-weight: 800;
  cursor: pointer; font-family: inherit;
  &:hover { opacity: .9; }
`;
const RecomWrap = styled.div`margin-top: 14px; padding-top: 14px; border-top: 1px solid #2B2A26;`;
const RecomTitle = styled.div`font-size: .72rem; font-weight: 700; color: #8C8677; margin-bottom: 8px;`;
const RecomChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const RecomChip = styled.span<{$color:string}>`
  padding: 4px 10px; border-radius: 14px;
  background: ${p=>p.$color}12; border: 1px solid ${p=>p.$color}30;
  font-size: .7rem; color: ${p=>p.$color}; font-weight: 600;
`;

/* ── 상수 ── */
const API = process.env.REACT_APP_API_URL || "";
const OE_COLOR: Record<string,string> = {목:"#2E86AB",화:"#D0654F",토:"#F4A261",금:"#A8A8A8",수:"#1D3557"};
const OE_KOR:   Record<string,string> = {목:"木",화:"火",토:"土",금:"金",수:"水"};
// 오행 영문명 (영어 리포트용)
const OE_EN:    Record<string,string> = {목:"Wood",화:"Fire",토:"Earth",금:"Metal",수:"Water"};
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const STEM_OHAENG: Record<string,string> = {
  甲:"목",乙:"목",丙:"화",丁:"화",戊:"토",己:"토",庚:"금",辛:"금",壬:"수",癸:"수",
};
const BRANCH_OHAENG: Record<string,string> = {
  寅:"목",卯:"목",巳:"화",午:"화",辰:"토",戌:"토",丑:"토",未:"토",申:"금",酉:"금",亥:"수",子:"수",
};
const STEM_INFO_R: Record<string,{hangul:string;yinYang:boolean}> = {
  甲:{hangul:"갑",yinYang:true}, 乙:{hangul:"을",yinYang:false},
  丙:{hangul:"병",yinYang:true}, 丁:{hangul:"정",yinYang:false},
  戊:{hangul:"무",yinYang:true}, 己:{hangul:"기",yinYang:false},
  庚:{hangul:"경",yinYang:true}, 辛:{hangul:"신",yinYang:false},
  壬:{hangul:"임",yinYang:true}, 癸:{hangul:"계",yinYang:false},
};
const BRANCH_INFO_R: Record<string,{ohaeng:string;yinYang:boolean;jijanggan:string[]}> = {
  子:{ohaeng:"수",yinYang:true, jijanggan:["壬","癸"]},
  丑:{ohaeng:"토",yinYang:false,jijanggan:["癸","辛","己"]},
  寅:{ohaeng:"목",yinYang:true, jijanggan:["戊","丙","甲"]},
  卯:{ohaeng:"목",yinYang:false,jijanggan:["甲","乙"]},
  辰:{ohaeng:"토",yinYang:true, jijanggan:["乙","癸","戊"]},
  巳:{ohaeng:"화",yinYang:false,jijanggan:["戊","庚","丙"]},
  午:{ohaeng:"화",yinYang:true, jijanggan:["丙","己","丁"]},
  未:{ohaeng:"토",yinYang:false,jijanggan:["丁","乙","己"]},
  申:{ohaeng:"금",yinYang:true, jijanggan:["戊","壬","庚"]},
  酉:{ohaeng:"금",yinYang:false,jijanggan:["庚","辛"]},
  戌:{ohaeng:"토",yinYang:true, jijanggan:["辛","丁","戊"]},
  亥:{ohaeng:"수",yinYang:false,jijanggan:["戊","甲","壬"]},
};
const _60_STEMS_R   = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const _60_BRANCHES_R = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
function getDayGanzhi(year:number,month:number,day:number):{gan:string;ji:string}{
  const refMs=new Date(1900,0,1).getTime();
  const tgtMs=new Date(year,month-1,day).getTime();
  const diff=Math.round((tgtMs-refMs)/86400000);
  const idx=((10+diff)%60+60)%60;
  return{gan:_60_STEMS_R[idx%10],ji:_60_BRANCHES_R[idx%12]};
}
const CYCLE5_R=["목","화","토","금","수"];
function ohGenR(a:string,b:string){return CYCLE5_R[(CYCLE5_R.indexOf(a)+1)%5]===b;}
function ohCtlR(a:string,b:string){return CYCLE5_R[(CYCLE5_R.indexOf(a)+2)%5]===b;}
function calcTenStar(dayGan:string,targetChar:string):string{
  const dayOe=STEM_OHAENG[dayGan];
  const dayYY=STEM_INFO_R[dayGan]?.yinYang;
  if(!dayOe||dayYY===undefined)return"—";
  let tOe:string|undefined,tYY:boolean|undefined;
  if(STEM_INFO_R[targetChar]){tOe=STEM_OHAENG[targetChar];tYY=STEM_INFO_R[targetChar].yinYang;}
  else if(BRANCH_INFO_R[targetChar]){
    const b=BRANCH_INFO_R[targetChar];tOe=b.ohaeng;
    const bonggi=b.jijanggan[b.jijanggan.length-1];
    tYY=STEM_INFO_R[bonggi]?.yinYang??b.yinYang;
  }
  if(!tOe||tYY===undefined)return"—";
  const same=dayYY===tYY;
  if(tOe===dayOe)       return same?"비견":"겁재";
  if(ohGenR(dayOe,tOe)) return same?"식신":"상관";
  if(ohCtlR(dayOe,tOe)) return same?"편재":"정재";
  if(ohGenR(tOe,dayOe)) return same?"편인":"정인";
  if(ohCtlR(tOe,dayOe)) return same?"편관":"정관";
  return"—";
}

const COMPAT_ICONS: Record<string,string> = {
  "성격궁합":"🧭","속궁합":"🔥","주의점":"⚠️",
  "결혼운/가치관":"","자녀운":"👶","이별수/위험시기":"⚡",
  "심층속궁합":"","리스크관리":"🛡️",
};
const DEFAULT_COMPAT_ITEMS_R: Record<string,boolean> = {
  "성격궁합":true,"속궁합":true,"주의점":true,
};

function gradeLabel(s:number){
  if(s>=85)return"대길";if(s>=70)return"길";if(s>=55)return"소길";
  if(s>=40)return"보통";if(s>=25)return"소흉";return"흉";
}
function isPos(t:string){
  return !["주의","위험","조심","금물","피해","손실","갈등","분쟁","배신","약한","장애","충돌","막힘","방해","상처","부채","파경"].some(w=>t.includes(w));
}

// ── 자연어 내러티브 생성 ─────────────────────────────────────────────────────
type NarrativeTier = { good: string[]; mid: string[]; bad: string[] };
const NARRATIVE_SCRIPTS: Record<string, NarrativeTier> = {
  love: {
    good: [
      "새로운 이성 인연이 찾아올 가능성이 높은 시기입니다.",
      "솔로라면 적극적으로 만남의 기회를 만들어 보세요.",
      "현재 만나는 분이 있다면 관계가 한 단계 더 깊어질 수 있습니다.",
    ],
    mid: [
      "연애에 큰 변화보다는 안정적인 흐름의 시기입니다.",
      "현재 만나는 분이 있다면 관계를 천천히 다져가기에 좋습니다.",
      "서두르지 않고 자연스러운 인연의 흐름을 따라가는 것이 좋습니다.",
    ],
    bad: [
      "이성 관계에서 갈등이나 어려움이 생길 수 있는 시기입니다.",
      "현재 만나는 분이 있다면 작은 오해가 이별로 이어질 수 있으니 소통에 각별히 신경 써 주세요.",
      "새로운 만남보다는 기존 관계를 돌아보는 시간이 필요합니다.",
    ],
  },
  marry: {
    good: [
      "결혼의 인연이 맺어질 수 있는 좋은 흐름의 시기입니다.",
      "배우자와의 관계가 깊어지거나 결혼 이야기가 구체적으로 진전될 수 있습니다.",
      "결혼을 준비 중이라면 이 시기에 적극적으로 움직여 보세요.",
    ],
    mid: [
      "결혼에 관한 이야기가 오가더라도 서두르지 않는 것이 좋습니다.",
      "현재의 관계를 충분히 다져가며 서로를 더 깊이 알아가는 시기입니다.",
    ],
    bad: [
      "결혼 진행에 방해 요소가 생기거나 의견 충돌이 있을 수 있는 시기입니다.",
      "배우자 또는 결혼 상대와 갈등이 생길 수 있으니 감정적인 결정을 피하세요.",
      "무리하게 진행하기보다 관계를 안정시키는 것을 우선하세요.",
    ],
  },
  business: {
    good: [
      "사업 기회와 계약이 활발히 들어오는 시기입니다.",
      "새로운 파트너십이나 사업 확장을 시도하기에 좋습니다.",
      "적극적인 영업과 네트워킹 활동이 좋은 결과를 가져올 수 있습니다.",
    ],
    mid: [
      "사업에 무리한 확장보다는 안정적인 운영에 집중하는 것이 좋습니다.",
      "기존 사업의 내실을 다지고 준비를 탄탄히 하는 시기입니다.",
    ],
    bad: [
      "사업에 예상치 못한 손실이나 분쟁이 생길 수 있는 시기입니다.",
      "큰 투자나 새로운 사업 시작은 충분히 검토 후 신중하게 결정하세요.",
      "계약서 등 중요 문서는 꼼꼼하게 확인하고, 리스크 관리에 집중하세요.",
    ],
  },
  career: {
    good: [
      "승진·이직·취업의 기회가 활발하게 열리는 시기입니다.",
      "직장에서 능력을 인정받고 도약할 기회가 찾아옵니다.",
      "목표하는 직업 변화가 있다면 이 시기를 적극 활용하세요.",
    ],
    mid: [
      "현재 직장에서 실력을 쌓으며 기회를 준비하는 시기입니다.",
      "큰 변화보다는 꾸준한 성과 관리로 기반을 다지는 것이 중요합니다.",
    ],
    bad: [
      "직장에서 갈등이나 불안정한 상황이 생길 수 있는 시기입니다.",
      "충동적인 이직이나 독립은 피하고, 신중하게 상황을 지켜보세요.",
      "직장 내 인간관계에서 오해가 생기지 않도록 언행에 주의하세요.",
    ],
  },
  wealth: {
    good: [
      "재물이 들어오는 흐름이 좋은 시기입니다.",
      "적극적인 재테크나 사업 활동이 결실을 맺을 수 있습니다.",
      "수입이 늘어나거나 예상치 못한 재물이 들어올 수 있습니다.",
    ],
    mid: [
      "재물 흐름이 안정적으로 유지되는 시기입니다.",
      "큰 지출이나 투자는 충분히 검토한 후 결정하는 것이 좋습니다.",
    ],
    bad: [
      "예상치 못한 지출이나 금전 손실이 있을 수 있는 시기입니다.",
      "불필요한 지출을 줄이고 저축과 고정 지출 관리에 집중하세요.",
      "보증이나 대출 등 금전 관련 보증은 이 시기에 피하는 것이 좋습니다.",
    ],
  },
  health: {
    good: [
      "건강 상태가 안정적이고 활력이 넘치는 시기입니다.",
      "꾸준한 운동과 관리로 좋은 컨디션을 유지하세요.",
    ],
    mid: [
      "건강에 큰 문제는 없지만 과로하지 않도록 주의가 필요합니다.",
      "규칙적인 생활 습관과 충분한 수면을 유지하는 것이 중요합니다.",
    ],
    bad: [
      "과로나 스트레스로 건강을 해칠 수 있는 시기입니다.",
      "수면과 영양 관리에 특히 신경 쓰고, 몸에 이상 신호가 오면 즉시 확인하세요.",
      "수술이나 큰 의료적 결정은 충분히 검토 후 진행하세요.",
    ],
  },
  family: {
    good: [
      "가족 간의 유대가 깊어지는 화목한 시기입니다.",
      "가족과 함께하는 시간이 늘어나고 서로에게 의지가 되는 흐름입니다.",
    ],
    mid: [
      "가족 관계는 무난하게 유지되는 시기입니다.",
      "소소한 갈등이 생기더라도 대화와 배려로 해결하면 문제없습니다.",
    ],
    bad: [
      "가족 내 의견 충돌이나 갈등이 생길 수 있는 시기입니다.",
      "가족 간의 배려와 소통을 더욱 신경 써 주세요.",
      "부모님 또는 가족의 건강이나 어려움에 관심을 기울이는 것이 좋습니다.",
    ],
  },
  friends: {
    good: [
      "좋은 인연이 생기고 사교 활동이 활발해지는 시기입니다.",
      "귀한 인연이나 도움을 주는 귀인을 만날 수 있습니다.",
    ],
    mid: [
      "인간관계는 안정적으로 유지되는 시기입니다.",
      "기존 인맥을 소중히 관리하며 신뢰를 쌓아가세요.",
    ],
    bad: [
      "인간관계에서 오해나 배신이 생길 수 있는 시기입니다.",
      "새로운 만남보다 이미 믿을 수 있는 사람들에게 집중하는 것이 좋습니다.",
      "금전 거래나 보증은 친한 사이라도 신중하게 결정하세요.",
    ],
  },
};

const NARRATIVE_SCRIPTS_EN: Record<string, NarrativeTier> = {
  love: {
    good: [
      "This is a period when a new romantic connection is likely to come your way.",
      "If you are single, actively create opportunities to meet new people.",
      "If you are currently seeing someone, the relationship can deepen a step further.",
    ],
    mid: [
      "Rather than major changes, this is a steady, stable period for your love life.",
      "If you are currently seeing someone, it is a good time to slowly build the relationship.",
      "It is best to follow the natural flow of connection without rushing.",
    ],
    bad: [
      "This is a period when conflict or difficulty may arise in your romantic relationships.",
      "If you are seeing someone, a small misunderstanding could lead to a breakup, so take special care with communication.",
      "Rather than new encounters, this is a time to reflect on your existing relationship.",
    ],
  },
  marry: {
    good: [
      "This is a favorable period in which a marriage connection can be formed.",
      "Your relationship with your partner may deepen, or marriage talks may progress concretely.",
      "If you are preparing for marriage, take active steps during this period.",
    ],
    mid: [
      "Even if marriage comes up in conversation, it is best not to rush.",
      "This is a time to build your current relationship fully and get to know each other more deeply.",
    ],
    bad: [
      "This is a period when obstacles or disagreements may arise in the progress toward marriage.",
      "Conflict may arise with your spouse or marriage partner, so avoid emotional decisions.",
      "Rather than pushing things forward, prioritize stabilizing the relationship.",
    ],
  },
  business: {
    good: [
      "This is a period when business opportunities and contracts flow in actively.",
      "It is a good time to attempt new partnerships or business expansion.",
      "Active sales and networking efforts can bring good results.",
    ],
    mid: [
      "Rather than aggressive expansion, it is best to focus on stable operation of your business.",
      "This is a time to strengthen the fundamentals of your existing business and prepare thoroughly.",
    ],
    bad: [
      "This is a period when unexpected losses or disputes may arise in business.",
      "Decide carefully after full review before making large investments or starting a new business.",
      "Check important documents such as contracts thoroughly and focus on risk management.",
    ],
  },
  career: {
    good: [
      "This is a period when opportunities for promotion, job change, and employment open up actively.",
      "Your abilities will be recognized at work and a chance to leap forward will come.",
      "If you have a targeted career change, make active use of this period.",
    ],
    mid: [
      "This is a time to build your skills at your current job while preparing for opportunities.",
      "Rather than major changes, steady performance management to build your foundation is important.",
    ],
    bad: [
      "This is a period when conflict or instability may arise at work.",
      "Avoid impulsive job changes or going independent, and observe the situation carefully.",
      "Take care with your words and actions to avoid misunderstandings in workplace relationships.",
    ],
  },
  wealth: {
    good: [
      "This is a period with a good flow of incoming wealth.",
      "Active investing or business activity can bear fruit.",
      "Your income may increase, or unexpected wealth may come in.",
    ],
    mid: [
      "This is a period when your wealth flow stays stable.",
      "It is best to decide on large expenses or investments only after full review.",
    ],
    bad: [
      "This is a period when unexpected expenses or financial losses may occur.",
      "Reduce unnecessary spending and focus on saving and managing fixed expenses.",
      "It is best to avoid guarantees or loans during this period.",
    ],
  },
  health: {
    good: [
      "This is a period when your health is stable and you are full of vitality.",
      "Maintain good condition with consistent exercise and self-care.",
    ],
    mid: [
      "There are no major health issues, but take care not to overwork.",
      "It is important to maintain regular habits and get enough sleep.",
    ],
    bad: [
      "This is a period when overwork or stress could harm your health.",
      "Pay special attention to sleep and nutrition, and check immediately if your body sends warning signs.",
      "Proceed with surgery or major medical decisions only after full review.",
    ],
  },
  family: {
    good: [
      "This is a harmonious period when family bonds grow deeper.",
      "Time spent together increases and you become a source of support for one another.",
    ],
    mid: [
      "Family relationships stay smooth during this period.",
      "Even if small conflicts arise, they can be resolved with conversation and consideration.",
    ],
    bad: [
      "This is a period when disagreements or conflict may arise within the family.",
      "Pay extra attention to consideration and communication among family members.",
      "It is good to pay attention to the health or difficulties of your parents or family.",
    ],
  },
  friends: {
    good: [
      "This is a period when good connections form and social activity becomes lively.",
      "You may meet a valuable connection or a benefactor who offers help.",
    ],
    mid: [
      "Your relationships stay stable during this period.",
      "Cherish and manage your existing network, building trust over time.",
    ],
    bad: [
      "This is a period when misunderstanding or betrayal may arise in your relationships.",
      "Rather than new encounters, it is best to focus on people you can already trust.",
      "Be cautious with money dealings or guarantees, even among close friends.",
    ],
  },
};

// ── 내러티브 컨텍스트 타입 ──────────────────────────────────────────
interface NarrCtx {
  useGod?: string;
  avoidGod?: string;
  gender?: string;
  svcType?: string;
}

// 오행별 인연 외모·성격 (여성→남성 / 남성→여성)
const OE_MATE_M: Record<string,string> = {
  목: "자연스럽고 편안한 인상의 남성입니다. 키가 크거나 체형이 날씬한 편이며, 말수는 많지 않지만 깊이 있는 대화가 가능한 지적인 스타일이네요. 교육·의료·상담·예술 분야에 종사하거나 관심이 많을 가능성이 높습니다. 처음엔 거리감이 있어 보여도 마음을 열면 세상에서 가장 든든한 지지자가 되는, 천천히 알아가야 할 매력의 소유자입니다.",
  화: "카리스마 넘치고 에너지가 가득한 남성입니다. 눈빛이 강렬하고 말솜씨가 뛰어나며, 어딜 가든 분위기를 이끄는 존재감이 있네요. IT·미디어·마케팅·자영업 분야의 능력자일 가능성이 높습니다. 열정적이고 승부욕이 강한 스타일이지만, 좋아하는 사람 앞에서는 한없이 부드러워지는 반전 매력을 가졌습니다.",
  토: "묵직하고 믿음직스러운 분위기의 남성입니다. 말보다 행동으로 보여주는 스타일로, 겉으로는 무뚝뚝해 보여도 사람을 세심하게 챙기는 면이 있습니다. 금융·부동산·공무원·건설 등 안정적인 분야에서 묵묵히 자기 자리를 지키는 사람일 가능성이 높습니다. 연애는 서툴러도 한 번 결심하면 끝까지 함께하는 타입입니다.",
  금: "날카롭고 세련된 인상의 남성입니다. 각진 윤곽선에 정돈된 스타일이 인상적이며, 논리적이고 결단력 있는 성격으로 법조·의료·IT·금융 등 전문직에서 두각을 나타내는 타입입니다. 겉으로는 차갑게 느껴질 수 있지만, 한번 마음을 열면 깊이 헌신하는 진성 츤데레 스타일입니다.",
  수: "지적이고 사색적인 분위기의 남성입니다. 말수가 적고 눈빛이 깊으며, 겉으로는 차가워 보여도 감수성이 풍부하고 은근히 로맨틱한 면이 있습니다. IT·연구·의료·철학 분야의 전문가로 자기 분야에서 깊이 있는 실력을 쌓은 사람일 가능성이 높습니다.",
};
const OE_MATE_F: Record<string,string> = {
  목: "단정하고 자연스러운 분위기의 여성입니다. 꾸미지 않아도 자연스럽게 빛이 나는 타입으로, 처음 만나는 사람도 쉽게 편안하게 만드는 친화력이 있습니다. 교육·의료·상담 분야에 종사하거나 관심이 많을 가능성이 높으며, 알수록 매력이 깊어지는 인연입니다.",
  화: "밝고 활기찬 분위기의 여성입니다. 눈빛이 반짝이고 표정이 풍부하며, 어딜 가든 분위기를 밝히는 존재감이 있네요. 패션 감각이 뛰어나고 트렌디한 것을 좋아하며, 미디어·뷰티·마케팅·요식업 분야에서 활발하게 활동하는 타입일 가능성이 높습니다.",
  토: "포근하고 믿음직스러운 분위기의 여성입니다. 감정 기복이 적고 안정감을 주는 성격으로, 금융·행정·유통 분야에서 신뢰받는 사람일 가능성이 높습니다. 가정적이고 현실적인 감각이 뛰어나며, 한 번 인연을 맺으면 오래도록 곁에 있어주는 사람입니다.",
  금: "단정하고 도회적인 스타일의 여성입니다. 날카로운 이목구비와 깔끔한 패션 센스가 돋보이며, 처음엔 차갑게 느껴질 수 있지만 신뢰가 쌓이면 의리 있고 따뜻한 속내를 보여줍니다. IT·법조·금융 전문직 종사자일 가능성이 높습니다.",
  수: "지적이고 차분한 분위기의 여성입니다. 큰 눈과 사색적인 눈빛이 인상적이며, 말이 많지 않지만 깊은 생각과 감수성을 가진 타입입니다. 예술적 감각이 뛰어나고 창의적이며, 연구직·IT·의료·예술 분야에 관심이 많을 가능성이 높습니다.",
};
const OE_HEALTH_RISK: Record<string,string[]> = {
  목: ["간·담낭","눈(시력 저하·안구건조)","근육·인대","신경계 피로"],
  화: ["심장·혈관(부정맥·고혈압)","뇌(두통·편두통)","소장","스트레스성 질환"],
  토: ["위장·소화기(위염·역류)","췌장·비장","피부","당뇨 위험"],
  금: ["폐·기관지(호흡기·천식)","대장","피부·코(비염·비염)","치아·잇몸"],
  수: ["신장·방광(신장 결석·방광염)","뼈·관절(허리·무릎)","귀(이명)","생식기·호르몬"],
};
const OE_BIZ: Record<string,string[]> = {
  목: ["교육 사업·학원","의료·헬스케어","인테리어·리모델링","컨텐츠 제작·출판","환경·에코 사업"],
  화: ["IT·소프트웨어","요식업·카페·베이커리","미디어·유튜브·방송","뷰티·패션","이벤트·엔터테인먼트"],
  토: ["부동산 중개·임대","건설·시공","유통·물류·식품 도소매","금융·보험·컨설팅","프랜차이즈"],
  금: ["제조·기계·부품","귀금속·주얼리","IT 하드웨어","법무·세무·회계 서비스","보안·경비"],
  수: ["온라인 쇼핑몰·이커머스","여행·관광 기획","수입·무역·해외 유통","의약·바이오·화학","연구·개발·컨설팅"],
};
const OE_JOB: Record<string,string[]> = {
  목: ["교사·교수·강사","상담사·코치·심리사","의사·한의사·약사","디자이너·작가·기자","환경·농업 전문가"],
  화: ["IT 개발자·기획자","마케터·브랜드 매니저","방송인·유튜버·크리에이터","요리사·F&B 전문가","예술가·공연기획자"],
  토: ["공무원·행정직","금융 전문가·펀드매니저","부동산 에이전트·감정평가사","세무사·회계사","유통·물류 매니저"],
  금: ["변호사·검사·법조인","의사·치과의사·외과의","IT 아키텍트·엔지니어","금융 애널리스트","제조·기계 전문가"],
  수: ["IT 연구원·데이터 과학자","의약·바이오 연구원","번역·통역사","여행 기획자·항공 분야","심리학자·철학자·역술인"],
};

// ── 영어판 데이터 사전 ──────────────────────────────────────────
const OE_MATE_M_EN: Record<string,string> = {
  목: "A man with a natural, easygoing impression. He tends to be tall or slender, and while not talkative, he is the intellectual type capable of deep conversation. He is likely to work in or be drawn to education, medicine, counseling, or the arts. He may seem distant at first, but once he opens up he becomes the most dependable supporter in the world — a charm worth getting to know slowly.",
  화: "A charismatic man overflowing with energy. His gaze is intense and he is an excellent talker, with a presence that leads the mood wherever he goes. He is likely a talented figure in IT, media, marketing, or running his own business. He is passionate and competitive, yet turns endlessly gentle in front of someone he loves — a captivating reversal of charm.",
  토: "A man with a solid, trustworthy air. He shows things through action rather than words, and though he may seem blunt on the outside, he looks after people with care. He is likely someone who quietly holds his ground in a stable field such as finance, real estate, public service, or construction. He may be awkward at romance, but once he commits he is the type who stays to the end.",
  금: "A man with a sharp, refined impression. His angular features and neat style stand out, and with a logical, decisive character he tends to excel in professions such as law, medicine, IT, or finance. He may feel cold at first, but once he opens his heart he is deeply devoted — a genuine tough-yet-tender type.",
  수: "A man with an intellectual, contemplative air. He is quiet with deep eyes, and though he seems cool on the surface, he is rich in sensitivity and quietly romantic. He is likely an expert in IT, research, medicine, or philosophy who has built deep expertise in his field.",
};
const OE_MATE_F_EN: Record<string,string> = {
  목: "A woman with a tidy, natural air. She is the type who shines effortlessly without much adornment, with a warmth that quickly puts even first-time acquaintances at ease. She is likely to work in or be drawn to education, medicine, or counseling — a connection whose charm deepens the more you know her.",
  화: "A bright, lively woman. Her eyes sparkle and her expressions are vivid, with a presence that lights up the mood wherever she goes. She has an excellent sense of fashion and loves what is trendy, and is likely active in media, beauty, marketing, or the food industry.",
  토: "A warm, dependable woman. With an even temperament that brings a sense of stability, she is likely someone trusted in finance, administration, or distribution. She is domestic and highly practical, and once she forms a bond she stays by your side for a long time.",
  금: "A woman with a neat, urban style. Her sharp features and clean fashion sense stand out; she may feel cold at first, but once trust is built she reveals a loyal, warm nature. She is likely a professional in IT, law, or finance.",
  수: "A woman with an intellectual, calm air. Her large eyes and contemplative gaze are striking, and though not talkative she is a type of deep thought and sensitivity. She is artistically gifted and creative, and likely drawn to research, IT, medicine, or the arts.",
};
const OE_HEALTH_RISK_EN: Record<string,string[]> = {
  목: ["liver & gallbladder","eyes (declining vision, dry eyes)","muscles & ligaments","nervous-system fatigue"],
  화: ["heart & blood vessels (arrhythmia, hypertension)","brain (headaches, migraines)","small intestine","stress-related conditions"],
  토: ["stomach & digestion (gastritis, reflux)","pancreas & spleen","skin","diabetes risk"],
  금: ["lungs & bronchi (respiratory, asthma)","large intestine","skin & nose (rhinitis)","teeth & gums"],
  수: ["kidneys & bladder (kidney stones, cystitis)","bones & joints (lower back, knees)","ears (tinnitus)","reproductive & hormonal system"],
};
const OE_BIZ_EN: Record<string,string[]> = {
  목: ["education / academies","medical & healthcare","interior design & remodeling","content creation & publishing","environmental / eco business"],
  화: ["IT & software","food service / cafes / bakeries","media / YouTube / broadcasting","beauty & fashion","events & entertainment"],
  토: ["real estate brokerage & rental","construction","distribution / logistics / food wholesale & retail","finance / insurance / consulting","franchising"],
  금: ["manufacturing / machinery / parts","jewelry & precious metals","IT hardware","legal / tax / accounting services","security & guarding"],
  수: ["online stores / e-commerce","travel & tourism planning","import / trade / overseas distribution","pharmaceutical / bio / chemical","research & development / consulting"],
};
const OE_JOB_EN: Record<string,string[]> = {
  목: ["teacher / professor / instructor","counselor / coach / therapist","doctor / oriental-medicine doctor / pharmacist","designer / writer / journalist","environmental & agricultural specialist"],
  화: ["IT developer / planner","marketer / brand manager","broadcaster / YouTuber / creator","chef / F&B specialist","artist / performance planner"],
  토: ["civil servant / administrator","finance specialist / fund manager","real estate agent / appraiser","tax accountant / CPA","distribution / logistics manager"],
  금: ["lawyer / prosecutor / legal professional","doctor / dentist / surgeon","IT architect / engineer","financial analyst","manufacturing / machinery specialist"],
  수: ["IT researcher / data scientist","pharmaceutical / bio researcher","translator / interpreter","travel planner / aviation field","psychologist / philosopher / metaphysics reader"],
};

function makeNarrative(themeKey: string, score: number, saeunScores: any[], ctx?: NarrCtx, lang: Lang = "ko"): string {
  const en = lang === "en";
  const _MATE_M = en ? OE_MATE_M_EN : OE_MATE_M;
  const _MATE_F = en ? OE_MATE_F_EN : OE_MATE_F;
  const _BIZ = en ? OE_BIZ_EN : OE_BIZ;
  const _JOB = en ? OE_JOB_EN : OE_JOB;
  const _HEALTH_RISK = en ? OE_HEALTH_RISK_EN : OE_HEALTH_RISK;
  const _god = (g?: string) => g ? (en ? (OE_EN[g] ?? g) : g) : "";
  const _GREG = [2,3,4,5,6,7,8,9,10,11,12,1];
  const now = new Date().getFullYear();

  const yearData = saeunScores.map((sy:any) => ({
    year: sy.year as number,
    score: (sy.scores?.[themeKey] ?? 50) as number,
    months: ((sy.month_scores ?? []) as any[]).map((ms:any) => ({
      month: (ms.greg_month ?? _GREG[(ms.month ?? 1) - 1]) as number,
      score: (ms.scores?.[themeKey] ?? 50) as number,
    })),
  }));

  const futureSrc = yearData.filter(y => y.year >= now);
  const src = futureSrc.length > 0 ? futureSrc : yearData;

  const gY = [...src].filter(y => y.score >= 65).sort((a,b) => b.score - a.score).slice(0,3);
  const bY = [...src].filter(y => y.score < 45).sort((a,b) => a.score - b.score).slice(0,2);

  const allM: {year:number;month:number;score:number}[] = [];
  src.forEach(y => y.months.forEach(m => allM.push({year:y.year, month:m.month, score:m.score})));
  const gM = [...allM].filter(m => m.score >= 68).sort((a,b) => b.score - a.score).slice(0,4);
  const bM = [...allM].filter(m => m.score <= 38).sort((a,b) => a.score - b.score).slice(0,2);

  const fY = (arr:{year:number}[]) => arr.map(y=> en ? `${y.year}` : `${y.year}년`).join(', ');
  const fM = (arr:{year:number;month:number}[]) => arr.map(m=> en ? `${MONTHS_EN[(m.month-1+12)%12]} ${m.year}` : `${m.year}년 ${m.month}월`).join(', ');

  const L: string[] = [];

  switch(themeKey) {
    case 'love': {
      const isRejoin = ctx?.svcType === "재회운";
      if (isRejoin) {
        if (gM.length) {
          const tm = gM.slice(0,3);
          L.push(en
            ? `The periods most favorable for reconciliation are ${fM(tm.slice(0,2))}. During these months, try reaching out or arranging a natural meeting through mutual acquaintances.`
            : `재회 가능성이 높은 시기는 ${fM(tm.slice(0,2))}입니다. 이 달에는 연락을 취하거나 공통 지인을 통한 자연스러운 만남을 시도해보세요.`);
          if (tm.length >= 2)
            L.push(en
              ? `In particular, ${fM(tm.slice(0,1))} is a month when the other person's feelings open up, so reaching out first is likely to draw a positive response.`
              : `특히 ${fM(tm.slice(0,1))}은 상대방의 감정이 열리는 달이라 먼저 연락했을 때 긍정적인 반응을 얻을 가능성이 높습니다.`);
        }
        if (bY.length)
          L.push(en
            ? `In ${fY(bY.slice(0,1))}, forcing contact or pressing your feelings may push the relationship further apart. During this time, the strategy is to first set yourself in order.`
            : `${fY(bY.slice(0,1))}에는 억지로 연락하거나 감정을 강요하면 관계가 더 멀어질 수 있습니다. 이 시기에는 자신을 먼저 정비하는 것이 전략입니다.`);
        L.push(en
          ? "If you want to reconcile, the key is to show a version of yourself that has grown one step beyond how the other person remembers you. A relaxed attitude, rather than clinging, is what opens their heart."
          : "재회를 원한다면 상대방이 기억하는 당신의 모습에서 한 단계 성장한 모습을 보여주는 것이 핵심입니다. 집착보다 여유 있는 태도가 상대방의 마음을 열게 만듭니다.");
        if (ctx?.gender && ctx.useGod) {
          const mateDesc = ctx.gender === "female" ? _MATE_M[ctx.useGod] : _MATE_F[ctx.useGod];
          if (mateDesc) L.push(en ? `For reference, looking at the other person's energy — ${mateDesc}` : `참고로 상대방의 사주 기운을 보면 — ${mateDesc}`);
        }
        break;
      }
      if (gY.length) {
        if (score >= 75)
          L.push(en
            ? `${fY(gY.slice(0,2))} are years when a strong new romantic connection comes in. Introductions, blind dates, and workplace encounters are all likely to lead to romance.`
            : `${fY(gY.slice(0,2))}은 새로운 이성 인연이 강하게 들어오는 해입니다. 지인 소개·소개팅·직장 내 만남이 연애로 이어질 가능성이 높습니다.`);
        else
          L.push(en
            ? `${fY(gY.slice(0,2))} carry a flow of incoming romantic connection. Opportunities lie in natural encounters through hobbies or gatherings.`
            : `${fY(gY.slice(0,2))}에 이성 인연이 찾아오는 흐름입니다. 취미 활동이나 모임을 통한 자연스러운 만남에 기회가 있습니다.`);
      }
      if (gM.length) {
        const tm = gM.slice(0,3);
        if (tm.length >= 2)
          L.push(en
            ? `${fM(tm.slice(0,1))} is favorable for meeting someone through a blind date or acquaintances, and ${fM(tm.slice(1,2))} through work, gatherings, or travel. Increase your social activity during these months.`
            : `${fM(tm.slice(0,1))}은 소개팅이나 지인 자리에서, ${fM(tm.slice(1,2))}은 직장·모임·여행 중 새로운 인연을 만날 가능성이 높습니다. 이 달에는 사교 활동을 늘려보세요.`);
        else
          L.push(en
            ? `If there is a blind date or gathering in ${fM(tm)}, take part actively. It is a month with a high chance of a new connection.`
            : `${fM(tm)}에 소개팅이나 모임 자리가 있다면 적극적으로 참여하세요. 새로운 만남이 이루어질 가능성이 높은 달입니다.`);
      }
      if (score >= 65)
        L.push(en
          ? "If you currently have a partner, the relationship may deepen a step further or develop toward a promise of marriage during this period."
          : "현재 연인이 있다면 이 시기에 관계가 한 단계 깊어지거나 결혼을 약속하는 흐름으로 발전할 수 있습니다.");
      else if (score < 45)
        L.push(en
          ? "This is a period prone to misunderstanding and emotional drain in romantic relationships. When emotions run high, do not decide on the spot — give yourself a day or two."
          : "이성 관계에서 오해와 감정 소모가 생기기 쉬운 시기입니다. 감정이 격해질 때는 그 자리에서 결정하지 말고 하루 이틀 여유를 두세요.");
      else
        L.push(en
          ? "If you let things flow naturally without rushing, good results will follow."
          : "서두르지 않고 자연스럽게 흘러가도록 두면 좋은 결과가 따라옵니다.");
      if (bM.length)
        L.push(en
          ? `In ${fM(bM)}, a small misunderstanding could lead to a breakup, so resolve any conflict through conversation right away.`
          : `${fM(bM)}에는 작은 오해가 이별로 이어질 수 있으니 갈등이 생기면 즉시 대화로 풀어야 합니다.`);
      if (ctx?.gender && ctx.useGod) {
        const mateDesc = ctx.gender === "female" ? _MATE_M[ctx.useGod] : _MATE_F[ctx.useGod];
        if (mateDesc) L.push(en ? `\nAppearance & personality of your likely match — ${mateDesc}` : `\n인연의 외모·성격 특징 — ${mateDesc}`);
      }
      break;
    }

    case 'marry':
      if (gY.length) {
        if (score >= 75)
          L.push(en
            ? `${fY(gY.slice(0,2))} are the years with the strongest marriage fortune. If you are dating, bringing up marriage during this period is likely to be received naturally.`
            : `${fY(gY.slice(0,2))}은 결혼 운이 가장 강한 해입니다. 연애 중이라면 이 시기에 결혼 이야기를 꺼내는 것이 자연스럽게 받아들여질 가능성이 높습니다.`);
        else
          L.push(en
            ? `${fY(gY.slice(0,2))} carry an incoming flow of marriage fortune. If trust with your partner and a financial foundation are in place, make use of this period.`
            : `${fY(gY.slice(0,2))}에 결혼 운이 들어오는 흐름입니다. 상대방과의 신뢰와 경제적 기반이 준비됐다면 이 시기를 활용하세요.`);
      }
      if (gM.length)
        L.push(en
          ? `In particular, a proposal, meeting of both families, or wedding preparations around ${fM(gM.slice(0,2))} are likely to progress smoothly.`
          : `특히 ${fM(gM.slice(0,2))}에 프러포즈, 양가 상견례, 결혼 준비를 진행하면 자연스럽게 진척될 가능성이 높습니다.`);
      if (score >= 65)
        L.push(en
          ? "For the wedding date, spring (March–May) or autumn (September–October) is favorable for a stable start. If you are preparing, consider this period actively."
          : "결혼식 날짜는 봄(3~5월)이나 가을(9~10월)이 안정적인 출발에 유리합니다. 준비 중이라면 이 시기를 적극 고려하세요.");
      else if (score < 45) {
        if (bY.length)
          L.push(en
            ? `In ${fY(bY.slice(0,1))}, unexpected obstacles may arise in the marriage process. Align your views fully and confirm agreement between both families before proceeding.`
            : `${fY(bY.slice(0,1))}에는 결혼 진행에 예상치 못한 장애물이 생길 수 있습니다. 서로의 의견을 충분히 맞추고 양가 합의를 먼저 확인한 뒤 진행하세요.`);
        else
          L.push(en
            ? "Rather than rushing, building enough trust with your partner before proceeding to marriage is more advantageous in the long run."
            : "무리하게 서두르기보다 상대와의 신뢰를 충분히 쌓은 뒤 결혼을 진행하는 것이 장기적으로 유리합니다.");
      }
      if (bM.length)
        L.push(en
          ? `${fM(bM)} are months prone to emotional clashes. It is best to avoid making important marriage-related decisions during them.`
          : `${fM(bM)}은 감정적 충돌이 생기기 쉬운 달입니다. 결혼 관련 중요 결정은 이 달을 피해 진행하는 것이 좋습니다.`);
      break;

    case 'business': {
      if (gY.length) {
        if (score >= 75)
          L.push(en
            ? `${fY(gY.slice(0,2))} bring a good flow for new contracts, securing clients, and expansion alike. If you have long envisioned a business, this is a good time to launch it in earnest.`
            : `${fY(gY.slice(0,2))}은 신규 계약·거래처 확보·사업 확장 모두 좋은 흐름입니다. 오랫동안 구상해온 사업이 있다면 이 시기에 본격적으로 시작해도 좋습니다.`);
        else
          L.push(en
            ? `${fY(gY.slice(0,2))} carry a flow of incoming business opportunity. It is important to strengthen the core of your business while seizing new chances.`
            : `${fY(gY.slice(0,2))}에 사업 기회가 찾아오는 흐름입니다. 핵심 사업의 내실을 다지면서 새로운 기회를 포착하는 자세가 중요합니다.`);
      }
      if (gM.length)
        L.push(en
          ? `In particular, ${fM(gM.slice(0,3))} are favorable months for important contracts or securing new clients. Concentrate your sales, meetings, and proposals in these months.`
          : `특히 ${fM(gM.slice(0,3))}은 중요한 계약이나 신규 거래처 확보에 유리한 달입니다. 영업·미팅·제안서 제출을 이 달에 집중하세요.`);
      if (score >= 68)
        L.push(en
          ? "In this period, securing operating capital and developing new clients leads to more certain profit than land or real estate deals. Decide on real estate only after full review and good timing."
          : "이 시기는 토지·부동산 계약보다 사업 운전자금 확보와 신규 거래처 개발이 더 확실한 수익으로 이어지는 흐름입니다. 부동산은 충분한 검토 후 시기를 보고 결정하세요.");
      else if (score >= 50)
        L.push(en
          ? "Rather than aggressive expansion or real estate deals, it is wiser to focus on the stable operation of your current business. Always review contracts carefully and start any partnership with caution."
          : "무리한 사업 확장이나 부동산 계약보다 현재 사업의 안정적 운영에 집중하는 것이 현명합니다. 계약서는 반드시 꼼꼼히 검토하고, 동업 시작은 신중하게 결정하세요.");
      else
        L.push(en
          ? "In this period, large investments, real estate deals, or starting a partnership may lead to losses. Protecting your cash liquidity and minimizing spending come first."
          : "이 시기에 대규모 투자·부동산 계약·동업 시작은 손실로 이어질 가능성이 있습니다. 현금 유동성을 지키면서 지출을 최소화하는 것이 우선입니다.");
      if (bY.length)
        L.push(en
          ? `In ${fY(bY.slice(0,1))}, watch out for partner conflicts or unexpected losses. It is safest to avoid large investments or partnership contracts during this time.`
          : `${fY(bY.slice(0,1))}에는 파트너 갈등이나 예상치 못한 손실에 주의하세요. 이 시기의 대규모 투자나 동업 계약은 피하는 것이 안전합니다.`);
      if (bM.length)
        L.push(en
          ? `In ${fM(bM)}, review contracts thoroughly and postpone any new investment or partnership decisions to the following month.`
          : `${fM(bM)}에는 계약서 검토를 철저히 하고, 새로운 투자·동업 결정은 다음 달로 미루세요.`);
      if (ctx?.useGod) {
        const bizTypes = _BIZ[ctx.useGod];
        if (bizTypes?.length)
          L.push(en
            ? `\nFavorable industries based on your favorable element (${_god(ctx.useGod)}) — the ${bizTypes.slice(0,3).join(', ')} fields align well with your energy. If you are considering a startup or a change of field, review this direction first.`
            : `\n사주 용신(${ctx.useGod}) 기반 유리한 업종 — ${bizTypes.slice(0,3).join(', ')} 분야가 에너지와 잘 맞습니다. 창업이나 업종 전환을 고려 중이라면 이 방향을 우선적으로 검토해보세요.`);
      }
      break;
    }

    case 'career': {
      if (gY.length) {
        if (score >= 75)
          L.push(en
            ? `${fY(gY.slice(0,2))} are years when career fortune rises sharply. Job changes, promotions, and hiring are all likely to yield good results if attempted during this period.`
            : `${fY(gY.slice(0,2))}은 직업 운이 크게 상승하는 해입니다. 이직·승진·취업 모두 이 시기에 시도하면 좋은 결과를 얻을 가능성이 높습니다.`);
        else
          L.push(en
            ? `${fY(gY.slice(0,2))} bring an opportunity for a career leap. If you prepare quietly and then move during this period, you can achieve the results you want.`
            : `${fY(gY.slice(0,2))}에 커리어 도약의 기회가 찾아옵니다. 조용히 준비하다 이 시기에 움직이면 원하는 결과를 얻을 수 있습니다.`);
      }
      if (gM.length) {
        const tm = gM.slice(0,3);
        if (tm.length >= 2)
          L.push(en
            ? `${fM(tm.slice(0,1))} is favorable for submitting applications or interviews, and ${fM(tm.slice(1,2))} is a good month to attempt a promotion, salary negotiation, or job offer.`
            : `${fM(tm.slice(0,1))}은 이력서 제출이나 면접에 유리하고, ${fM(tm.slice(1,2))}은 승진·연봉 협상·이직 제안을 시도하기에 좋은 달입니다.`);
        else
          L.push(en
            ? `Attempting a career change or job move in ${fM(tm)} is likely to bring good results.`
            : `${fM(tm)}에 직업 변화나 이직을 시도하면 좋은 결과가 따를 가능성이 높습니다.`);
      }
      if (score >= 70)
        L.push(en
          ? "Requesting a desired position move or salary increase at your current job during this period is likely to be accepted. If you are considering a change, a good offer may come in, so prepare your portfolio in advance."
          : "현재 직장에서 원하는 포지션 이동이나 연봉 인상 요청을 이 시기에 시도하면 받아들여질 가능성이 높습니다. 이직을 생각 중이라면 좋은 제안이 들어올 수 있으니 포트폴리오를 미리 준비해두세요.");
      else if (score >= 50)
        L.push(en
          ? "Right now, building skills and trust at your current job matters more than flashy change. Rather than rushing to switch jobs, it is better to wait for the right opportunity."
          : "지금은 화려한 변화보다 현재 직장에서 실력을 쌓고 신뢰를 높이는 것이 중요합니다. 조급하게 이직하기보다 적절한 기회를 기다리는 것이 유리합니다.");
      else
        L.push(en
          ? "It is best to avoid impulsive job changes or starting a business during this period. Be especially careful with workplace relationships, and take care that your words do not turn into gossip."
          : "충동적인 이직이나 창업은 이 시기에 피하는 것이 좋습니다. 직장 내 인간관계에 특히 조심하고, 언행이 뒷말로 이어지지 않도록 주의하세요.");
      if (bY.length)
        L.push(en
          ? `In ${fY(bY.slice(0,1))}, workplace conflict or instability may arise. Avoid this period for important career decisions.`
          : `${fY(bY.slice(0,1))}에는 직장 내 갈등이나 불안정한 상황이 생길 수 있습니다. 중요한 직업적 결정은 이 시기를 피해 진행하세요.`);
      if (bM.length)
        L.push(en
          ? `In ${fM(bM)}, be careful with decisions and watch your words and actions to avoid unnecessary conflict.`
          : `${fM(bM)}에는 의사 결정에 신중하고, 불필요한 갈등을 피하기 위해 언행을 조심하세요.`);
      if (ctx?.useGod) {
        const jobTypes = _JOB[ctx.useGod];
        if (jobTypes?.length)
          L.push(en
            ? `\nFavorable occupations based on your favorable element (${_god(ctx.useGod)}) — you express your energy best in the ${jobTypes.slice(0,3).join(', ')} fields. Refer to this when deciding on a career change or how to deepen your expertise.`
            : `\n사주 용신(${ctx.useGod}) 기반 유리한 직종 — ${jobTypes.slice(0,3).join(', ')} 분야에서 자신의 에너지가 가장 잘 발휘됩니다. 직종 전환이나 전문성 강화 방향을 정할 때 참고하세요.`);
      }
      break;
    }

    case 'wealth':
      if (gY.length) {
        if (score >= 75)
          L.push(en
            ? `${fY(gY.slice(0,2))} are years when wealth fortune comes in strongly. Rising business income, realized investment gains, and unexpected windfalls are all possible.`
            : `${fY(gY.slice(0,2))}은 재물 운이 강하게 들어오는 해입니다. 사업 수입 증가·투자 수익 실현·뜻밖의 수입이 생길 수 있는 흐름입니다.`);
        else
          L.push(en
            ? `${fY(gY.slice(0,2))} bring an improving flow of wealth. It is a period to reap the fruits of the investments or business you have been preparing.`
            : `${fY(gY.slice(0,2))}에 재물 흐름이 좋아지는 운이 들어옵니다. 그동안 준비해온 투자나 사업의 결실을 맺을 수 있는 시기입니다.`);
      }
      if (gM.length)
        L.push(en
          ? `In particular, ${fM(gM.slice(0,3))} are favorable months for locking in profit, signing contracts, or starting to invest. Act on postponed financial sign-ups or investment decisions in these months.`
          : `특히 ${fM(gM.slice(0,3))}은 수익 확정이나 계약 체결, 재테크 시작에 유리한 달입니다. 미뤄두었던 금융 상품 가입이나 투자 결정을 이 달에 실행하는 것이 좋습니다.`);
      if (score >= 70)
        L.push(en
          ? "In this period, real income through business or contracts flows in more steadily than stock or real estate speculation. Real estate is worth considering for actual residence, but avoid investing for short-term price gains."
          : "이 시기는 주식·부동산 투기보다 사업 수입이나 계약을 통한 실질적인 수익이 더 안정적으로 들어오는 흐름입니다. 부동산은 실거주 목적이라면 고려해볼 만하지만 단기 시세 차익 목적의 투자는 피하세요.");
      else if (score >= 50)
        L.push(en
          ? "This is a period when your wealth flow stays stable. Building a foundation with stable financial products or savings suits this time better than high-risk investment."
          : "재물 흐름이 안정적으로 유지되는 시기입니다. 고위험 투자보다 안정형 금융 상품이나 예·적금으로 기반을 다지는 것이 이 시기에 적합합니다.");
      else
        L.push(en
          ? "In this period, managing expenses comes before investing. Review real estate deals or stock investment carefully, and avoid guarantees and loans if possible."
          : "이 시기에는 투자보다 지출 관리가 우선입니다. 부동산 계약이나 주식 투자는 신중하게 검토하고, 보증·대출은 가급적 피하세요.");
      if (bY.length)
        L.push(en
          ? `In ${fY(bY.slice(0,1))}, unexpected expenses or financial losses may occur. This year, focus on securing cash liquidity rather than new investments.`
          : `${fY(bY.slice(0,1))}에는 예상치 못한 지출이나 금전 손실이 있을 수 있습니다. 이 해에는 새로운 투자보다 현금 유동성 확보에 집중하세요.`);
      if (bM.length)
        L.push(en
          ? `In ${fM(bM)}, politely decline any large expense or request to act as guarantor, and always check contracts carefully.`
          : `${fM(bM)}에는 큰 지출이나 보증 요청이 들어오면 정중히 거절하고, 계약서는 반드시 꼼꼼하게 확인하세요.`);
      break;

    case 'health': {
      if (gY.length)
        L.push(en
          ? `${fY(gY.slice(0,2))} are a period of rising health and vitality. It is a good time to begin, in earnest, the exercise or health care you have been putting off.`
          : `${fY(gY.slice(0,2))}에 건강과 활력이 높아지는 시기입니다. 그동안 미뤄왔던 운동이나 건강 관리를 본격적으로 시작하기에 좋습니다.`);
      else
        L.push(en
          ? "For health, consistent care is key. Keeping a regular rhythm of sleep, diet, and exercise is especially important during this period."
          : "건강은 꾸준한 관리가 핵심입니다. 수면·식습관·운동 리듬을 규칙적으로 유지하는 것이 이 시기에 특히 중요합니다.");
      if (ctx?.avoidGod) {
        const riskParts = _HEALTH_RISK[ctx.avoidGod];
        if (riskParts?.length)
          L.push(en
            ? `Vulnerable areas based on your unfavorable element (${_god(ctx.avoidGod)}) — take special care with conditions related to the ${riskParts.join(', ')}. Focus on these areas during regular checkups.`
            : `사주 기신(${ctx.avoidGod}) 기반 취약 부위 — ${riskParts.join(', ')} 관련 질환에 특히 주의가 필요합니다. 정기 검진 시 이 부위를 중점적으로 확인하세요.`);
      }
      if (score < 50) {
        if (bY.length)
          L.push(en
            ? `In ${fY(bY.slice(0,1))}, lowered immunity and accumulated fatigue overlap. If you are planning surgery or a procedure, avoid this year and manage any existing conditions thoroughly.`
            : `${fY(bY.slice(0,1))}에는 면역력 저하와 피로 누적이 겹치는 시기입니다. 수술·시술 계획이 있다면 이 해는 피하고, 기존 지병 관리를 철저히 하세요.`);
        else
          L.push(en
            ? "Do not ignore the signals your body sends during this period. When fatigue builds up, immunity weakens and a small ailment can grow into a serious one."
            : "이 시기에 몸이 보내는 신호를 무시하지 마세요. 피로가 쌓이면 면역력이 약해지고 작은 병이 큰 병으로 이어질 수 있습니다.");
      }
      if (bM.length) {
        L.push(en
          ? `In particular, ${fM(bM)} carry a higher risk of accidents and surgery. Cut back on strenuous work and late-night activity, and take extra care when driving or doing leisure activities. See a doctor immediately if your body shows warning signs.`
          : `특히 ${fM(bM)}에는 사고수·수술수가 높아지는 시기입니다. 무리한 업무나 야간 활동을 줄이고, 운전·레저 활동 시 각별히 조심하세요. 몸에 이상 신호가 오면 즉시 병원을 찾으세요.`);
      }
      if (score >= 65)
        L.push(en
          ? "Since your stamina improves during this period, it is a good time to start a new exercise routine or check your baseline health with a checkup."
          : "체력이 좋아지는 시기인 만큼 새로운 운동을 시작하거나 건강 검진을 통해 기초 건강 상태를 체크해두면 좋습니다.");
      break;
    }

    case 'family':
      if (gY.length)
        L.push(en
          ? `${fY(gY.slice(0,2))} carry a flow of deepening family bonds and joyful events. Family gatherings or good news may follow.`
          : `${fY(gY.slice(0,2))}에 가족 간의 유대가 깊어지고 경사스러운 일이 생길 수 있는 흐름입니다. 가족 모임이나 좋은 소식이 이어질 수 있습니다.`);
      else
        L.push(en
          ? "Keeping steady communication within the family is important during this period. Small acts of care and attention become the strength that supports the relationship."
          : "가족 간의 소통을 꾸준히 유지하는 것이 이 시기에 중요합니다. 작은 배려와 관심이 관계를 지탱하는 힘이 됩니다.");
      if (score < 50) {
        if (bY.length)
          L.push(en
            ? `In ${fY(bY.slice(0,1))}, take care regarding your parents' health or financial disagreements within the family. Make important family decisions only after enough conversation.`
            : `${fY(bY.slice(0,1))}에는 부모님 건강이나 가족 내 경제적 의견 충돌에 주의가 필요합니다. 중요한 가족 관련 결정은 충분히 대화한 뒤 진행하세요.`);
        else
          L.push(en
            ? "This is a period when conflict may arise over financial matters or health issues within the family. Communicating in advance and sharing each other's situations is the way to reduce clashes."
            : "가족 내에서 경제적 문제나 건강 이슈로 갈등이 생길 수 있는 시기입니다. 미리 소통하고 서로의 상황을 공유하는 것이 충돌을 줄이는 방법입니다.");
      }
      if (bM.length)
        L.push(en
          ? `In particular, in ${fM(bM)}, check family members' health carefully and resolve issues through calm conversation rather than emotional arguments.`
          : `특히 ${fM(bM)}에는 가족 건강 상태를 꼼꼼히 체크하고, 감정적인 언쟁보다 차분한 대화로 문제를 해결하세요.`);
      if (score >= 65)
        L.push(en
          ? "Planning a family trip or a special event during this period can create memories that stay with you."
          : "가족과 함께하는 여행이나 특별한 행사를 계획하면 기억에 남는 추억이 될 수 있는 시기입니다.");
      break;

    case 'friends':
      if (gY.length)
        L.push(en
          ? `In ${fY(gY.slice(0,2))}, you are likely to meet valuable connections and people who offer real help (benefactors). Important encounters may occur at social or networking occasions.`
          : `${fY(gY.slice(0,2))}에 귀한 인연과 실질적인 도움을 주는 사람(귀인)을 만날 가능성이 높습니다. 사교 활동이나 네트워킹 자리에서 중요한 만남이 이루어질 수 있습니다.`);
      else
        L.push(en
          ? "Cherishing the relationships you have already built trust with matters more than new encounters during this period."
          : "새로운 만남보다 기존에 신뢰를 쌓아온 관계를 소중히 유지하는 것이 이 시기에 더 중요합니다.");
      if (gM.length)
        L.push(en
          ? `${fM(gM.slice(0,2))} are times when important connections form or old ties reconnect. Reach out first to someone you have been meaning to contact.`
          : `${fM(gM.slice(0,2))}은 중요한 인맥이 형성되거나 오래된 인연이 다시 연결되는 시기입니다. 연락을 미뤄왔던 사람에게 먼저 다가가 보세요.`);
      if (score < 50) {
        if (bY.length)
          L.push(en
            ? `In ${fY(bY.slice(0,1))}, betrayal or misunderstanding from someone close may arise. Watch your words and actions, and be especially careful with how you express yourself on social media or in group chats.`
            : `${fY(bY.slice(0,1))}에는 가까운 사람으로부터 배신이나 오해가 생길 수 있습니다. 말과 행동에 주의하고 SNS나 단체 대화방에서의 표현도 각별히 조심하세요.`);
        else
          L.push(en
            ? "This is a period when misunderstandings may build up or gossip may spread in your relationships. It is best to deliver important matters in person."
            : "인간관계에서 오해가 쌓이거나 뒷말이 나올 수 있는 시기입니다. 중요한 이야기는 직접 만나서 전달하는 것이 좋습니다.");
      }
      if (bM.length)
        L.push(en
          ? `In ${fM(bM)}, absolutely avoid money dealings or guarantees, even with close friends. Money problems can ruin a relationship forever.`
          : `${fM(bM)}에는 친한 사이라도 금전 거래나 보증은 절대 피하세요. 돈 문제는 관계를 영원히 망가뜨릴 수 있습니다.`);
      if (score >= 65)
        L.push(en
          ? "A benefactor may connect you to business or career opportunities. Joining a new gathering or community can be a great help during this period."
          : "귀인을 통해 사업이나 직업적 기회가 연결될 수 있습니다. 새로운 모임이나 커뮤니티에 참여하는 것이 큰 도움이 될 수 있는 시기입니다.");
      break;

    default: break;
  }

  if (L.length === 0) {
    const tier = score >= 68 ? 'good' : score >= 46 ? 'mid' : 'bad';
    const scripts = en ? NARRATIVE_SCRIPTS_EN : NARRATIVE_SCRIPTS;
    return (scripts[themeKey]?.[tier] ?? []).join('\n');
  }
  return L.join('\n');
}

const SVC_THEMES: Record<string, string[]> = {
  "재회운":   ["love","marry"],
  "신년운세": ["love","marry","business","career","wealth","health","family","friends"],
};

const THEME_META: Record<string,{icon:string;name:string}> = {
  love:     {icon:"♥",  name:"연애운"},
  marry:    {icon:"", name:"결혼운"},
  business: {icon:"💼", name:"사업운"},
  career:   {icon:"🏆", name:"직업운"},
  wealth:   {icon:"", name:"재물운"},
  health:   {icon:"", name:"건강운"},
  family:   {icon:"🏠", name:"가족관계"},
  friends:  {icon:"🤝", name:"인간관계"},
};
// 테마 영문 표시명 (영어 리포트용)
const THEME_NAME_EN: Record<string,string> = {
  love:"Love Fortune", marry:"Marriage Fortune", business:"Business Fortune", career:"Career Fortune",
  wealth:"Wealth Fortune", health:"Health Fortune", family:"Family Ties", friends:"Relationships",
};
const themeName = (key: string, lang: Lang, fallback: string): string =>
  lang === "en" ? (THEME_NAME_EN[key] ?? fallback) : fallback;

// 서비스별 종합 서술형 내러티브 (2000~10000자, 대운별 상세 포함)
function makeHolisticNarrative(themeKey: string, daeunScores: any[], ctx?: NarrCtx, lang: Lang = "ko"): string {
  const en = lang === "en";
  const _BIZ = en ? OE_BIZ_EN : OE_BIZ;
  const _JOB = en ? OE_JOB_EN : OE_JOB;
  const _HEALTH_RISK = en ? OE_HEALTH_RISK_EN : OE_HEALTH_RISK;
  const _MATE_M = en ? OE_MATE_M_EN : OE_MATE_M;
  const _MATE_F = en ? OE_MATE_F_EN : OE_MATE_F;
  const _god = (g?: string) => g ? (en ? (OE_EN[g] ?? g) : g) : "";
  const now = new Date().getFullYear();

  const daeunInfo = daeunScores.map((d: any) => {
    const startAge = (d.start_age ?? parseInt(String(d.age||"0").replace("세","")) ?? 0) as number;
    const endAge   = (d.end_age ?? startAge + 9) as number;
    return {
      label: d.label as string,
      startAge, endAge,
      score: (d[themeKey]?.score ?? 0) as number,
      reason: (d[themeKey]?.reason ?? '') as string,
      saeunScores: (d.saeun_scores ?? []) as any[],
    };
  });

  // 연애/결혼/사업/직업/재물/건강은 성인(20세) 이후만 의미 있음
  const _ADULT_ONLY_THEMES = ['love','marry','business','career','wealth','health'];
  const _filterByAdult = _ADULT_ONLY_THEMES.includes(themeKey);

  const allSaeun: {year:number; score:number}[] = [];
  daeunInfo.forEach(d => {
    d.saeunScores.forEach((sy:any, idx:number) => {
      const ageAtYear = d.startAge + idx;
      if (!_filterByAdult || ageAtYear >= 20) {
        allSaeun.push({year: sy.year as number, score: (sy.scores?.[themeKey] ?? 50) as number});
      }
    });
  });
  const futureSaeun = allSaeun.filter(y => y.year >= now);
  const workSaeun   = futureSaeun.length > 0 ? futureSaeun : allSaeun;

  const goodYears  = [...workSaeun].filter(y => y.score >= 65).sort((a,b) => b.score - a.score).slice(0,6);
  const greatYears = goodYears.filter(y => y.score >= 75);
  const badYears   = [...workSaeun].filter(y => y.score < 45).sort((a,b) => a.score - b.score).slice(0,4);

  // 성인 대운만 topDaeun/worstDaeun 후보 (연애/결혼/사업/직업/재물/건강은 20세 이상 대운만)
  const adultDaeun  = _filterByAdult
    ? daeunInfo.filter(d => d.endAge >= 20)   // 20세 이후 구간이 있는 대운만
    : daeunInfo;
  const workDaeun   = adultDaeun.length > 0 ? adultDaeun : daeunInfo;
  const topDaeun    = [...workDaeun].sort((a,b) => b.score - a.score)[0] ?? null;
  const worstDaeun  = [...workDaeun].sort((a,b) => a.score - b.score)[0] ?? null;

  const fY  = (arr:{year:number}[]) => arr.map(y=> en ? `${y.year}` : `${y.year}년`).join(', ');
  const fYS = (arr:{year:number;score:number}[]) => arr.map(y=> en ? `${y.year} (${y.score} pts)` : `${y.year}년(${y.score}점)`).join(', ');

  // 대운별 상세 섹션 생성
  // mode: 'good' = 좋은 해만 (연애운/결혼운), 'all' = 전체 (건강운/사업운/직업운)
  // 테마별 이벤트 필터: 무관한 테마 이벤트 제거, 공통 이벤트는 항상 표시
  const _filterEventsForTheme = (evts: string[], thKey: string): string[] => {
    const _UNIVERSAL = ['삼합','개고','형살','파해','원진','큰 발복','각별히 주의','전반적','평온'];
    const _LOVE_EXCL  = ['승진','이직','직업','직장','사업','계약','재물','수입','손재','금전','건강','수술','과로'];
    const _BIZ_EXCL   = ['이성 인기','연애','이성 인연','이별','감성','건강','수술','과로'];
    const _CAR_EXCL   = ['이성 인기','연애','이성 인연','이별','감성','건강','수술','과로'];
    const _WLTH_EXCL  = ['이성 인기','연애','이성 인연','이별','감성','건강','수술','과로'];
    const _HLTH_EXCL  = ['이성 인기','연애','이성 인연','이별','감성','승진','이직','사업','계약'];
    const excludeMap: Record<string, string[]> = {
      love: _LOVE_EXCL, marry: _LOVE_EXCL,
      business: _BIZ_EXCL, career: _CAR_EXCL,
      wealth: _WLTH_EXCL, health: _HLTH_EXCL,
    };
    const excl = excludeMap[thKey] ?? [];
    return evts.filter(ev => {
      if (_UNIVERSAL.some(k => ev.includes(k))) return true;
      return !excl.some(k => ev.includes(k));
    });
  };

  const makeDaeunDetail = (thKey: string, scoreLabel: (sc:number)=>string, mode: 'good'|'all' = 'all') => {
    const _G = [2,3,4,5,6,7,8,9,10,11,12,1];
    // 성인 전용 테마는 20세 이후 구간이 있는 대운만 표시
    return daeunInfo.filter(d => d.score > 0 && (!_filterByAdult || d.endAge >= 20)).map(d => {
      const tier = en
        ? (d.score >= 75 ? "★ Very strong period" : d.score >= 60 ? "◎ Good period" : d.score >= 45 ? "△ Average period" : "▽ Caution period")
        : (d.score >= 75 ? "★ 매우 강한 시기" : d.score >= 60 ? "◎ 좋은 시기" : d.score >= 45 ? "△ 평범한 시기" : "▽ 주의 시기");
      let out = en
        ? `■ ${d.label} Luck Cycle (age ${d.startAge}–${d.endAge}) — ${scoreLabel(d.score)} [${tier}]\n`
        : `■ ${d.label} 대운 (${d.startAge}세~${d.endAge}세) — ${scoreLabel(d.score)} [${tier}]\n`;
      if (d.reason) out += `  ${d.reason}\n`;
      // 20세 미만 구간의 세운은 제외 (성인 전용 테마)
      const saeunList = _filterByAdult
        ? (d.saeunScores as any[]).filter((_:any, idx:number) => d.startAge + idx >= 20)
        : d.saeunScores as any[];

      // 연애/결혼운 관련 이벤트 키워드 — 해당 이벤트가 있으면 점수 무관하게 표시
      const _LOVE_KW = ['배우자','복음','인연','이성','합','결혼','도화','관성'];
      const _hasLoveEvent = (sy:any) =>
        ((sy.events ?? []) as string[]).some((e:string) => _LOVE_KW.some(k => e.includes(k)));

      if (mode === 'good') {
        // 주요 해: score >= 55 이상이거나 연애 관련 이벤트가 있는 해 표시 (내림차순)
        const goodSY = [...saeunList]
          .filter((sy:any) => (sy.scores?.[thKey] ?? 50) >= 55 || _hasLoveEvent(sy))
          .sort((a:any,b:any) => (b.scores?.[thKey]??50) - (a.scores?.[thKey]??50));
        if (goodSY.length === 0) {
          out += en ? `  No pronounced energy stands out in this Luck Cycle.\n` : `  이 대운에서 두드러진 기운이 없는 구간입니다.\n`;
        } else {
          goodSY.forEach((sy:any) => {
            const sc = (sy.scores?.[thKey] ?? 50) as number;
            const mk = sc >= 75 ? "★" : sc >= 62 ? "◎" : sc >= 50 ? "△" : "⚠";
            out += en ? `  ${sy.year}  ${sc} pts ${mk}\n` : `  ${sy.year}년  ${sc}점 ${mk}\n`;
            // 테마 무관 이벤트 필터링 후 출력
            const evts = _filterEventsForTheme((sy.events ?? []) as string[], thKey);
            if (evts.length > 0) out += `    ◆ ${evts.join('  /  ')}\n`;
            const mdata = ((sy.month_scores ?? []) as any[]).map((ms:any) => ({
              m: (ms.greg_month ?? _G[(ms.month ?? 1) - 1]) as number,
              s: (ms.scores?.[thKey] ?? 50) as number,
            }));
            const gM = mdata.filter(x => x.s >= 65).sort((a,b) => b.s - a.s).slice(0,4);
            if (gM.length > 0) out += en
              ? `    → Especially good months: ${gM.map(x=>`${MONTHS_EN[(x.m-1+12)%12]} (${x.s} pts)`).join('  ')}\n`
              : `    → 특히 좋은 달: ${gM.map(x=>`${x.m}월(${x.s}점)`).join('  ')}\n`;
          });
        }
      } else {
        // 전체 연도 + 좋은달/주의달 + 이벤트
        saeunList.forEach((sy:any) => {
          const sc = (sy.scores?.[thKey] ?? 50) as number;
          const mk = sc >= 75 ? "★" : sc >= 65 ? "◎" : sc < 40 ? "▽▽" : sc < 50 ? "▽" : "–";
          out += en ? `  ${sy.year}  ${sc} pts ${mk}\n` : `  ${sy.year}년  ${sc}점 ${mk}\n`;
          // 테마 무관 이벤트 필터링 후 출력
          const evts = _filterEventsForTheme((sy.events ?? []) as string[], thKey);
          if (evts.length > 0) out += `    ◆ ${evts.join('  /  ')}\n`;
          const mdata = ((sy.month_scores ?? []) as any[]).map((ms:any) => ({
            m: (ms.greg_month ?? _G[(ms.month ?? 1) - 1]) as number,
            s: (ms.scores?.[thKey] ?? 50) as number,
          }));
          const gM = mdata.filter(x => x.s >= 65).sort((a,b) => b.s - a.s).slice(0,4);
          const bM = mdata.filter(x => x.s < 45).sort((a,b) => a.s - b.s).slice(0,3);
          if (gM.length > 0) out += en
            ? `    ↑ Good months: ${gM.map(x=>`${MONTHS_EN[(x.m-1+12)%12]} (${x.s} pts)`).join('  ')}\n`
            : `    ↑ 좋은 달: ${gM.map(x=>`${x.m}월(${x.s}점)`).join('  ')}\n`;
          if (bM.length > 0) out += en
            ? `    ↓ Caution months: ${bM.map(x=>`${MONTHS_EN[(x.m-1+12)%12]} (${x.s} pts)`).join('  ')}\n`
            : `    ↓ 주의 달: ${bM.map(x=>`${x.m}월(${x.s}점)`).join('  ')}\n`;
        });
      }
      return out.trim();
    }).join('\n\n');
  };

  switch(themeKey) {

    case 'love': {
      const mate = ctx?.gender && ctx.useGod
        ? (ctx.gender === "female" ? _MATE_M[ctx.useGod] : _MATE_F[ctx.useGod]) ?? ""
        : "";

      const daeunSec = makeDaeunDetail('love', sc => en
        ? (sc >= 75 ? `${sc} pts — a period of strong incoming romantic connection` :
           sc >= 60 ? `${sc} pts — a period when romantic energy activates` :
           sc >= 45 ? `${sc} pts — an ordinary flow, natural encounters possible` :
           `${sc} pts — watch for emotional drain; high risk of breakup`)
        : (sc >= 75 ? `${sc}점 — 이성 인연이 강하게 들어오는 시기` :
           sc >= 60 ? `${sc}점 — 연애 기운이 활성화되는 시기` :
           sc >= 45 ? `${sc}점 — 평범한 흐름, 자연스러운 만남 가능` :
           `${sc}점 — 감정 소모 주의, 이별수 높음`)
      , 'good');

      if (en) return [
        `━━━━━━━━ Love Fortune — Overall Analysis ━━━━━━━━`,
        ``,
        topDaeun
          ? `Analyzing your overall love fortune by Luck Cycle, the period of strongest incoming romantic energy is age ${topDaeun.startAge}–${topDaeun.endAge} (${topDaeun.label} Luck Cycle, ${topDaeun.score} pts). This is the golden window in which to pursue love most actively, and how you spend it may well decide the connection of a lifetime.`
          : `Here is a comprehensive analysis of your overall love fortune.`,
        ``,
        goodYears.length > 0
          ? `Specifically, the years most likely to bring a romantic connection are ${fYS(goodYears.slice(0,5))}. Among these, ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))} carry strong connection energy of 75+ points, so` : `in the highest-scoring years,`} a connection is likely through many paths — blind dates, acquaintances, or the workplace. During this time, deliberately increase your social activity and join new gatherings or classes.`
          : `In the current flow, strong love energy is briefly at rest. Rather than forcing a new connection, it is best to use this time to grow and prepare yourself.`,
        ``,
        badYears.length > 0
          ? `On the other hand, ${fY(badYears)} are periods of heightened breakup risk and emotional drain. In this time, a small misunderstanding in an ongoing relationship can flare into serious conflict, and the flow leans toward ending existing relationships rather than starting new ones. If you experience a breakup during this period, it is not necessarily a bad thing — it is wise to accept it as preparation for a better connection.`
          : "",
        ``,
        mate ? `━━━━━━━━ Traits of the Partner You Are Likely to Meet ━━━━━━━━\n\n${mate}\n\nYou are likely to connect with someone of this style, so even if the first impression is not striking, take time to get to know them slowly. It is best to look first at the person's energy and life direction rather than their appearance.` : "",
        mate ? `` : "",
        `━━━━━━━━ Love Fortune by Luck Cycle — Detailed Analysis ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ Strategy for Success in Love ━━━━━━━━`,
        ``,
        goodYears.length > 0
          ? `In the strongest love periods (${fY(goodYears.slice(0,3))}), an active attitude is key. Fortune creates the opportunity first, but if you do not open the door, the chance simply passes. When a blind date is offered, try to go at least once. Joining travel, hobby, or self-development groups is also effective.`
          : `Right now is a time to deepen inner maturity and self-understanding. Prepare so that you can make a better choice when love energy returns.`,
        ``,
        `The most important thing in love is timing. In years when connection energy enters your chart, meetings happen far more naturally than usual. Conversely, in years when it is weak, effort alone may not bring the result you want — so do not be impatient. Even a connection that seems accidental is likely a meeting set within the flow of your chart.`,
      ].filter(s => s !== "").join('\n');

      return [
        `━━━━━━━━ 연애운 종합 분석 ━━━━━━━━`,
        ``,
        topDaeun
          ? `전체 연애 운세 흐름을 대운별로 분석한 결과, 가장 강한 이성 인연의 기운이 들어오는 시기는 ${topDaeun.startAge}세~${topDaeun.endAge}세(${topDaeun.label} 대운, ${topDaeun.score}점)입니다. 이 기간은 삶에서 가장 적극적으로 연애를 시도해야 할 골든타임으로, 이 시기를 어떻게 보내느냐에 따라 평생의 인연이 결정될 가능성이 높습니다.`
          : `전체 연애 운세를 종합 분석했습니다.`,
        ``,
        goodYears.length > 0
          ? `구체적으로 이성 인연이 찾아올 가능성이 높은 해는 ${fYS(goodYears.slice(0,5))}입니다. 이 중에서도 ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))}은 75점 이상의 강한 인연 기운이 들어오는 해로,` : `점수가 가장 높은 해에`} 소개팅·지인 자리·직장 내 만남 등 다양한 경로에서 인연이 연결될 가능성이 높습니다. 이 시기에는 사교 활동을 의도적으로 늘리고, 새로운 모임이나 클래스 등에 참여해보세요.`
          : `현재 흐름에서 큰 연애 기운은 잠시 소강 상태입니다. 새로운 인연을 억지로 만들기보다 자기 자신을 성장시키고 준비하는 시기로 활용하는 것이 좋습니다.`,
        ``,
        badYears.length > 0
          ? `반면 ${fY(badYears)}에는 이별수와 감정 소모가 높아지는 시기입니다. 이 시기에는 연애 중인 관계에서 작은 오해가 큰 갈등으로 번질 수 있고, 연애 시작보다는 기존 관계를 정리하게 되는 흐름이 강하게 작용합니다. 만약 이 시기에 이별을 경험하게 된다면, 그것이 반드시 나쁜 일만은 아닙니다. 더 나은 인연을 위한 준비 과정으로 받아들이는 것이 현명합니다.`
          : "",
        ``,
        mate ? `━━━━━━━━ 나와 인연이 닿을 상대방의 특징 ━━━━━━━━\n\n${mate}\n\n이러한 스타일의 상대방과 인연이 닿을 가능성이 높으니, 처음 만날 때 인상이 강렬하지 않더라도 천천히 알아가는 시간을 가져보세요. 외모보다 그 사람이 가진 에너지와 삶의 방향성을 먼저 보는 것이 좋습니다.` : "",
        mate ? `` : "",
        `━━━━━━━━ 대운별 연애운 상세 분석 ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ 연애 성공 전략 ━━━━━━━━`,
        ``,
        goodYears.length > 0
          ? `연애 운이 가장 강한 시기(${fY(goodYears.slice(0,3))})에는 적극적인 자세가 핵심입니다. 이 시기에는 운이 먼저 기회를 만들어주지만, 본인이 문을 열지 않으면 기회가 그냥 지나칩니다. 소개팅 제안이 오면 가급적 한 번은 나가보세요. 여행·취미·자기계발 모임에 참여하는 것도 효과적입니다.`
          : `지금은 내면의 성숙과 자기 이해를 깊게 하는 시기입니다. 연애 기운이 다시 들어올 때 더 좋은 선택을 할 수 있도록 준비해두세요.`,
        ``,
        `연애에서 가장 중요한 것은 타이밍입니다. 사주에서 인연 기운이 들어오는 해에는 평소보다 훨씬 자연스럽게 만남이 이루어집니다. 반대로 인연 기운이 약한 해에는 아무리 노력해도 원하는 결과가 나오지 않을 수 있으니 조급해하지 마세요. 우연히 만나는 것처럼 보이는 인연도 사주의 흐름 안에서 정해진 만남일 가능성이 높습니다.`,
      ].filter(s => s !== "").join('\n');
    }

    case 'marry': {
      const daeunSec = makeDaeunDetail('marry', sc => en
        ? (sc >= 75 ? `${sc} pts — the strongest period for marriage fortune` :
           sc >= 60 ? `${sc} pts — a good flow for preparing and proceeding with marriage` :
           sc >= 45 ? `${sc} pts — an ordinary flow` :
           `${sc} pts — watch for obstacles when proceeding with marriage`)
        : (sc >= 75 ? `${sc}점 — 결혼 운이 가장 강한 시기` :
           sc >= 60 ? `${sc}점 — 결혼 준비·진행에 좋은 흐름` :
           sc >= 45 ? `${sc}점 — 평범한 흐름` :
           `${sc}점 — 결혼 진행 시 장애물 주의`)
      , 'good');
      if (en) return [
        `━━━━━━━━ Marriage Fortune — Overall Analysis ━━━━━━━━`,
        ``,
        topDaeun
          ? `Analyzing your marriage fortune overall, the Luck Cycle most likely to bring marriage is age ${topDaeun.startAge}–${topDaeun.endAge} (${topDaeun.label} Luck Cycle, ${topDaeun.score} pts). Within this period, the flow from dating to marriage connects most naturally.`
          : `Here is an analysis of your overall marriage fortune.`,
        ``,
        goodYears.length > 0
          ? `The most favorable years to proceed with marriage are ${fYS(goodYears.slice(0,5))}. Among these, ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))} carry especially strong marriage fortune, so` : `in the higher-scoring years,`} a proposal, meeting of both families, marriage registration, or wedding are all likely to be received naturally in this period.`
          : `In the current flow, marriage fortune is in a preparatory stage. It is more advantageous to proceed after building a financial foundation and enough trust with your partner.`,
        ``,
        goodYears.length > 0
          ? `For the wedding date, spring (March–May) or autumn (September–October) is favorable for a stable start. It is even better to consult a date-selection specialist for a day when your favorable element is active. Arranging the budget and schedule in advance during preparation reduces unnecessary conflict.`
          : "",
        ``,
        badYears.length > 0
          ? `On the other hand, in ${fY(badYears)}, unexpected obstacles may arise during marriage preparation. Financial issues, disagreement between families, or a change of heart may appear, so in this period do enough talking and confirming before any major decision.`
          : "",
        ``,
        `━━━━━━━━ Marriage Fortune by Luck Cycle — Detailed Analysis ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ Strategy for Marriage Preparation ━━━━━━━━`,
        ``,
        `Timing matters in marriage, but trust with your partner and a financial foundation come first. Rather than forcing marriage during a strong period, the ideal is for marriage fortune to arrive when the relationship has fully matured. Sharing each other's views on money, family, and lifestyle thoroughly before marriage is the key to reducing conflict afterward.`,
        ``,
        `The first one to three years after marriage are an adjustment period, and how you resolve small conflicts then sets the direction of the whole marriage. When a small conflict arises, an attitude of seeking to understand matters more than trying to win.`,
      ].filter(s => s !== "").join('\n');
      return [
        `━━━━━━━━ 결혼운 종합 분석 ━━━━━━━━`,
        ``,
        topDaeun
          ? `결혼 운세를 전체적으로 분석한 결과, 결혼이 이루어질 가능성이 가장 높은 대운은 ${topDaeun.startAge}세~${topDaeun.endAge}세(${topDaeun.label} 대운, ${topDaeun.score}점)입니다. 이 기간 안에서 연애에서 결혼까지의 흐름이 가장 자연스럽게 이어지는 구조입니다.`
          : `결혼운 전체 흐름을 분석했습니다.`,
        ``,
        goodYears.length > 0
          ? `결혼 진행에 가장 유리한 해는 ${fYS(goodYears.slice(0,5))}입니다. 이 해들 중에서 ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))}은 특히 강한 결혼 운이 들어오는 해로,` : `점수가 높은 해에`} 프러포즈·양가 상견례·혼인신고·결혼식 모두 이 시기에 진행하면 자연스럽게 받아들여질 가능성이 높습니다.`
          : `현재 흐름에서 결혼 운은 준비 단계입니다. 경제적 기반과 상대방과의 신뢰를 충분히 쌓은 후 진행하는 것이 유리합니다.`,
        ``,
        goodYears.length > 0
          ? `결혼식 날짜 선택은 봄(3~5월) 또는 가을(9~10월)이 안정적인 시작에 유리합니다. 특히 용신 오행이 활성화되는 날을 택일사에게 문의해서 고르면 더욱 좋습니다. 결혼 준비 과정에서 예산과 일정을 미리 조율해두면 불필요한 갈등을 줄일 수 있습니다.`
          : "",
        ``,
        badYears.length > 0
          ? `반면 ${fY(badYears)}에는 결혼 준비 과정에서 예상치 못한 장애물이 생길 수 있습니다. 경제적 문제나 양가 의견 충돌, 상대방의 마음 변화 등이 나타날 수 있으므로 이 시기에는 중요한 결정보다 충분한 대화와 확인을 먼저 하세요.`
          : "",
        ``,
        `━━━━━━━━ 대운별 결혼운 상세 분석 ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ 결혼 준비 전략 ━━━━━━━━`,
        ``,
        `결혼은 타이밍도 중요하지만, 상대방과의 신뢰와 경제적 기반이 먼저입니다. 결혼 운이 강한 시기에 억지로 결혼을 밀어붙이기보다, 상대방과의 관계가 충분히 무르익었을 때 결혼 운이 들어오는 것이 이상적입니다. 결혼 전 서로의 경제관·가족관·생활습관을 충분히 공유하는 과정이 결혼 후 갈등을 줄이는 핵심입니다.`,
        ``,
        `결혼 후 첫 1~3년은 적응 기간으로, 이 시기에 사소한 갈등을 어떻게 해결하느냐가 결혼 생활 전체의 방향을 결정합니다. 작은 갈등이 생겼을 때 이기려 하기보다 이해하려는 자세가 더 중요합니다.`,
      ].filter(s => s !== "").join('\n');
    }

    case 'business': {
      const bizList = ctx?.useGod ? (_BIZ[ctx.useGod] ?? []) : [];
      const daeunSec = makeDaeunDetail('business', sc => en
        ? (sc >= 75 ? `${sc} pts — a good period for expansion, contracts, and investment alike` :
           sc >= 60 ? `${sc} pts — a flow of opening business opportunity` :
           sc >= 45 ? `${sc} pts — stable operation is best` :
           `${sc} pts — caution with large investments and contracts`)
        : (sc >= 75 ? `${sc}점 — 사업 확장·계약·투자 모두 좋은 시기` :
           sc >= 60 ? `${sc}점 — 사업 기회가 열리는 흐름` :
           sc >= 45 ? `${sc}점 — 안정적 운영이 최선` :
           `${sc}점 — 대규모 투자·계약 주의`)
      );
      if (en) return [
        `━━━━━━━━ Business Fortune — Overall Analysis ━━━━━━━━`,
        ``,
        topDaeun
          ? `Analyzing your business fortune by Luck Cycle, the period of strongest incoming business energy is age ${topDaeun.startAge}–${topDaeun.endAge} (${topDaeun.label} Luck Cycle, ${topDaeun.score} pts). This is the golden window for achieving your greatest business results.`
          : `Here is an analysis of your overall business fortune.`,
        ``,
        goodYears.length > 0
          ? `Specifically, the years of strong incoming business fortune are ${fYS(goodYears.slice(0,5))}. Among these, ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))} are` : `in the top-scoring years,`} worth attempting a new venture, a major contract, or client expansion. In this period, put into action the business ideas you were once too afraid to try.`
          : `In the current flow, business fortune is in a preparatory phase. Rather than new expansion, use this time to strengthen the fundamentals of your existing business.`,
        ``,
        goodYears.length > 0
          ? `Even in good business years, distinguish your investment direction. In years above 75 points, launching new ventures, securing clients, and signing contracts are favorable; in years between 65 and 75, focusing on operating capital and managing existing clients is safer. Undertake real estate deals or large facility investment in the highest-scoring year, when you have ample funds.`
          : "",
        ``,
        bizList.length > 0
          ? `━━━━━━━━ Favorable Industries Based on Your Favorable Element (${_god(ctx?.useGod)}) ━━━━━━━━\n\nA chart whose favorable element is ${_god(ctx?.useGod)} tends to stand out in industries where this element's energy is activated.\n\nFavorable industries: ${bizList.join(', ')}\n\nAmong these, it is important to choose a field where you have experience and a network. Even an industry that suits your chart is hard to succeed in without preparation. The ideal is to build three or more years of experience in a favorable industry before starting your own venture.`
          : "",
        bizList.length > 0 ? `` : "",
        badYears.length > 0
          ? `━━━━━━━━ Business Caution Periods ━━━━━━━━\n\nIn ${fY(badYears)}, unexpected losses or conflict may occur in business. Avoid large investments, real estate deals, starting a partnership, or opening a new venture, and manage cash liquidity as the top priority. Always have contracts reviewed by an expert, and put verbal promises in writing.`
          : "",
        ``,
        `━━━━━━━━ Business Fortune by Luck Cycle — Detailed Analysis ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ Strategy for Business Success ━━━━━━━━`,
        ``,
        `The most important things in business are timing and preparation. To achieve maximum results, you must jump in fully prepared when business fortune is strong. Starting preparation one to two years before the fortune arrives and executing in the strongest year is an effective strategy.`,
        ``,
        `Partnerships should be reviewed together with the other person's chart. No matter how good your business fortune, a mismatch in the elemental relationship with your partner can lead to loss. Before partnering, always go through a sufficient period of trust, and clearly set out roles and profit-sharing in a contract.`,
      ].filter(s => s !== "").join('\n');
      return [
        `━━━━━━━━ 사업운 종합 분석 ━━━━━━━━`,
        ``,
        topDaeun
          ? `전체 사업 운세를 대운별로 분석한 결과, 사업 에너지가 가장 강하게 들어오는 시기는 ${topDaeun.startAge}세~${topDaeun.endAge}세(${topDaeun.label} 대운, ${topDaeun.score}점)입니다. 이 기간이 사업에서 가장 큰 성과를 낼 수 있는 골든타임입니다.`
          : `사업운 전체 흐름을 분석했습니다.`,
        ``,
        goodYears.length > 0
          ? `구체적으로 사업 운이 강하게 들어오는 해는 ${fYS(goodYears.slice(0,5))}입니다. 이 중 ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))}은` : `상위 점수 해에는`} 신규 사업 시작·주요 계약 체결·거래처 확장 모두 시도해볼 만한 해입니다. 이 시기에는 평소에 두려워서 시도하지 못했던 사업 아이디어를 실행에 옮겨보세요.`
          : `현재 흐름에서 사업 운은 준비 구간입니다. 신규 확장보다 현재 사업의 내실을 다지는 시기로 활용하세요.`,
        ``,
        goodYears.length > 0
          ? `사업 운이 좋은 해에도 투자 방향은 구분이 필요합니다. 75점 이상의 해에는 신규 사업·거래처 확보·계약 체결이 유리하고, 65~75점 사이의 해에는 운영 자금 확보와 기존 거래처 관리에 집중하는 것이 더 안전합니다. 부동산 계약이나 대규모 시설 투자는 사업 운이 가장 높은 해 중 자금 여유가 충분할 때 진행하세요.`
          : "",
        ``,
        bizList.length > 0
          ? `━━━━━━━━ 사주 용신(${ctx?.useGod}) 기반 유리한 업종 ━━━━━━━━\n\n용신 오행이 ${ctx?.useGod}인 사주는 이 오행의 에너지가 활성화되는 업종에서 두각을 나타내는 경향이 있습니다.\n\n유리한 업종 목록: ${bizList.join(', ')}\n\n이 중에서도 본인의 경험과 네트워크가 있는 분야를 선택하는 것이 중요합니다. 사주가 맞는 업종이라도 준비 없이 뛰어들면 결과를 얻기 어렵습니다. 용신 업종에서 3년 이상 경험을 쌓은 후 창업하는 것이 이상적입니다.`
          : "",
        bizList.length > 0 ? `` : "",
        badYears.length > 0
          ? `━━━━━━━━ 사업 주의 시기 ━━━━━━━━\n\n${fY(badYears)}에는 사업에서 예상치 못한 손실이나 갈등이 발생할 수 있습니다. 이 시기에는 대규모 투자·부동산 계약·동업 시작·신규 사업 오픈을 피하고 현금 유동성을 최우선으로 관리하세요. 계약서는 반드시 전문가 검토를 거치고, 구두 약속은 서면으로 남겨두세요.`
          : "",
        ``,
        `━━━━━━━━ 대운별 사업운 상세 분석 ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ 사업 성공 전략 ━━━━━━━━`,
        ``,
        `사업에서 가장 중요한 것은 타이밍과 준비입니다. 사업 운이 강한 시기에 이미 준비가 완료된 상태로 뛰어들어야 최대 성과를 낼 수 있습니다. 사업 운이 들어오기 1~2년 전부터 준비를 시작하고, 운이 가장 강한 해에 실행하는 전략이 효과적입니다.`,
        ``,
        `동업은 상대방의 사주와 함께 검토해야 합니다. 아무리 사업 운이 좋아도 동업자와의 오행 관계가 맞지 않으면 손실로 이어질 수 있습니다. 동업 전 반드시 충분한 신뢰 기간을 거치고, 역할과 수익 분배를 명확히 계약서로 정리해두세요.`,
      ].filter(s => s !== "").join('\n');
    }

    case 'career': {
      const jobList = ctx?.useGod ? (_JOB[ctx.useGod] ?? []) : [];
      const daeunSec = makeDaeunDetail('career', sc => en
        ? (sc >= 75 ? `${sc} pts — a strong period for job change, promotion, and success alike` :
           sc >= 60 ? `${sc} pts — a flow of opening career-advancement opportunity` :
           sc >= 45 ? `${sc} pts — building skills where you are is best` :
           `${sc} pts — caution with workplace conflict and instability`)
        : (sc >= 75 ? `${sc}점 — 이직·승진·합격 모두 강한 시기` :
           sc >= 60 ? `${sc}점 — 커리어 상승 기회가 열리는 흐름` :
           sc >= 45 ? `${sc}점 — 현재 자리에서 실력 축적이 최선` :
           `${sc}점 — 직장 내 갈등·불안정 주의`)
      );
      if (en) return [
        `━━━━━━━━ Career Fortune — Overall Analysis ━━━━━━━━`,
        ``,
        topDaeun
          ? `Analyzing your overall career fortune, the period of strongest career-advancement energy is age ${topDaeun.startAge}–${topDaeun.endAge} (${topDaeun.label} Luck Cycle, ${topDaeun.score} pts). This is the period to make your biggest professional leap, and a choice made now can set the direction of the next ten years.`
          : `Here is an analysis of your overall career fortune.`,
        ``,
        goodYears.length > 0
          ? `Good years to attempt a job change, promotion, or getting hired are ${fYS(goodYears.slice(0,5))}. In particular, ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))} carry` : `in the highest-fortune years,`} an energy that makes a far better impression than usual when you submit applications or interview. In these years, boldly apply to the companies or positions you once feared to attempt.`
          : `In the current flow, career fortune is a phase for building substance. Rather than a flashy job change, building trust and expertise at your current job is the most advantageous strategy for this period.`,
        ``,
        goodYears.length > 0
          ? `In good career years, salary negotiations also go more favorably than usual. Requesting a raise or a position change during this time is likely to be accepted. However, do not impulsively apply to many places at once just because your fortune is strong — you may let opportunities slip. Set a direction, then move with focus.`
          : "",
        ``,
        jobList.length > 0
          ? `━━━━━━━━ Favorable Occupations Based on Your Favorable Element (${_god(ctx?.useGod)}) ━━━━━━━━\n\nA chart whose favorable element is ${_god(ctx?.useGod)} expresses its abilities best in occupations where this energy is activated.\n\nFavorable occupations: ${jobList.join(', ')}\n\nBuilding ten or more years of expertise in one of these fields makes you likely to reach an unrivaled position within it. If your current job is on this list, your current direction is right. If not, a change is worth considering.`
          : "",
        jobList.length > 0 ? `` : "",
        badYears.length > 0
          ? `━━━━━━━━ Career Caution Periods ━━━━━━━━\n\nIn ${fY(badYears)}, workplace conflict or instability may occur. Avoid impulsive job changes or starting a business, and take special care with workplace relationships. A single remark can easily turn into gossip in this period, so manage your words and actions carefully.`
          : "",
        ``,
        `━━━━━━━━ Career Fortune by Luck Cycle — Detailed Analysis ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ Strategy for Career Success ━━━━━━━━`,
        ``,
        `To make the most of a strong career period, you must be prepared one to two years before it arrives. Earning certifications, organizing a portfolio, and expanding your network in advance let you achieve far more when career fortune enters.`,
        ``,
        `The core of workplace success is expertise and trust. No matter how good your career fortune, you cannot seize the chance without the skills to back it up. Use weak periods to accumulate skill and move boldly in strong ones — that is the formula for career success.`,
      ].filter(s => s !== "").join('\n');
      return [
        `━━━━━━━━ 직업운 종합 분석 ━━━━━━━━`,
        ``,
        topDaeun
          ? `전체 직업 운세를 분석한 결과, 커리어 상승 에너지가 가장 강한 시기는 ${topDaeun.startAge}세~${topDaeun.endAge}세(${topDaeun.label} 대운, ${topDaeun.score}점)입니다. 이 기간은 직업적으로 가장 큰 도약을 이룰 수 있는 시기로, 이 때 한 선택이 이후 10년의 직업 방향을 결정할 수 있습니다.`
          : `직업운 전체 흐름을 분석했습니다.`,
        ``,
        goodYears.length > 0
          ? `이직·승진·합격을 시도하기에 좋은 해는 ${fYS(goodYears.slice(0,5))}입니다. 특히 ${greatYears.length > 0 ? `${fY(greatYears.slice(0,3))}은` : `운이 가장 높은 해에는`} 이력서를 제출하거나 면접을 볼 때 평소보다 훨씬 좋은 인상을 주는 기운이 작용합니다. 이 해에는 평소 도전하기 두려웠던 회사나 포지션에 과감하게 지원해보세요.`
          : `현재 흐름에서 직업 운은 내실을 다지는 구간입니다. 화려한 이직보다 현재 직장에서 신뢰와 전문성을 쌓는 것이 이 시기에 가장 유리한 전략입니다.`,
        ``,
        goodYears.length > 0
          ? `직업 운이 좋은 해에는 연봉 협상도 평소보다 유리하게 진행됩니다. 이 시기에 연봉 인상 요청이나 포지션 변경 제안을 하면 받아들여질 가능성이 높습니다. 단, 직업 운이 강하다고 충동적으로 여러 곳에 동시에 이직을 시도하면 오히려 기회를 흘려보낼 수 있으니, 방향을 정한 후 집중적으로 움직이세요.`
          : "",
        ``,
        jobList.length > 0
          ? `━━━━━━━━ 사주 용신(${ctx?.useGod}) 기반 유리한 직종 ━━━━━━━━\n\n용신 오행이 ${ctx?.useGod}인 사주는 이 에너지가 활성화되는 직종에서 자신의 능력을 가장 잘 발휘할 수 있습니다.\n\n유리한 직종 목록: ${jobList.join(', ')}\n\n이 분야에서 10년 이상 전문성을 쌓으면 그 분야에서 독보적인 위치에 오를 가능성이 높습니다. 지금 하고 있는 직업이 이 목록에 있다면 지금 방향이 맞습니다. 없다면 전환을 고려해볼 만합니다.`
          : "",
        jobList.length > 0 ? `` : "",
        badYears.length > 0
          ? `━━━━━━━━ 직업 주의 시기 ━━━━━━━━\n\n${fY(badYears)}에는 직장 내 갈등이나 불안정한 상황이 발생할 수 있습니다. 이 시기에는 충동적인 이직이나 창업을 피하고, 직장 내 인간관계에 각별히 주의하세요. 말 한 마디가 뒷말로 이어지기 쉬운 시기이니 언행을 신중하게 관리하세요.`
          : "",
        ``,
        `━━━━━━━━ 대운별 직업운 상세 분석 ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ 커리어 성공 전략 ━━━━━━━━`,
        ``,
        `직업 운이 강한 시기를 최대한 활용하려면 그 시기가 오기 1~2년 전부터 준비가 되어 있어야 합니다. 자격증 취득, 포트폴리오 정리, 네트워킹 확대를 미리 해두면 직업 운이 들어올 때 훨씬 큰 성과를 낼 수 있습니다.`,
        ``,
        `직장에서 성공하는 핵심은 전문성과 신뢰입니다. 아무리 직업 운이 좋아도 실력이 뒷받침되지 않으면 기회를 잡기 어렵습니다. 직업 운이 약한 시기를 실력 축적의 기간으로 활용하고, 강한 시기에 과감하게 움직이는 것이 커리어 성공의 공식입니다.`,
      ].filter(s => s !== "").join('\n');
    }

    case 'health': {
      const riskParts = ctx?.avoidGod ? (_HEALTH_RISK[ctx.avoidGod] ?? []) : [];
      const daeunSec = makeDaeunDetail('health', sc => en
        ? (sc >= 75 ? `${sc} pts — optimal period for stamina recovery and health care` :
           sc >= 60 ? `${sc} pts — a healthy flow` :
           sc >= 45 ? `${sc} pts — steady care needed` :
           `${sc} pts — high risk of accidents and surgery; caution needed`)
        : (sc >= 75 ? `${sc}점 — 체력 회복 및 건강 관리 최적 시기` :
           sc >= 60 ? `${sc}점 — 건강 흐름 양호` :
           sc >= 45 ? `${sc}점 — 꾸준한 관리 필요` :
           `${sc}점 — 사고수·수술수 높음, 주의 필요`)
      );
      if (en) return [
        `━━━━━━━━ Health Fortune — Overall Analysis ━━━━━━━━`,
        ``,
        riskParts.length > 0
          ? `Based on your unfavorable element (${_god(ctx?.avoidGod)}), the areas to watch most throughout life are as follows.\n\n${riskParts.map((r,i) => `${i+1}. ${r}`).join('\n')}\n\nThese are the areas that send the first signals when stress or overwork builds up. Focus on them during regular checkups, and manage the related diet and lifestyle habits from early on.`
          : "",
        riskParts.length > 0 ? `` : "",
        topDaeun && topDaeun.score >= 60
          ? `In your overall health flow, the period of strongest stamina and best health management is age ${topDaeun.startAge}–${topDaeun.endAge} (${topDaeun.label} Luck Cycle, ${topDaeun.score} pts). Establishing checkups and exercise habits in this period becomes a great asset for your later health.`
          : worstDaeun && worstDaeun.score < 50
            ? `In your overall health flow, the period needing the most caution is age ${worstDaeun.startAge}–${worstDaeun.endAge} (${worstDaeun.label} Luck Cycle, ${worstDaeun.score} pts). In this period, make managing overwork and stress the top priority, and if you plan surgery or a procedure, avoid this period if possible.`
            : `Here is a health-fortune analysis by Luck Cycle.`,
        ``,
        badYears.length > 0
          ? `The years with a higher risk of accidents and surgery in particular are ${fYS(badYears.slice(0,4))}. In these years, take special care with driving, leisure activities, and strenuous late-night schedules. If fatigue builds faster than usual or your body sends warning signs (headaches, indigestion, sleep problems), see a doctor right away. Ignoring small symptoms often leads to serious illness.`
          : "",
        ``,
        goodYears.length > 0
          ? `On the other hand, the years when health and stamina recover and improve are ${fY(goodYears.slice(0,3))}. This is a good time to start a new exercise habit or get the checkup you have been putting off. Building baseline fitness while you are strong is a great help for weathering weaker health periods.`
          : "",
        ``,
        `━━━━━━━━ Health Fortune by Luck Cycle — Detailed Analysis ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ Strategy for Health Management ━━━━━━━━`,
        ``,
        riskParts.length > 0
          ? `Conditions related to your vulnerable areas — ${riskParts.slice(0,2).join(', ')} — often feel mild in their early stages. If you feel discomfort in these areas, do not brush it off; get checked right away. Early detection determines both treatment outcome and recovery speed.`
          : "",
        ``,
        `Once health collapses, it takes several times as long to recover. Getting 7–8 hours of sleep, eating regularly, and doing light aerobic exercise at least three times a week are the most basic foundations that sustain your health fortune. Stress has the greatest impact on health, so be sure to build a routine for relieving it.`,
      ].filter(s => s !== "").join('\n');
      return [
        `━━━━━━━━ 건강운 종합 분석 ━━━━━━━━`,
        ``,
        riskParts.length > 0
          ? `사주 기신(${ctx?.avoidGod}) 오행을 기반으로 분석한 결과, 평생 가장 주의해야 할 취약 부위는 다음과 같습니다.\n\n${riskParts.map((r,i) => `${i+1}. ${r}`).join('\n')}\n\n이 부위들은 스트레스나 과로가 쌓일 때 가장 먼저 신호를 보내는 곳입니다. 정기 검진 시 이 부위를 중점적으로 확인하고, 이 부위에 관련된 식습관과 생활 습관을 평소부터 관리해두세요.`
          : "",
        riskParts.length > 0 ? `` : "",
        topDaeun && topDaeun.score >= 60
          ? `전체 건강 운세 흐름에서 체력이 가장 강하고 건강 관리가 잘 되는 시기는 ${topDaeun.startAge}세~${topDaeun.endAge}세(${topDaeun.label} 대운, ${topDaeun.score}점)입니다. 이 기간에 건강 검진과 운동 습관을 잡아두면 이후 건강에 큰 자산이 됩니다.`
          : worstDaeun && worstDaeun.score < 50
            ? `전체 건강 운세 흐름에서 가장 주의가 필요한 시기는 ${worstDaeun.startAge}세~${worstDaeun.endAge}세(${worstDaeun.label} 대운, ${worstDaeun.score}점)입니다. 이 기간에는 과로와 스트레스 관리를 최우선으로 하고, 수술이나 시술 계획이 있다면 가능하면 이 기간을 피해서 진행하세요.`
            : `건강 운세를 대운별로 분석했습니다.`,
        ``,
        badYears.length > 0
          ? `특히 사고수·수술수가 높아지는 해는 ${fYS(badYears.slice(0,4))}입니다. 이 해들에는 운전·레저 활동·무리한 야간 일정에 각별히 주의하세요. 평소와 달리 피로감이 빨리 쌓이거나 몸이 보내는 경고 신호(두통, 소화 불량, 수면 장애 등)가 있다면 즉시 병원을 찾는 것이 좋습니다. 작은 증상을 무시하다가 큰 병으로 이어지는 경우가 많습니다.`
          : "",
        ``,
        goodYears.length > 0
          ? `반면 건강과 체력이 회복되고 좋아지는 해는 ${fY(goodYears.slice(0,3))}입니다. 이 시기는 새로운 운동 습관을 시작하거나, 그동안 미뤄왔던 건강 검진을 받기 좋은 때입니다. 체력이 좋을 때 기초 체력을 더 쌓아두면 건강 운이 약한 시기를 버티는 데 큰 힘이 됩니다.`
          : "",
        ``,
        `━━━━━━━━ 대운별 건강운 상세 분석 ━━━━━━━━`,
        ``,
        daeunSec,
        ``,
        `━━━━━━━━ 건강 관리 전략 ━━━━━━━━`,
        ``,
        riskParts.length > 0
          ? `취약 부위인 ${riskParts.slice(0,2).join(', ')} 관련 질환은 초기 증상이 가볍게 느껴지는 경우가 많습니다. 이 부위에 불편함이 느껴지면 넘어가지 말고 바로 검진을 받으세요. 조기 발견이 치료 효과와 회복 속도를 결정합니다.`
          : "",
        ``,
        `건강은 한 번 무너지면 회복하는 데 몇 배의 시간이 걸립니다. 수면 7~8시간 확보, 규칙적인 식사, 주 3회 이상 가벼운 유산소 운동이 건강 운을 지탱하는 가장 기본적인 토대입니다. 스트레스가 건강에 미치는 영향이 가장 크니, 스트레스 해소 루틴을 반드시 만들어두세요.`,
      ].filter(s => s !== "").join('\n');
    }

    default: return '';
  }
}

// 서비스별 종합 서술형 분석 (연애운/결혼운/사업운/직업운/건강운)
const HOLISTIC_PRIMARY: Record<string,string> = {
  "연애운": "love", "결혼운": "marry", "사업운": "business",
  "직업운": "career", "건강운": "health",
};
const HOLISTIC_ICON: Record<string,string> = {
  "연애운":"♥", "결혼운":"", "사업운":"💼", "직업운":"🏆", "건강운":"",
};

function ServiceNarrativeReport({daeunScores, svcType, reportConfig, ctx}: {daeunScores:any[]; svcType:string; reportConfig:any; ctx?:NarrCtx}) {
  const { lang, t: L10N } = useLang();
  const now = new Date().getFullYear();

  // 연애운/결혼운/사업운/직업운/건강운 → 종합 서술형 500자
  if (svcType in HOLISTIC_PRIMARY) {
    const key = HOLISTIC_PRIMARY[svcType];
    const narrative = makeHolisticNarrative(key, daeunScores, ctx, lang);
    const icon = HOLISTIC_ICON[svcType] ?? "✦";
    const svcName = L10N("svc." + svcType);
    return (
      <div style={{margin:"12px 16px 0"}}>
        <ThemeBlock>
          <ThemeHeader2>
            <span style={{fontSize:"1.1rem"}}>{icon}</span>
            <span style={{flex:1,fontSize:".85rem",fontWeight:700,color:"#2B2A26"}}>{lang === "en" ? `${svcName} — Overall Analysis` : `${svcType} 종합 분석`}</span>
          </ThemeHeader2>
          <CommentBox style={{lineHeight:1.9,whiteSpace:"pre-wrap"}}>{narrative}</CommentBox>
        </ThemeBlock>
      </div>
    );
  }

  // 신년운세 / 재회운
  const allSaeun: any[] = daeunScores.flatMap((d:any) => d.saeun_scores ?? []);
  let useSaeun: any[];
  if (svcType === "신년운세") {
    const sel: number[] = reportConfig?.saeun_selected ?? [];
    const filtered = sel.length > 0 ? allSaeun.filter((sy:any) => sel.includes(sy.year)) : [];
    if (filtered.length > 0) {
      useSaeun = filtered;
    } else {
      const future = allSaeun.filter((sy:any) => sy.year >= now).sort((a:any,b:any) => a.year - b.year);
      useSaeun = future.length > 0 ? [future[0]] : allSaeun.slice(-1);
    }
  } else {
    const future = allSaeun.filter((sy:any) => sy.year >= now);
    useSaeun = future.length > 0 ? future : allSaeun.slice(-8);
  }

  const themeKeys = SVC_THEMES[svcType] ?? ["love","marry","business","career","wealth","health"];
  const targetYear = svcType === "신년운세" && useSaeun.length > 0 ? useSaeun[0].year : null;

  return (
    <div style={{margin:"12px 16px 0",display:"flex",flexDirection:"column",gap:10}}>
      {targetYear && (
        <div style={{fontSize:".78rem",fontWeight:700,color:"#8C8677",padding:"0 2px",marginBottom:2}}>
          {lang === "en" ? `${targetYear} New Year Reading` : `${targetYear}년 신년운세`}
        </div>
      )}
      {themeKeys.map(key => {
        const avgSc = useSaeun.length > 0
          ? Math.round(useSaeun.reduce((s:number,sy:any) => s+(sy.scores?.[key]??50), 0) / useSaeun.length)
          : 50;
        const narrative = makeNarrative(key, avgSc, useSaeun, ctx, lang);
        if (!narrative) return null;
        const t = THEME_META[key] ?? {icon:"✦", name:key};
        return (
          <ThemeBlock key={key}>
            <ThemeHeader2>
              <span style={{fontSize:"1.1rem"}}>{t.icon}</span>
              <span style={{flex:1,fontSize:".85rem",fontWeight:700,color:"#2B2A26"}}>{themeName(key, lang, t.name)}</span>
            </ThemeHeader2>
            <CommentBox>{narrative}</CommentBox>
          </ThemeBlock>
        );
      })}
    </div>
  );
}

const DAEUN_THEMES = [
  {key:"love",icon:"♥",name:"연애운"},{key:"marry",icon:"",name:"결혼운"},
  {key:"business",icon:"💼",name:"사업운"},{key:"career",icon:"🏆",name:"직업운"},
  {key:"wealth",icon:"",name:"재물운"},{key:"health",icon:"",name:"건강운"},
  {key:"family",icon:"🏠",name:"가족관계"},{key:"friends",icon:"🤝",name:"친구관계"},
];

function getDefaultDaeunThemesR(startAge: number): string[] {
  if (startAge < 10) return ["health","family","friends"];
  if (startAge < 20) return ["health","family","friends","career"];
  return DAEUN_THEMES.map(t => t.key);
}

function DaeunSection({d,idx,themeConfig,svcThemes,ctx}:{d:any;idx:number;themeConfig:Record<string,string[]>;svcThemes?:string[];ctx?:NarrCtx}){
  const { lang } = useLang();
  const _oeName = (o:string) => lang === "en" ? (OE_EN[o] ?? o) : (OE_KOR[o] ?? o);
  const [open,setOpen]=useState(idx===0);
  const startAge = d.start_age ?? parseInt(String(d.age||"0").replace("세","")) ?? 0;
  const baseKeys: string[] = themeConfig[d.label] ?? getDefaultDaeunThemesR(startAge);
  const enabledKeys = svcThemes ? baseKeys.filter(k => svcThemes.includes(k)) : baseKeys;
  const visibleThemes = DAEUN_THEMES.filter(t => enabledKeys.includes(t.key));
  const avg = visibleThemes.length
    ? visibleThemes.reduce((s,t)=>s+(d[t.key]?.score??0),0)/visibleThemes.length
    : DAEUN_THEMES.reduce((s,t)=>s+(d[t.key]?.score??0),0)/DAEUN_THEMES.length;
  const isGil=avg>=50;
  return(
    <DaeunCard open={open}>
      <DaeunHeader open={open} isGil={isGil} onClick={()=>setOpen(v=>!v)}>
        <AgeTag isGil={isGil}><div>{d.age_range}</div></AgeTag>
        <DaeunInfo>
          <DaeunGanji>{d.label}</DaeunGanji>
          <DaeunOe>
            <span style={{color:OE_COLOR[d.gan_oe]||"#8C8677"}}>{lang === "en" ? "Stem" : "천간"} {_oeName(d.gan_oe)}</span>
            {" · "}
            <span style={{color:OE_COLOR[d.ji_oe]||"#8C8677"}}>{lang === "en" ? "Branch" : "지지"} {_oeName(d.ji_oe)}</span>
          </DaeunOe>
        </DaeunInfo>
        <ChevronIcon open={open}>▼</ChevronIcon>
      </DaeunHeader>
      <DaeunBody open={open}>
        <DaeunInner>
          {visibleThemes.map(t=>{
            const th=d[t.key]; if(!th)return null;
            const score:number=th.score??0;
            return(
              <ThemeBlock key={t.key}>
                <ThemeHeader2>
                  <span style={{fontSize:"1.1rem"}}>{t.icon}</span>
                  <span style={{flex:1,fontSize:".82rem",fontWeight:700,color:"#8C8677"}}>{themeName(t.key, lang, t.name)}</span>
                </ThemeHeader2>
                <CommentBox>{makeNarrative(t.key, score, d.saeun_scores ?? [], ctx, lang)}</CommentBox>
              </ThemeBlock>
            );
          })}
        </DaeunInner>
      </DaeunBody>
    </DaeunCard>
  );
}

/* ── 오각형 pentagon 상수 ── */
const P_OE_ORDER = ["목","화","토","금","수"] as const;
const P_OE_COLOR: Record<string,string> = {목:"#2E86AB",화:"#D0654F",토:"#F4A261",금:"#A8A8A8",수:"#1D3557"};
const P_OE_BG:    Record<string,string> = {목:"#EBF4F9",화:"#FDE8EA",토:"#FEF4E8",금:"#F5F5F5",수:"#E8EDF3"};
const P_OE_KAN:   Record<string,string> = {목:"木",화:"火",토:"土",금:"金",수:"水"};

function extractOePct(wx: any): Record<string,number> {
  const extAn = wx?.extended_analysis;
  const dmPct = extAn?.diagram_matrix?.opt_on_on?.pct as Record<string,number> | undefined;
  const ftPct = extAn?.forceteller_ohaeng?.pct  as Record<string,number> | undefined;
  const src = dmPct || ftPct;
  if (src && Object.keys(src).length) {
    return Object.fromEntries(P_OE_ORDER.map(o => [o, Math.round(src[o] || 0)]));
  }
  const vals = P_OE_ORDER.map(k => (typeof wx?.[k] === "number" ? wx[k] : 0));
  const total = vals.reduce((a,b)=>a+b,0) || 1;
  return Object.fromEntries(P_OE_ORDER.map((k,i) => [k, Math.round(vals[i]/total*100)]));
}

const P_OE_CYCLE = ["목","화","토","금","수"];
function _relLabel(oe:string, dayOe:string):string {
  if(oe===dayOe) return "비겁";
  const idx=(o:string)=>P_OE_CYCLE.indexOf(o);
  if(P_OE_CYCLE[(idx(oe)+1)%5]===dayOe) return "인성";
  if(P_OE_CYCLE[(idx(dayOe)+1)%5]===oe)  return "식상";
  if(P_OE_CYCLE[(idx(dayOe)+2)%5]===oe)  return "재성";
  return "관성";
}

function OePentagon({wx, name, uid, dayOe="목"}: {wx:any; name:string; uid:string; dayOe?:string}) {
  const oePct = extractOePct(wx);
  const SZ=280, CX=140, CY=140, R=100, NR=34;
  const dayOeIdx = P_OE_ORDER.indexOf(dayOe as typeof P_OE_ORDER[number]);
  const pts = P_OE_ORDER.map((_,i) => {
    const a=((i - dayOeIdx)*72-90)*Math.PI/180;
    return {x:CX+R*Math.cos(a), y:CY+R*Math.sin(a)};
  });
  const edgePt = (si:number,ei:number,off:number) => {
    const s=pts[si],e=pts[ei],dx=e.x-s.x,dy=e.y-s.y,len=Math.sqrt(dx*dx+dy*dy);
    return {x:s.x+(dx/len)*off, y:s.y+(dy/len)*off};
  };
  const dist = (si:number,ei:number) => {
    const s=pts[si],e=pts[ei];
    return Math.sqrt((e.x-s.x)**2+(e.y-s.y)**2);
  };
  const genArr = P_OE_ORDER.map((_,i)=>{const ni=(i+1)%5;return{s:edgePt(i,ni,NR+5),e:edgePt(i,ni,dist(i,ni)-NR-5)};});
  const ctlArr = P_OE_ORDER.map((_,i)=>{const ni=(i+2)%5;return{s:edgePt(i,ni,NR+5),e:edgePt(i,ni,dist(i,ni)-NR-5)};});
  const mBId=`mB${uid}`, mRId=`mR${uid}`;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1}}>
      <div style={{fontSize:11,fontWeight:700,color:"#8C8677",marginBottom:4,letterSpacing:".02em"}}>{name}</div>
      <svg width="100%" viewBox={`0 0 ${SZ} ${SZ}`} style={{display:"block"}}>
        <defs>
          <marker id={mBId} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#4A7BA8"/>
          </marker>
          <marker id={mRId} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#D05050"/>
          </marker>
        </defs>
        {ctlArr.map((a,i)=><line key={`c${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#D05050" strokeWidth={1.5} markerEnd={`url(#${mRId})`}/>)}
        {genArr.map((a,i)=><line key={`g${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#4A7BA8" strokeWidth={1.5} markerEnd={`url(#${mBId})`}/>)}
        {P_OE_ORDER.map((oe,i)=>{
          const p=pts[i], oeP=oePct[oe]||0, isDay=oe===dayOe;
          const fillH=NR*2*Math.min(oeP,100)/100;
          const cpId=`cp${i}${uid}`;
          return(
            <g key={oe}>
              <clipPath id={cpId}><rect x={p.x-NR} y={p.y+NR-fillH} width={NR*2} height={fillH+2}/></clipPath>
              <circle cx={p.x} cy={p.y} r={NR} fill={P_OE_BG[oe]} stroke={isDay?P_OE_COLOR[oe]:"#ccc"} strokeWidth={isDay?2.5:1.2}/>
              <circle cx={p.x} cy={p.y} r={NR-1} fill={P_OE_COLOR[oe]} opacity={0.25} clipPath={`url(#${cpId})`}/>
              <text x={p.x} y={p.y-9} textAnchor="middle" fontSize={9} fontWeight="700" fill={P_OE_COLOR[oe]}>
                {P_OE_KAN[oe]}({_relLabel(oe,dayOe)})
              </text>
              <text x={p.x} y={p.y+10} textAnchor="middle" fontSize={13} fontWeight="800" fill={P_OE_COLOR[oe]}>{oeP}%</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function OeCompSection({myWx,ptWx,myName,ptName,oc}:{myWx:any;ptWx:any;myName:string;ptName:string;oc:any}){
  const { t } = useLang();
  if (!myWx && !ptWx && !oc) return null;
  const hj = oc?.hannan_josup;
  const totalScore = oc?.total_score;
  const myDayOe = STEM_OHAENG[oc?.my_day_gan || ""] || "목";
  const ptDayOe = STEM_OHAENG[oc?.partner_day_gan || ""] || "목";

  return (
    <OeCompWrap>
      <OeCompTitle>☯️ {t("report.ohaengCompat")} <span style={{fontSize:".6rem",opacity:.6,textTransform:"none",letterSpacing:0}}>{t("report.hapTransformApplied")}</span></OeCompTitle>
      {(myWx || ptWx) && (
        <div style={{display:"flex",gap:8,marginBottom:8}}>
          {myWx && <OePentagon wx={myWx} name={myName} uid="my" dayOe={myDayOe}/>}
          {ptWx && <OePentagon wx={ptWx} name={ptName} uid="pt" dayOe={ptDayOe}/>}
        </div>
      )}
      <div style={{display:"flex",gap:16,justifyContent:"center",marginBottom:12,fontSize:10,color:"#8C8677"}}>
        <span style={{color:"#4A7BA8"}}>{t("report.generation")}</span>
        <span style={{color:"#C04040"}}>{t("report.control")}</span>
      </div>
      {hj && (
        <>
          <div style={{fontSize:".7rem",fontWeight:700,color:"#8C8677",textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>🌡️ {t("report.hannanjoseupCompat")}</div>
          <HjScoreGrid>
            <HjCell>
              <HjLabel>{t("report.hannanLabel", { name: myName })}</HjLabel>
              <HjVal $good={hj.person1?.grade_hn!==hj.person2?.grade_hn}>{hj.person1?.label_hn||"—"}</HjVal>
            </HjCell>
            <HjCell>
              <HjLabel>{t("report.hannanLabel", { name: ptName })}</HjLabel>
              <HjVal $good={hj.person1?.grade_hn!==hj.person2?.grade_hn}>{hj.person2?.label_hn||"—"}</HjVal>
            </HjCell>
            <HjCell>
              <HjLabel>{t("report.joseupLabel", { name: myName })}</HjLabel>
              <HjVal $good={hj.person1?.grade_js!==hj.person2?.grade_js}>{hj.person1?.label_js||"—"}</HjVal>
            </HjCell>
            <HjCell>
              <HjLabel>{t("report.joseupLabel", { name: ptName })}</HjLabel>
              <HjVal $good={hj.person1?.grade_js!==hj.person2?.grade_js}>{hj.person2?.label_js||"—"}</HjVal>
            </HjCell>
          </HjScoreGrid>
          <div style={{display:"flex",gap:8,marginTop:10,justifyContent:"center"}}>
            <span style={{
              padding:"4px 12px",borderRadius:20,fontSize:".7rem",fontWeight:700,
              background: hj.person1?.grade_hn!==hj.person2?.grade_hn ? "#eaf5ee" : "#fdf5ee",
              color:       hj.person1?.grade_hn!==hj.person2?.grade_hn ? "#5E8A4E" : "#8C8677",
              border:      `1px solid ${hj.person1?.grade_hn!==hj.person2?.grade_hn ? "#b5dfc4" : "#EAE3D2"}`,
            }}>
              {hj.person1?.grade_hn!==hj.person2?.grade_hn ? t("report.hannanComplementary") : t("report.hannanSimilar")}
            </span>
            <span style={{
              padding:"4px 12px",borderRadius:20,fontSize:".7rem",fontWeight:700,
              background: hj.person1?.grade_js!==hj.person2?.grade_js ? "#eaf5ee" : "#fdf5ee",
              color:       hj.person1?.grade_js!==hj.person2?.grade_js ? "#5E8A4E" : "#8C8677",
              border:      `1px solid ${hj.person1?.grade_js!==hj.person2?.grade_js ? "#b5dfc4" : "#EAE3D2"}`,
            }}>
              {hj.person1?.grade_js!==hj.person2?.grade_js ? t("report.joseupComplementary") : t("report.joseupSimilar")}
            </span>
          </div>
        </>
      )}
      {oc?.interpretation && (
        <div style={{marginTop:12,fontSize:".78rem",color:"#8C8677",lineHeight:1.8,borderTop:"1px solid #2B2A26",paddingTop:10}}>
          {oc.interpretation}
        </div>
      )}
    </OeCompWrap>
  );
}

/* ── 궁합 서술형 내러티브 생성 ── */
function makeCompatNarrative(
  myName: string, ptName: string,
  perScore: number, intScore: number, warnScore: number,
  sepYears: any[], oc: any
): string {
  const oe1 = oc?.my_oe    || "";
  const oe2 = oc?.pt_oe    || "";
  const hapDetails: string[]  = oc?.hap_details  || [];
  const chungDetails: string[]= oc?.chung_details || [];

  // 오행별 성격 특성
  const OE_PERSONALITY: Record<string,string> = {
    목: "인자하고 따뜻한 성품으로, 새로운 시작과 성장에 에너지를 쏟는 이상주의자입니다. 직선적이고 솔직한 표현을 좋아하며, 한 번 맺은 인연을 소중히 여기는 의리파입니다. 시작을 잘하지만 마무리가 다소 약한 편이며, 자신의 신념이 강해 고집으로 비춰질 수 있습니다. 감정이 상했을 때는 말이 없어지거나 속으로 삭히는 경향이 있어 상대방이 눈치채기 어려울 수 있습니다.",
    화: "열정적이고 활력이 넘치며 감정 표현이 풍부하고 주변을 밝히는 존재감이 있습니다. 즉흥적이고 직관적인 결정을 잘 하며, 사람과의 관계에서 에너지를 얻는 외향적인 타입입니다. 감정 기복이 있어 흥분했을 때 충동적인 말이 나올 수 있고, 관심이 분산되면 집중력이 흔들릴 수 있습니다. 연애에서 열정적인 초반 감정을 매우 중요시하는 타입입니다.",
    토: "안정과 신뢰를 중시하는 성품으로, 말보다 행동으로 보여주는 묵직한 스타일입니다. 책임감이 강하고 현실적인 판단이 뛰어나며, 한 번 결심하면 흔들리지 않는 지속력이 있습니다. 변화를 싫어하고 고집이 있어 새로운 시도에 보수적일 수 있으며, 감정 표현이 서툴러 속마음을 전달하는 데 시간이 걸립니다.",
    금: "원칙과 의리를 중시하는 성품으로, 냉철하고 논리적인 판단력이 뛰어납니다. 완벽주의적 성향이 있어 자신에게도 타인에게도 높은 기준을 적용하며, 한 번 신뢰한 사람에게는 깊이 헌신합니다. 겉으로는 차갑게 느껴질 수 있고 감정보다 이성이 앞서는 면이 있어 연인이 섭섭함을 느낄 수 있습니다. 처음 벽이 높지만 그 안에 있으면 누구보다 든든한 사람입니다.",
    수: "지혜롭고 사색적인 성품으로, 깊은 감수성과 풍부한 내면 세계를 가진 타입입니다. 상황에 유연하게 적응하는 능력이 뛰어나고 창의적이며, 관계에서 깊은 정서적 교감을 중시합니다. 결정이 느리고 우유부단해 보일 수 있으며, 상처를 쉽게 받는 만큼 방어적으로 변하기도 합니다. 감정을 직접 드러내기보다 간접적으로 표현하는 경향이 있습니다.",
  };

  // 오행별 연애 방식
  const OE_LOVE_STYLE: Record<string,string> = {
    목: "정신적 교감과 공감을 가장 중요하게 여깁니다. 억지스럽거나 부담스러운 상황을 싫어하고, 서로의 공간을 지키면서 자연스럽게 깊어지는 관계를 선호합니다. 기념일과 약속을 중요시하며, 감정이 상했을 때는 직접 말하기보다 거리를 두는 방식으로 신호를 보냅니다.",
    화: "설렘과 열정을 최우선으로 여깁니다. 적극적으로 마음을 표현하고 이벤트나 즉흥 데이트를 즐기며, 상대방이 자신의 감정에 빠르게 반응해주기를 원합니다. 관계가 루틴화되면 권태감을 빠르게 느끼는 편이므로 지속적인 새로운 자극이 필요합니다.",
    토: "안정감과 신뢰감을 가장 중요하게 여깁니다. 일관성 있게 곁에 있어주는 사람에게 깊은 안도감을 느끼며, 과도한 감정 기복이나 불확실한 상황을 어려워합니다. 한 번 사랑을 시작하면 쉽게 변하지 않으며 오랜 시간을 함께할 준비가 된 사람입니다.",
    금: "명확함과 진심을 중요하게 여깁니다. 감언이설보다 행동으로 사랑을 증명해주는 사람을 신뢰하며, 선을 지키고 약속을 반드시 지키는 것을 기본 예의로 생각합니다. 한 번 상처받으면 쉽게 신뢰를 회복하지 않으므로 처음부터 진실한 태도가 필요합니다.",
    수: "감정적 교감과 이해를 최우선으로 여깁니다. 말하지 않아도 알아주기를 바라는 면이 있고, 깊은 대화와 공감 능력이 있는 사람에게 빠르게 마음을 엽니다. 혼자만의 시간이 필요한 타입으로 지나친 요구나 간섭을 부담스러워하며, 감정이 차오르면 말보다 침묵으로 표현합니다.",
  };

  // 상생/상극 판별
  const SANGSEONG = new Set(["목화","화토","토금","금수","수목"]);
  const SANGGEUK  = new Set(["목토","화금","토수","금목","수화"]);
  const pairKey   = oe1 && oe2 ? oe1+oe2 : "";
  const isSangseong = SANGSEONG.has(pairKey) || SANGSEONG.has(oe2+oe1);
  const isSanggeuk  = SANGGEUK.has(pairKey)  || SANGGEUK.has(oe2+oe1);
  const isSame      = oe1 !== "" && oe1 === oe2;

  // 오행 조합별 갈등 패턴 (10방향)
  const CONFLICT_MAP: Record<string,string> = {
    목화: `${myName}님의 木 기운이 ${ptName}님의 火 기운을 키우는 상생 구조입니다. 갈등보다는 보완이 강하지만 ${myName}님이 지속적으로 에너지를 쏟다 소진될 수 있습니다. 두 분이 가장 많이 싸우는 패턴은 속도 차이입니다. ${ptName}님은 즉각적인 반응을 원하지만 ${myName}님은 충분히 생각하고 대답하는 스타일이라, "왜 이렇게 느려" vs "왜 이렇게 급해"의 충돌이 반복될 수 있습니다.`,
    화목: `${ptName}님의 木 기운이 ${myName}님의 火 기운을 키우는 상생 구조입니다. ${myName}님이 에너지를 주도하고 ${ptName}님이 뒷받침하는 흐름으로, 두 분이 싸울 때는 ${myName}님의 즉흥적 결정과 ${ptName}님의 신중한 검토 방식 차이에서 시작됩니다. "${ptName}님이 왜 항상 제동을 거는가"와 "${myName}님이 왜 항상 확인 없이 나아가는가"의 패턴이 핵심입니다.`,
    화토: `${myName}님의 火 기운이 ${ptName}님의 土 기운을 키우는 상생 구조입니다. 두 분의 갈등은 주로 변화 욕구와 현상 유지 성향의 충돌에서 옵니다. ${myName}님이 새로운 시도를 제안할 때 ${ptName}님이 "그게 됩니까?"로 반응하면 ${myName}님이 답답함을 느끼고 감정이 폭발하는 패턴이 반복될 수 있습니다.`,
    토화: `${ptName}님의 火 기운이 ${myName}님의 土 기운을 자극하는 구조입니다. ${myName}님은 안정을 원하는데 ${ptName}님의 에너지가 끊임없이 변화를 불러일으키면 피로감을 느낍니다. "또 뭔가 바꾸려 해" vs "왜 그렇게 고지식해"의 패턴이 반복될 수 있습니다.`,
    토금: `${myName}님의 土 기운이 ${ptName}님의 金 기운을 성장시키는 상생 구조입니다. 두 분 모두 안정과 원칙을 중시해 갈등 빈도는 낮습니다. 다만 싸울 때는 서로 자기 방식만이 맞다고 우기는 '팽팽한 침묵 전쟁'이 됩니다. 어느 쪽도 먼저 사과하지 않으려는 고집이 만나면 냉전이 길어질 수 있습니다.`,
    금토: `${ptName}님의 土 기운이 ${myName}님의 金 기운을 지지하는 구조입니다. ${ptName}님이 묵묵히 뒷받침하면 ${myName}님의 완벽주의적 성향이 더 강해질 수 있습니다. 두 분의 갈등은 주로 ${myName}님의 높은 기준을 ${ptName}님이 부담스러워하는 데서 시작됩니다.`,
    금수: `${myName}님의 金 기운이 ${ptName}님의 水 기운을 풍부하게 만드는 상생 구조입니다. ${myName}님의 논리적 결단에 ${ptName}님이 안도감을 느끼고, ${myName}님은 ${ptName}님의 섬세한 감수성에 위로받는 상호 보완 구조입니다. 갈등은 ${myName}님의 직설적 표현이 ${ptName}님의 감수성을 건드릴 때 발생합니다. "그냥 사실을 말한 것뿐"이라는 ${myName}님과 "그 말이 상처였어"라는 ${ptName}님의 인식 차이가 핵심입니다.`,
    수금: `${ptName}님의 金 기운이 ${myName}님의 水 기운을 지지하는 구조입니다. ${ptName}님의 직설적이고 결단력 있는 말이 감수성 풍부한 ${myName}님에게 상처로 남을 수 있습니다. ${myName}님이 침묵하거나 멀어지는 행동이 ${ptName}님에게는 이유를 알 수 없는 벽처럼 느껴지는 패턴이 생깁니다.`,
    수목: `${myName}님의 水 기운이 ${ptName}님의 木 기운을 키우는 상생 구조입니다. ${ptName}님이 클수록 ${myName}님의 지원이 더 많이 필요해지는 구조로, 장기적으로 ${myName}님이 소진될 위험이 있습니다. 갈등은 주로 역할 불균형에서 옵니다. ${myName}님이 "내가 다 해줬는데"라고 느끼는 순간 그 감정이 폭발하는 패턴이 생깁니다.`,
    목수: `${ptName}님의 水 기운이 ${myName}님의 木 기운을 키우는 구조입니다. ${myName}님이 성장할수록 ${ptName}님의 에너지가 더 많이 소모됩니다. 두 분이 싸우는 주된 이유는 ${myName}님이 원하는 속도로 나아갈 때 ${ptName}님이 "잠깐, 우리 방향 맞아?"라고 제동을 거는 순간에서 비롯됩니다.`,
    목토: `${myName}님의 木 기운이 ${ptName}님의 土 기운을 흔드는 상극 구조입니다. ${myName}님의 변화 지향적 성격이 ${ptName}님의 안정 추구 성향을 지속적으로 흔들고, ${ptName}님의 보수적 반응이 ${myName}님의 답답함을 유발하는 구조입니다. "넌 너무 고지식해" vs "넌 너무 들떠 있어"의 평가 전쟁으로 번질 수 있습니다.`,
    토목: `${ptName}님의 木 기운이 ${myName}님의 土 기운을 흔드는 상극 구조입니다. ${ptName}님의 이상주의적이고 빠른 변화 추구가 안정을 원하는 ${myName}님을 불안하게 만듭니다. ${myName}님의 "현실적으로 생각해" vs ${ptName}님의 "왜 항상 막아"의 충돌이 반복될 수 있습니다.`,
    화금: `${myName}님의 火 기운과 ${ptName}님의 金 기운이 충돌하는 상극 구조입니다. ${myName}님은 감정적으로 즉각 반응하지만 ${ptName}님은 논리적으로 분석합니다. 감정적인 ${myName}님의 말에 논리로 받아치는 ${ptName}님의 반응이 ${myName}님에게 벽처럼 느껴지고, ${myName}님의 감정 폭발이 ${ptName}님에게 비논리적으로 보이는 악순환이 생깁니다.`,
    금화: `${ptName}님의 火 기운과 ${myName}님의 金 기운이 부딪히는 상극 구조입니다. ${ptName}님이 감정적으로 폭발할 때 ${myName}님이 냉정하게 대응하면 ${ptName}님은 "공감 없는 사람"이라 느끼고, ${myName}님은 ${ptName}님을 "감정 조절이 안 되는 사람"이라 느끼는 패턴이 반복됩니다.`,
    토수: `${myName}님의 土 기운과 ${ptName}님의 水 기운이 마찰하는 상극 구조입니다. '확실함 vs 유동성'의 핵심 갈등입니다. ${myName}님은 명확하고 계획적인 것을 좋아하지만 ${ptName}님은 상황에 따라 유연하게 흐르는 것을 선호합니다. "왜 그렇게 왔다 갔다 해" vs "왜 그렇게 딱딱하게 굴어"의 충돌이 반복됩니다.`,
    수토: `${ptName}님의 土 기운이 ${myName}님의 Water 흐름을 막는 상극 구조입니다. ${myName}님의 유연하고 흐르는 방식이 ${ptName}님의 고지식한 고집에 막히는 느낌을 받습니다. ${myName}님은 답답함을 침묵으로, ${ptName}님은 변화 없음으로 응수하는 냉전 패턴이 생깁니다.`,
    금목: `${myName}님의 金 기운이 ${ptName}님의 木 기운과 부딪히는 상극 구조입니다. ${ptName}님의 창의적이고 이상주의적 아이디어가 ${myName}님의 현실적이고 완벽주의적 시각과 충돌합니다. "그게 현실적이야?" vs "왜 항상 부정적으로 봐"의 패턴이 반복됩니다.`,
    목금: `${ptName}님의 金 기운이 ${myName}님의 木 성장을 자르는 상극 구조입니다. ${myName}님이 새롭게 시도하려 할 때 ${ptName}님의 비판적 시각이 날카롭게 들어오면, ${myName}님은 점점 자신의 생각을 표현하기 두려워지는 패턴이 생깁니다.`,
    수화: `${myName}님의 水 기운과 ${ptName}님의 火 기운이 충돌하는 상극 구조입니다. ${ptName}님의 폭발적인 감정 에너지가 ${myName}님에게 압도적으로 느껴질 수 있고, ${myName}님의 조용한 대처가 ${ptName}님에게 "관심 없는 것"으로 오해받는 악순환이 생깁니다.`,
    화수: `${ptName}님의 Water 기운이 ${myName}님의 火 에너지를 잠재우는 상극 구조입니다. ${myName}님이 열정적으로 뭔가를 밀어붙일 때 ${ptName}님의 신중하고 느린 반응이 ${myName}님의 의욕을 꺾는 패턴이 생깁니다. "왜 항상 찬물 끼얹어" vs "왜 항상 준비 없이 달려들어"의 충돌이 반복됩니다.`,
  };

  const getConflictDesc = (o1: string, o2: string): string => {
    const hit = CONFLICT_MAP[o1+o2];
    if (hit) return hit;
    if (isSame) return `두 분은 같은 ${OE_KOR[o1]||o1}(${o1}) 오행을 가진 동기(同氣) 조합입니다. 서로를 누구보다 잘 이해하지만, 두 분의 약점이 동시에 터질 때는 그 어떤 조합보다 강한 갈등이 생깁니다. "나도 지금 힘들어"가 동시에 폭발하는 상황에 대비해 각자의 회복 공간을 서로 보장해주는 것이 중요합니다.`;
    return `두 분의 에너지는 서로 독립적으로 움직이는 중립 조합입니다. 갈등의 원인은 오행 충돌보다 소통 습관 차이에서 오는 경우가 많으니, 상대방이 왜 그렇게 반응하는지 이해하려는 노력이 이 관계의 가장 큰 자산이 됩니다.`;
  };

  const parts: string[] = [];

  // ━━━ 1. 두 사람의 에너지 분석 ━━━
  parts.push(`━━━ ${myName}님 · ${ptName}님의 사주 에너지 분석 ━━━`);
  let sec1 = "";
  if (oe1 && OE_PERSONALITY[oe1]) {
    sec1 += `■ ${myName}님 — ${OE_KOR[oe1]||oe1}(${oe1}) 기운\n${OE_PERSONALITY[oe1]}\n\n연애 방식: ${OE_LOVE_STYLE[oe1]||""}`;
  }
  if (oe2 && OE_PERSONALITY[oe2]) {
    if (sec1) sec1 += "\n\n";
    sec1 += `■ ${ptName}님 — ${OE_KOR[oe2]||oe2}(${oe2}) 기운\n${OE_PERSONALITY[oe2]}\n\n연애 방식: ${OE_LOVE_STYLE[oe2]||""}`;
  }
  if (!sec1) sec1 = `두 분의 사주 일간 정보를 기반으로 에너지 분석을 진행합니다.`;
  parts.push(sec1);

  // ━━━ 2. 성격 궁합 평가 ━━━
  parts.push(`━━━ 성격 궁합 평가 (${perScore > 0 ? perScore+"점" : "분석 중"}) ━━━`);
  let sec2 = "";
  if (hapDetails.length > 0) {
    sec2 += `두 분의 일간에 자연스러운 끌림의 합(合) 기운(${hapDetails.join(', ')})이 작용하고 있습니다. 처음 만났을 때 이유 없이 편안하거나 "이 사람이다" 싶은 직관이 왔다면, 그것이 바로 이 합의 작용입니다. `;
  }
  if (chungDetails.length > 0) {
    sec2 += `동시에 충(沖) 기운(${chungDetails.join(', ')})도 내재해 있어, 두 분이 서로를 강하게 끌어당기는 동시에 부딪히는 역동적인 관계입니다. 충 관계는 서로의 성장을 자극하는 긴장감을 만들지만, 스트레스 시기에는 폭발적인 갈등으로 이어질 수 있습니다. `;
  }
  if (isSangseong) {
    sec2 += `두 분의 오행은 상생(相生) 관계로 연결되어 있어, 한 분의 에너지가 자연스럽게 다른 분을 보완하고 채워주는 흐름입니다. `;
  } else if (isSanggeuk) {
    sec2 += `두 분의 오행은 상극(相克) 관계로 맞닿아 있어, 근본적인 에너지 방향이 다릅니다. 이 차이를 어떻게 다루느냐에 따라 관계의 깊이가 결정됩니다. `;
  }
  if (perScore >= 75) {
    sec2 += `성격 궁합 ${perScore}점은 높은 수준으로, 두 분은 함께 있을수록 자연스럽게 맞아가는 조합입니다. 가치관의 핵심 부분이 일치하고 상대방의 생각 흐름이 어느 정도 예측 가능해 대화에서 안도감을 느끼는 구조입니다. 이 궁합의 최대 복은 "억지로 맞추지 않아도 되는" 편안함이며, 이것이 오래 함께할수록 더 깊어지는 관계의 기반이 됩니다.`;
  } else if (perScore >= 60) {
    sec2 += `성격 궁합 ${perScore}점은 평균 이상으로, 큰 충돌 없이 관계를 유지할 수 있는 안정적인 조합입니다. 다만 완전히 맞는 것은 아니기에 상대방의 다른 점에 의아함을 느끼는 순간들이 있을 것입니다. 그 의아함을 "틀렸다"가 아닌 "다르다"로 받아들이는 연습이 이 관계의 핵심입니다.`;
  } else if (perScore >= 45) {
    sec2 += `성격 궁합 ${perScore}점은 평균 수준으로, 서로를 이해하려면 의식적인 노력이 지속적으로 필요합니다. 좋아한다는 감정만으로 버티기에는 결이 다른 부분이 있어, 표현 방식과 갈등 해소 방법에 대한 솔직한 대화가 반드시 필요합니다.`;
  } else if (perScore > 0) {
    sec2 += `성격 궁합 ${perScore}점은 상당한 차이가 있는 수준입니다. 이 관계를 유지하려면 단순한 노력을 넘어, 상대방의 방식을 진정으로 존중하는 마음가짐이 필요합니다. 두 분의 차이가 오히려 서로를 성장시키는 자극이 될 수 있다는 관점으로 접근하면 충분히 깊은 관계를 만들 수 있습니다.`;
  }
  parts.push(sec2);

  // ━━━ 3. 갈등 패턴 ━━━
  parts.push(`━━━ 두 분이 싸우는 이유와 갈등 패턴 ━━━`);
  let sec3 = "";
  if (oe1 && oe2) {
    sec3 += getConflictDesc(oe1, oe2) + "\n\n";
  }
  if (warnScore < 45) {
    sec3 += `주의점 분석에서 위험 신호가 감지됩니다. 두 분은 감정이 격해졌을 때 이성적 대화가 거의 불가능해지는 패턴이 있습니다. 중요한 결정이나 갈등 상황은 반드시 감정이 충분히 가라앉은 후 대화하는 것을 원칙으로 삼으세요. "지금 당장 해결해야 한다"는 압박이 오히려 관계를 더 망가뜨리는 경우가 많습니다.\n\n가장 위험한 패턴은 대화 중 과거의 상처를 꺼내 현재 갈등에 얹는 것입니다. "지난번에도 그랬잖아"라는 말은 상대방을 방어 모드로 만들어 문제 해결이 아닌 공격과 방어의 싸움으로 전환시킵니다. 지금 이 순간의 문제에만 집중하는 대화 규칙을 만드는 것이 중요합니다.`;
  } else if (warnScore < 65) {
    sec3 += `두 분이 가장 많이 싸우는 패턴은 '침묵 vs 폭발'의 구조입니다. 한 분이 감정을 쌓아두다 한 번에 터뜨리면, 다른 분이 방어적으로 대응하면서 갈등이 증폭됩니다. 작은 불편함도 즉시 표현하는 습관을 들이면 이 패턴을 상당 부분 예방할 수 있습니다.\n\n두 분에게 특히 추천하는 방법은 감정이 쌓이기 전에 "요즘 내가 섭섭하게 한 것 있어?"를 주기적으로 묻는 것입니다. 이 한 마디가 쌓인 감정을 흘려보내는 가장 효과적인 밸브 역할을 합니다.`;
  } else {
    sec3 += `두 분은 갈등을 비교적 유연하게 해결하는 편입니다. 갈등이 가장 쉽게 발생하는 순간은 서로 지쳐 있거나 외부 스트레스가 쌓였을 때입니다. 상대방이 "이유 없이 예민하다"고 느껴진다면, 그것은 관계의 문제가 아닌 개인적인 피로 신호일 수 있습니다.\n\n두 분의 관계에서 주의해야 할 것은 "괜찮아"라는 말의 남발입니다. 괜찮지 않을 때 괜찮다고 하는 습관이 쌓이면 나중에 예상치 못한 폭발로 이어집니다. 서로에게 솔직할 수 있는 안전한 표현 공간을 만드는 것이 이 관계에서 가장 중요합니다.`;
  }
  parts.push(sec3);

  // ━━━ 4. 속궁합 분석 ━━━
  parts.push(`━━━ 속궁합 분석 (${intScore > 0 ? intScore+"점" : "분석 중"}) ━━━`);
  let sec4 = "";
  if (intScore >= 72) {
    sec4 = `속궁합 ${intScore}점은 매우 높은 수준입니다. 두 분의 에너지는 신체적·감정적 차원에서 자연스럽게 조화를 이루는 구조로, 함께 있을수록 편안함과 설렘이 공존하는 드문 조합입니다. 신체 리듬이 비슷하고 감정적 교감 속도가 맞아 함께 있을 때 에너지가 충전되는 느낌을 받습니다.\n\n두 분은 말하지 않아도 서로의 컨디션을 감지하는 민감도가 시간이 지날수록 발달합니다. 초반의 두근거림이 안정감으로 전환되더라도 관계의 퇴보가 아닌 성숙으로 받아들이시면 됩니다. 이 조합의 진짜 깊이는 함께하는 시간이 쌓일수록 드러납니다.`;
  } else if (intScore >= 58) {
    sec4 = `속궁합 ${intScore}점은 양호한 수준입니다. 서로의 에너지 방식이 완전히 일치하지는 않지만, 배려와 이해를 통해 충분히 만족스러운 관계를 만들 수 있는 조합입니다.\n\n두 분이 주의해야 할 점은 한쪽이 다른 쪽에 맞추기 위해 일방적으로 노력하는 구조가 형성되지 않도록 하는 것입니다. 서로의 속도와 방식에 대해 솔직하게 대화하는 관계일수록 이 부분이 자연스럽게 해결됩니다. 서로의 페이스를 존중하면서도 공통된 리듬을 찾아가는 과정 자체가 두 분의 관계를 더 깊게 만드는 힘입니다.`;
  } else if (intScore > 0) {
    sec4 = `속궁합 ${intScore}점은 에너지 불균형이 있는 수준입니다. 두 분의 신체적·감정적 리듬이 상당히 달라, 한쪽이 더 많이 노력하고 다른 쪽이 받기만 하는 불균형한 구조가 생길 수 있습니다.\n\n이 점수가 낮다고 해서 관계가 불가능한 것은 절대 아닙니다. 다만 서로의 차이를 명확히 인지하고, "상대가 나와 다른 방식으로 표현할 수 있다"는 것을 전제로 대화해야 불필요한 오해를 줄일 수 있습니다. 서로에 대한 기대치를 솔직하게 공유하는 것이 이 관계에서 가장 중요한 열쇠입니다.`;
  } else {
    sec4 = `속궁합은 두 분이 함께하는 시간이 쌓일수록 서로의 에너지 패턴이 맞춰지는 방향으로 발전합니다. 처음부터 모든 게 완벽하게 맞는 관계는 거의 없습니다. 서로의 다름을 탐구하는 과정 자체가 이 관계의 깊이를 만드는 요소입니다.`;
  }
  parts.push(sec4);

  // ━━━ 5. 이별수 경고 ━━━
  if (sepYears.length > 0) {
    parts.push(`━━━ 이별수 위험 시기 집중 분석 ━━━`);
    let sec5 = `두 분의 사주 흐름에서 관계에 위기가 올 수 있는 시기를 감지했습니다. 이 시기에 이별이 반드시 발생한다는 의미가 아니라, 두 분의 사주 에너지 흐름이 충돌하는 시기라는 뜻입니다. 미리 알고 준비하면 충분히 극복할 수 있습니다.\n\n`;
    const topSep = sepYears.slice(0, 3);
    topSep.forEach((sy: any, i: number) => {
      const rank = i === 0 ? "▶ 최고 위험 시기" : i === 1 ? "▶ 주의 시기" : "▶ 경계 시기";
      sec5 += `${rank}: ${sy.year}년`;
      if (sy.months) sec5 += ` (특히 ${sy.months} 집중 주의)`;
      sec5 += `\n`;
    });
    sec5 += `\n이 시기를 헤쳐나가기 위한 3가지 원칙:\n`;
    sec5 += `① 감정이 격해질 때 그 자리에서 결론 내리지 말 것 — 최소 하루, 가능하면 3일의 여유를 두세요.\n`;
    sec5 += `② 외부 스트레스(직장·재정·가족 문제)를 관계 안으로 끌어들이지 말 것 — 지쳐있을 때 관계 평가는 왜곡됩니다.\n`;
    sec5 += `③ "이 관계가 맞는가"를 가장 힘든 시기에 판단하지 말 것 — 위기 때의 감정은 관계의 가치를 제대로 평가할 수 없습니다. 위기는 끝이 아닌 전환점입니다.`;
    parts.push(sec5);
  }

  // ━━━ 6. 이 관계를 지키는 법 ━━━
  parts.push(`━━━ 이 관계를 더 깊고 오래 지속하는 방법 ━━━`);
  let sec6 = "";
  if (isSangseong) {
    sec6 += `두 분의 오행은 상생(相生) 관계로, 서로에게 자연스럽게 에너지를 공급하는 구조입니다. 이 관계에서 가장 중요한 것은 상생의 균형입니다. 한쪽이 계속 주기만 하거나 받기만 하는 상황이 지속되면 결국 지치게 됩니다. 두 분이 번갈아 서로를 지지하는 상호적인 흐름을 의식적으로 만들어야 이 상생의 힘이 장기적으로 유지됩니다.\n\n`;
  } else if (isSanggeuk) {
    sec6 += `두 분의 오행은 상극(相克) 관계로, 서로가 서로를 자극하고 변화시키는 역동적인 구조입니다. 상극 관계의 연인들은 "함께여서 더 강해지거나, 함께여서 서로 상처받거나"의 두 방향 중 하나로 흘러갑니다. 어느 방향으로 갈지는 두 분이 서로의 차이를 어떻게 대하느냐에 달려 있습니다. 차이를 "고쳐야 할 결함"이 아닌 "내가 가지지 못한 강점"으로 보는 시각의 전환이 핵심입니다.\n\n`;
  } else if (isSame) {
    sec6 += `두 분은 같은 오행을 가진 동기(同氣) 조합입니다. 서로를 거울처럼 이해할 수 있는 장점이 있지만, 같은 약점이 동시에 터질 때 두 배로 힘들어질 수 있습니다. 서로의 강점으로 약점을 커버할 수 없는 구조이므로, 두 분 각자가 개인적으로 성장하는 것이 관계 유지의 핵심입니다.\n\n`;
  } else {
    sec6 += `두 분의 오행 에너지는 서로 독립적인 관계로, 잘하면 자유롭고 독립적인 좋은 관계를, 방치하면 서로 무관심해지는 방향으로 흘러갈 수 있습니다. 적극적인 관심과 표현이 이 관계를 지속시키는 핵심 연료입니다.\n\n`;
  }
  const avgScore = perScore > 0 && intScore > 0 ? (perScore + intScore) / 2 : perScore || intScore;
  if (avgScore >= 68) {
    sec6 += `두 분의 궁합 에너지가 좋은 만큼, 가장 큰 적은 권태감입니다. 서로에게 익숙해진 나머지 노력을 멈추는 순간 관계는 조용히 식어갑니다. 정기적으로 처음 만났을 때를 떠올리게 하는 새로운 경험을 함께 만드는 것이 이 관계의 가장 좋은 보약입니다.\n\n"당연히 해줘야 하는 것"이라고 생각하는 것들을 소리 내어 감사하는 습관이 이 관계를 아주 오래 지속시킬 수 있습니다. 좋은 관계를 유지하는 비결은 거창한 이벤트가 아니라 일상 속 작은 감사의 말입니다.`;
  } else if (avgScore >= 52) {
    sec6 += `두 분의 관계는 노력한 만큼 단단해지는 구조입니다. 가장 효과적인 방법은 월 1회 이상 '관계 점검 대화' 시간을 갖는 것입니다. "요즘 내가 섭섭하게 한 것 있어?", "네가 원하는 게 뭔지 잘 모르겠어"처럼 솔직한 대화를 이어가면 서로의 이해도가 빠르게 높아집니다.\n\n특히 두 분은 각자의 개인 공간을 지켜주는 것이 중요합니다. 상대방이 혼자 있고 싶어할 때 "나를 싫어하는 건가?"로 받아들이지 않도록 서로의 충전 방식을 미리 이야기해두세요.`;
  } else if (avgScore > 0) {
    sec6 += `두 분의 관계는 의식적인 노력 없이는 유지가 어려운 조합입니다. 하지만 어려운 관계를 함께 버텨낸 두 분은 어떤 역경도 이겨낼 수 있는 진짜 파트너십을 갖게 됩니다.\n\n가장 중요한 것은 상대방의 방식을 "고치려" 하지 않는 것입니다. 서로 다른 것이 맞고 틀림의 문제가 아님을 인정하는 순간, 이 관계는 전혀 다른 차원으로 성숙해집니다. 공동의 목표(여행, 취미, 프로젝트 등)를 통해 공통 언어를 만들어가는 것이 큰 도움이 됩니다.`;
  }
  parts.push(sec6);

  return parts.join('\n\n');
}

/* ── 궁합 리포트 ── */
function CompatReport({data}:{data:any}){
  const { t } = useLang();
  const rc      = data.report_config || {};
  const pt      = data.partner_info  || {};
  const isDeep  = rc.compatibility_type === "deep" || data.service_type === "심층궁합";
  const analysis = rc.compat_analysis || {};
  const sepYears: any[] = rc.separation_years || [];
  const oc      = rc.ohaeng_compat;

  const myWx = data.saju?.wuxing_analysis;
  const ptWx = oc?.partner_wuxing;

  // 점수 계산
  const perScore  = (analysis["성격궁합"]?.score  ?? 0) as number;
  const intScore  = (analysis["속궁합"]?.score    ?? 0) as number;
  const warnScore = (analysis["주의점"]?.score    ?? 100) as number;
  const rawScores = [perScore, intScore].filter(s => s > 0);
  const _rawTotal = rawScores.length > 0
    ? Math.round(rawScores.reduce((a,b)=>a+b,0) / rawScores.length)
    : (oc?.total_score ?? null) as number|null;
  const totalScore = _rawTotal !== null ? Math.max(40, _rawTotal) : null;

  const narrative = makeCompatNarrative(
    data.name, pt.name||"상대방",
    perScore, intScore, warnScore,
    sepYears, {
      ...oc,
      my_oe: oc?.my_oe || "",
      pt_oe: oc?.pt_oe || "",
    }
  );

  return(
    <>
      {/* 궁합 히어로 */}
      <CompatHero>
        <CompatNames>
          <span>{data.name}</span>
          <CompatArrow>♥</CompatArrow>
          <span>{pt.name || t("report.partnerDefault")}</span>
        </CompatNames>
        <div style={{fontSize:".78rem",color:"#8C8677",marginBottom:8}}>
          {data.birth} ({data.gender==="female"?t("report.femaleShort"):t("report.maleShort")})
          {pt.birth_year && ` · ${pt.birth_year}.${String(pt.birth_month||0).padStart(2,"0")}.${String(pt.birth_day||0).padStart(2,"0")} (${pt.gender==="female"?t("report.femaleShort"):t("report.maleShort")})`}
        </div>
        {totalScore !== null && (
          <TotalScoreWrap>
            <TotalScoreNum $score={totalScore}>{totalScore}</TotalScoreNum>
            <TotalScoreLabel>{t("report.totalCompatScore")}</TotalScoreLabel>
          </TotalScoreWrap>
        )}
        {/* 항목별 점수 배지 */}
        {(perScore > 0 || intScore > 0) && (
          <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:8,flexWrap:"wrap"}}>
            {perScore > 0 && (
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:".65rem",color:"#8C8677",marginBottom:2}}>{t("report.personalityCompat")}</div>
                <ScoreBadge $score={perScore}>{t("report.scorePts", { n: perScore })}</ScoreBadge>
              </div>
            )}
            {intScore > 0 && (
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:".65rem",color:"#8C8677",marginBottom:2}}>{t("report.intimacyCompat")}</div>
                <ScoreBadge $score={intScore}>{t("report.scorePts", { n: intScore })}</ScoreBadge>
              </div>
            )}
          </div>
        )}
      </CompatHero>

      {/* 오행 비교 — 두 사람 모두 표시 */}
      <OeCompSection
        myWx={myWx} ptWx={ptWx}
        myName={data.name} ptName={pt.name||t("report.partnerDefault")}
        oc={oc}
      />


      {/* 심층궁합 전용: 결혼 시기 + 자녀운 */}
      {isDeep && (rc.marriage_timing || rc.children_timing || rc.children_gender) && (
        <div style={{margin:"8px 16px 0",display:"flex",flexDirection:"column",gap:8}}>
          {rc.marriage_timing && (
            <ThemeBlock>
              <ThemeHeader2>
                <span style={{fontSize:"1.1rem"}}></span>
                <span style={{flex:1,fontSize:".85rem",fontWeight:700,color:"#2B2A26"}}>{t("report.bestMarriageTiming")}</span>
              </ThemeHeader2>
              <CommentBox>{rc.marriage_timing}</CommentBox>
            </ThemeBlock>
          )}
          {(rc.children_timing || rc.children_gender || rc.children_score) && (
            <ThemeBlock>
              <ThemeHeader2>
                <span style={{fontSize:"1.1rem"}}>👶</span>
                <span style={{flex:1,fontSize:".85rem",fontWeight:700,color:"#2B2A26"}}>{t("report.childrenFortune")}</span>
                {rc.children_score && <ScoreBadge $score={rc.children_score}>{t("report.scorePts", { n: rc.children_score })}</ScoreBadge>}
              </ThemeHeader2>
              <CommentBox>
                {[
                  rc.children_timing && t("report.firstChildTiming", { timing: rc.children_timing }),
                  rc.children_gender && t("report.childGenderPrediction", { gender: rc.children_gender }),
                ].filter(Boolean).join('\n')}
              </CommentBox>
            </ThemeBlock>
          )}
        </div>
      )}
    </>
  );
}

/* ── 메인 ── */
export default function ReportPage(){
  const { t, lang } = useLang();
  const {id} = useParams<{id:string}>();
  const [data,    setData]    = useState<any>(null);
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedWolunKey, setSelectedWolunKey] = useState<string|null>(null);
  const [dateUnlocked, setDateUnlocked] = useState(false);

  const [generating, setGenerating] = useState(false);

  /* 휴대폰으로 날짜 열람을 결제하고 돌아왔을 때.
     휴대폰 결제는 콜백 없이 주소 이동으로 돌아오기 때문에, 이 처리가 없으면
     결제만 되고 날짜는 계속 잠긴 채로 남는다(돈만 나간다). */
  useEffect(() => {
    // 결제 회사가 이미 물음표가 있는 주소에 또 물음표로 붙이는 경우가 있어 정리해서 읽는다
    const raw = (window.location.search || "").replace(/\?/g, (_m, i) => (i === 0 ? "?" : "&"));
    const q = new URLSearchParams(raw);
    const impUid = q.get("imp_uid") || "";
    const success = q.get("imp_success") ?? q.get("success");
    if (!impUid && success === null) return;

    let saved: any = null;
    try { saved = JSON.parse(sessionStorage.getItem("reportDatePending") || "null"); } catch { /* noop */ }
    try { sessionStorage.removeItem("reportDatePending"); } catch { /* noop */ }
    window.history.replaceState({}, "", window.location.pathname);
    if (success === "false" || success === "0") return;
    if (!saved?.consultationId) return;

    (async () => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await axios.post(`${API}/api/v1/client/payment-verify`, {
            consultation_id: saved.consultationId,
            imp_uid: impUid, merchant_uid: q.get("merchant_uid") || saved.merchantUid,
          }, { timeout: 120000 });
          setDateUnlocked(true);
          return;
        } catch (e: any) {
          if (attempt < 3) { await new Promise(r => setTimeout(r, attempt * 2000)); continue; }
          alert((e?.response?.data?.detail || t("report.payProcessError")) +
                `\n결제가 완료되셨다면 접수번호 ${saved.consultationId} 로 문의해 주세요.`);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(()=>{
    // 접수 직후엔 서버가 아직 리포트를 만드는 중(202)일 수 있어 준비될 때까지 재조회
    let stop = false;
    let timer: any = null;
    let tries = 0;
    const fetchReport = () => {
      axios.get(`${API}/api/v1/client/report/${id}`)
        .then(r=>{
          if(stop) return;
          if(r.status===202 || r.data?.generating){
            tries += 1;
            setGenerating(true);
            setLoading(false);
            if(tries < 60) timer = setTimeout(fetchReport, 3000);   // 최대 3분
            else { setGenerating(false); setError(t("report.loadError")); }
            return;
          }
          setGenerating(false);
          setData(r.data);
          setLoading(false);
        })
        .catch(e=>{
          if(stop) return;
          setError(e.response?.data?.detail||t("report.loadError"));
          setLoading(false);
        });
    };
    fetchReport();
    return ()=>{ stop = true; if(timer) clearTimeout(timer); };
  },[id]);

  if(error)  return<><Global/><LoadingWrap style={{color:"#e74c3c"}}>{error}</LoadingWrap></>;
  if(generating)return<><Global/><LoadingWrap>{t("report.generating")}</LoadingWrap></>;
  if(loading)return<><Global/><LoadingWrap>{t("report.loading")}</LoadingWrap></>;
  if(!data)  return null;

  const isCompat = ["연애궁합","심층궁합","간단사주"].includes(data.service_type||"");
  const myWx = data.saju?.wuxing_analysis;   // 오행 오각형 다이어그램용 (종합운세 등 비궁합 리포트)
  // 새 리포트는 본문(iframe) 상단에 기본정보+오행이 이미 포함됨. 구버전(캐시) 리포트는 카드로 폴백 표시.
  const hasTopBlock = !!data.report_content && data.report_content.includes("<!--WY_TOPBLOCK-->");
  const daeunScores: any[] = data.ai_scores?.daeun_scores ?? [];
  const rawSvcType: string = data.service_type || "종합운세";
  // "퀵사주_연애운" → "연애운", "쿠키사주_재물운" → "재물운"
  const svcType: string = rawSvcType.replace(/^퀵사주_|^쿠키사주_/, "");

  // 종합운세만 대운 아코디언 사용
  const SVC_DAEUN_FILTER: Record<string,string[]|undefined> = { "종합운세": undefined };
  const DAEUN_SVC_TYPES = ["종합운세"];
  const NARRATIVE_SVC_TYPES = ["신년운세","연애운","사업운","사업(재물운)","직업운","건강운"];

  // 내러티브 컨텍스트
  const narrativeCtx: NarrCtx = {
    useGod:   data.ai_scores?.detail?.use_god   || undefined,
    avoidGod: data.ai_scores?.detail?.avoid_god || undefined,
    gender:   data.gender || undefined,
    svcType,
  };

  // 서비스 타입별 스토리텔링 헤드라인
  const STORY: Record<string,{orb:string;title:string;sub:string}> = {
    "심층궁합":    {orb:"", title:t("report.storyTitleDeepCompat"),    sub:t("report.storySubDeepCompat")},
    "연애궁합":    {orb:"", title:t("report.storyTitleLoveCompat"),    sub:""},
    "연애운":      {orb:"❤️", title:t("report.storyTitleLove"),          sub:t("report.storySubLove")},
    "신년운세":    {orb:"", title:t("report.storyTitleNewYear"),       sub:t("report.storySubNewYear")},
    "사업운":      {orb:"", title:t("report.storyTitleBusiness"),      sub:t("report.storySubBusiness")},
    "사업(재물운)":{orb:"", title:t("report.storyTitleBusiness"),      sub:t("report.storySubWealth")},
    "직업운":      {orb:"🏆", title:t("report.storyTitleCareer"),        sub:t("report.storySubCareer")},
    "건강운":      {orb:"", title:t("report.storyTitleHealth"),        sub:t("report.storySubHealth")},
    "종합운세":    {orb:"", title:t("report.storyTitleComprehensive"), sub:t("report.storySubComprehensive")},
  };
  const story = STORY[svcType] ?? STORY["종합운세"];

  // 열람 기간 안내 배너는 제거(2026-08-03) — 저장 안내는 상단 SaveNotice가 대신함

  return(
    <>
      <Global/>
      <DancheongBarR />
      <Wrap>
        {/* 리포트는 재발급 안 됨 — 맨 위에 저장 안내 */}
        <SaveNotice>
          <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
          <SaveNoticeBody>{t("common.saveWarnAfter")}</SaveNoticeBody>
          <SaveNoticeBtn onClick={() => saveReport(data.report_content, `우연사주_${data.name || "리포트"}`)}>{t("common.saveWarnBtn")}</SaveNoticeBtn>
          {isInAppBrowser() && (
            <div style={{ marginTop: 8, fontSize: ".75rem", lineHeight: 1.5, color: "#7C2D12" }}>
              {t("common.saveWarnInApp")}
            </div>
          )}
        </SaveNotice>

        {/* 기본 정보 — 구버전 리포트 폴백. 새 리포트(궁합 포함)는 iframe 본문 최상단에 들어 있음 */}
        {!hasTopBlock && (
          <Card>
            <CardLabel>{t("report.basicInfo")}</CardLabel>
            <IGrid>
              <IK>{t("report.name")}</IK><IV>{data.name}</IV>
              <IK>{t("report.gender")}</IK><IV>{data.gender==="female"?t("report.female"):t("report.male")}</IV>
              {(() => {
                // 접수 시 음력으로 입력했어도 사주 기준은 양력 — 둘 다 표기(구버전 데이터는 기존대로)
                const bi = data.saju?.basic_info || {};
                const fmt = (s: string) => (s ? String(s).replace(/-/g, ".") : "");
                const solar = fmt(bi.solar_date || "");
                const lunar = fmt(bi.lunar_date || "");
                if (!solar && !lunar) return <><IK>{t("report.birthDate")}</IK><IV>{data.birth}</IV></>;
                const timeTxt = (bi.hour_unknown || bi.birth_hour === null || bi.birth_hour === undefined)
                  ? t("report.birthTimeUnknown")
                  : `${String(bi.birth_hour).padStart(2,"0")}:${String(bi.birth_minute ?? 0).padStart(2,"0")}`;
                return (
                  <>
                    {solar && <><IK>{t("report.birthSolar")}</IK><IV>{solar}</IV></>}
                    {lunar && <><IK>{t("report.birthLunar")}</IK><IV>{lunar}{bi.lunar_leap ? ` (${t("report.leapMonth")})` : ""}</IV></>}
                    <IK>{t("report.birthTime")}</IK><IV>{timeTxt}</IV>
                  </>
                );
              })()}
              {data.city && <><IK>{t("report.birthplace")}</IK><IV>{data.city}</IV></>}
              {isCompat && data.partner_info && (
                <>
                  <IK>{t("report.partner")}</IK><IV>{data.partner_info.name} ({data.partner_info.gender==="female"?t("report.femaleShort"):t("report.maleShort")})</IV>
                  <IK>{t("report.partnerBirth")}</IK>
                  <IV>{data.partner_info.birth_year}.{String(data.partner_info.birth_month||0).padStart(2,"0")}.{String(data.partner_info.birth_day||0).padStart(2,"0")}</IV>
                </>
              )}
            </IGrid>
          </Card>
        )}

        {/* 오행 분석 다이어그램 — 구버전 일반 리포트 폴백 (새 리포트는 iframe 내부에 포함) */}
        {!isCompat && myWx && !hasTopBlock && (
          <Card>
            <CardLabel>☯️ {t("report.ohaengAnalysis")}</CardLabel>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <OePentagon
                wx={myWx}
                name={data.name}
                uid="my"
                dayOe={STEM_OHAENG[data.saju?.saju_palja?.day_pillar?.stem || ""] || "목"}
              />
            </div>
          </Card>
        )}

        {/* ── 사주 분석 리포트 (새 디자인 · '정밀 명리학 기반 운세분석' 헤더 포함) ── */}
        {data.report_content && !data.report_content.includes("GEMINI_API_KEY") && (
          <iframe
            srcDoc={data.report_content}
            title={t("report.iframeTitle")}
            style={{
              width: "100%", border: "none", borderRadius: 16,
              minHeight: 800, display: "block",
              boxShadow: "none",
              marginBottom: 16,
            }}
            onLoad={(e) => {
              const iframe = e.currentTarget;
              try {
                const doc = iframe.contentDocument;
                if (doc) {
                  const h = doc.documentElement?.scrollHeight;
                  if (h && h > 100) iframe.style.height = h + 40 + "px";
                  // iframe 내부 링크는 모두 새 탭으로 열기
                  doc.querySelectorAll("a").forEach((a) => {
                    a.setAttribute("target", "_blank");
                    a.setAttribute("rel", "noopener noreferrer");
                  });
                }
              } catch { /* cross-origin 무시 */ }
            }}
          />
        )}

        {/* ── 서비스 타입별 스토리텔링 인트로 (전체 리포트가 있으면 생략) ── */}
        {!data.report_content && (() => {
          const ptName = data.partner_info?.name || t("report.partnerDefault");
          const SCENARIOS: Record<string,{color:string;items:{label:string;text:string}[]}> = {
            "재회운": {
              color:"#E03131",
              items:[
                {label:"", text:t("report.scenarioRejoin0", { ptName })},
                {label:"", text:t("report.scenarioRejoin1")},
                {label:"", text:t("report.scenarioRejoin2")},
              ],
            },
            "결혼운": {
              color:"#E64980",
              items:[
                {label:"", text:t("report.scenarioMarry0", { name: data.name })},
                {label:"", text:t("report.scenarioMarry1")},
                {label:"", text:t("report.scenarioMarry2")},
              ],
            },
            "심층궁합": {
              color:"#0CA678",
              items:[
                {label:"", text:t("report.scenarioDeep0")},
                {label:"", text:t("report.scenarioDeep1")},
                {label:"", text:t("report.scenarioDeep2")},
              ],
            },
            "사업(재물)운": {
              color:"#2F9E44",
              items:[
                {label:"", text:t("report.scenarioBiz0")},
                {label:"", text:t("report.scenarioBiz1")},
                {label:"", text:t("report.scenarioBiz2")},
              ],
            },
            "직업운": {
              color:"#F59F00",
              items:[
                {label:"", text:t("report.scenarioCareer0")},
                {label:"", text:t("report.scenarioCareer1")},
                {label:"", text:t("report.scenarioCareer2")},
              ],
            },
          };
          const scenario = SCENARIOS[svcType];
          if (!scenario) return null;
          return (
            <StoryCard $color={scenario.color}>
              <StoryScenarioTitle>
                {story.orb} {t(`svc.${svcType}`)}
              </StoryScenarioTitle>
              {scenario.items.map((item,i) => (
                <div key={i} style={{fontSize:".82rem",color:"#2B2A26",lineHeight:1.8,marginBottom:i<scenario.items.length-1?6:0}}>
                  {item.text}
                </div>
              ))}
            </StoryCard>
          );
        })()}

        {/* ── 서비스별 날짜 분석 (블러 결제) ── */}
        {["재회운","결혼운","사업운","직업운"].includes(svcType) && (() => {
          const useGod   = data.ai_scores?.detail?.use_god   || "";
          const avoidGod = data.ai_scores?.detail?.avoid_god || "";
          const johuGod  = data.ai_scores?.detail?.johu_god  || "";
          const OE_ALL: Record<string,string> = {...STEM_OHAENG,...BRANCH_OHAENG};
          const _GREG = [2,3,4,5,6,7,8,9,10,11,12,1];

          // 상대방 일주 계산 (재회운/결혼운)
          const ptInfo = data.partner_info;
          const ptDayGz = ptInfo?.birth_year
            ? getDayGanzhi(ptInfo.birth_year, ptInfo.birth_month, ptInfo.birth_day)
            : null;
          // 천간합/천간충
          const STEM_HAP: Record<string,string>  = {甲:"己",己:"甲",乙:"庚",庚:"乙",丙:"辛",辛:"丙",丁:"壬",壬:"丁",戊:"癸",癸:"戊"};
          const STEM_CHUNG: Record<string,string> = {甲:"庚",庚:"甲",乙:"辛",辛:"乙",丙:"壬",壬:"丙",丁:"癸",癸:"丁"};
          const JI_HAP: Record<string,string>     = {子:"丑",丑:"子",寅:"亥",亥:"寅",卯:"戌",戌:"卯",辰:"酉",酉:"辰",巳:"申",申:"巳",午:"未",未:"午"};
          const JI_CHUNG: Record<string,string>   = {子:"午",午:"子",丑:"未",未:"丑",寅:"申",申:"寅",卯:"酉",酉:"卯",辰:"戌",戌:"辰",巳:"亥",亥:"巳"};

          // 월운 전체 수집
          const allMonths = daeunScores.flatMap((d:any) =>
            (d.saeun_scores??[]).flatMap((sy:any) =>
              (sy.month_scores??[]).map((ms:any) => ({
                ...ms, year: sy.year,
                gm: ms.greg_month ?? _GREG[ms.month-1],
              }))
            )
          );
          const now = new Date();
          const scoreKey = (svcType==="재회운"||svcType==="결혼운") ? "love"
                         : svcType==="사업운" ? "business" : "career";

          const future = allMonths.filter(ms =>
            new Date(ms.year, ms.gm-1, 1) >= new Date(now.getFullYear(), now.getMonth(), 1)
          );
          const topMonths = [...future]
            .sort((a,b) => (b.scores?.[scoreKey]??0)-(a.scores?.[scoreKey]??0))
            .slice(0, 5);

          // 각 월에서 최적 일 계산
          const bestDates = topMonths.map(ms => {
            const daysInMonth = new Date(ms.year, ms.gm, 0).getDate();
            let best = {day:1, sc:-1, gz:{gan:"",ji:""},hapPartner:false};
            for(let day=1; day<=daysInMonth; day++){
              const gz = getDayGanzhi(ms.year, ms.gm, day);
              const gOe = OE_ALL[gz.gan]||"";
              const jOe = OE_ALL[gz.ji] ||"";
              let sc = 0;
              if(useGod && (gOe===useGod||jOe===useGod)) sc+=4;
              if(johuGod && (gOe===johuGod||jOe===johuGod)) sc+=2;
              if(avoidGod && gOe!==avoidGod && jOe!==avoidGod) sc+=1;
              // 상대방 일지와 합 여부
              let hapP = false;
              if(ptDayGz){
                if(STEM_HAP[gz.gan]===ptDayGz.gan || JI_HAP[gz.ji]===ptDayGz.ji) hapP=true;
                if(STEM_CHUNG[gz.gan]===ptDayGz.gan || JI_CHUNG[gz.ji]===ptDayGz.ji) sc-=2;
              }
              if(hapP) sc+=3;
              if(sc>best.sc) best={day,sc,gz,hapPartner:hapP};
            }
            return {year:ms.year,month:ms.gm,day:best.day,gz:best.gz,hapPartner:best.hapPartner,monthScore:ms.scores?.[scoreKey]??0};
          }).sort((a,b)=> new Date(a.year,a.month-1,1).getTime()-new Date(b.year,b.month-1,1).getTime()).slice(0,3);

          // 위험일 (과거 — 재회운용)
          const riskDates = svcType==="재회운" ? (() => {
            const past = allMonths.filter(ms => {
              const d = new Date(ms.year, ms.gm-1, 1);
              return d < new Date(now.getFullYear(), now.getMonth(), 1);
            });
            return [...past].sort((a,b)=>(a.scores?.[scoreKey]??0)-(b.scores?.[scoreKey]??0)).slice(0,2)
              .map(ms => {
                const daysInMonth=new Date(ms.year,ms.gm,0).getDate();
                let worst={day:1,sc:9999,gz:{gan:"",ji:""}};
                for(let day=1;day<=daysInMonth;day++){
                  const gz=getDayGanzhi(ms.year,ms.gm,day);
                  const gOe=OE_ALL[gz.gan]||"";const jOe=OE_ALL[gz.ji]||"";
                  let sc=0;
                  if(avoidGod&&(gOe===avoidGod||jOe===avoidGod)) sc+=3;
                  if(ptDayGz&&(JI_CHUNG[gz.ji]===ptDayGz.ji)) sc+=2;
                  if(sc>worst.sc||worst.sc===9999) worst={day,sc,gz};
                }
                return {year:ms.year,month:ms.gm,day:worst.day,gz:worst.gz};
              }).sort((a,b)=>new Date(a.year,a.month-1,1).getTime()-new Date(b.year,b.month-1,1).getTime());
          })() : [];

          // 직업/사업 추천
          const CAREER_BY_OE: Record<string,string[]> = {
            "목":[t("report.careerWood0"),t("report.careerWood1"),t("report.careerWood2"),t("report.careerWood3"),t("report.careerWood4")],
            "화":[t("report.careerFire0"),t("report.careerFire1"),t("report.careerFire2"),t("report.careerFire3"),t("report.careerFire4")],
            "토":[t("report.careerEarth0"),t("report.careerEarth1"),t("report.careerEarth2"),t("report.careerEarth3"),t("report.careerEarth4")],
            "금":[t("report.careerMetal0"),t("report.careerMetal1"),t("report.careerMetal2"),t("report.careerMetal3"),t("report.careerMetal4")],
            "수":[t("report.careerWater0"),t("report.careerWater1"),t("report.careerWater2"),t("report.careerWater3"),t("report.careerWater4")],
          };
          const BIZ_BY_OE: Record<string,string[]> = {
            "목":[t("report.bizWood0"),t("report.bizWood1"),t("report.bizWood2"),t("report.bizWood3"),t("report.bizWood4")],
            "화":[t("report.bizFire0"),t("report.bizFire1"),t("report.bizFire2"),t("report.bizFire3"),t("report.bizFire4")],
            "토":[t("report.bizEarth0"),t("report.bizEarth1"),t("report.bizEarth2"),t("report.bizEarth3"),t("report.bizEarth4")],
            "금":[t("report.bizMetal0"),t("report.bizMetal1"),t("report.bizMetal2"),t("report.bizMetal3"),t("report.bizMetal4")],
            "수":[t("report.bizWater0"),t("report.bizWater1"),t("report.bizWater2"),t("report.bizWater3"),t("report.bizWater4")],
          };
          const recomList = svcType==="직업운"
            ? (CAREER_BY_OE[useGod]||CAREER_BY_OE["목"])
            : svcType==="사업운"
            ? (BIZ_BY_OE[useGod]||BIZ_BY_OE["목"])
            : [];

          const SVC_COLOR: Record<string,string> = {
            "재회운":"#E03131","결혼운":"#E64980","사업운":"#2F9E44","직업운":"#F59F00",
          };
          const color = SVC_COLOR[svcType]||"#B8862A";
          const ptName = ptInfo?.name||t("report.partnerDefault");

          // 포트원 결제 핸들러 (날짜 블러 해제용)
          const handlePayment = () => {
            const IMP = (window as any).IMP;
            if(!IMP){ alert(t("report.payModuleLoading")); return; }
            const _mu = `saju_date_${data.consultation_id||id}_${Date.now()}`;
            // 휴대폰 결제는 주소 이동으로 돌아와 화면 상태가 날아간다 — 먼저 적어둔다
            try {
              sessionStorage.setItem("reportDatePending", JSON.stringify({
                consultationId: data.consultation_id || Number(id), merchantUid: _mu,
              }));
            } catch { /* 저장 실패해도 PC 결제는 정상 동작 */ }
            requestPay({
              merchantUid: _mu,
              name: t("report.paymentName", { svc: t(`svc.${svcType}`) }),
              amount: 9900,
              buyerName: data.name || "",
              buyerEmail: data.email || "",
              // 돌아올 주소에 물음표를 달지 않는다(결제번호가 또 붙으면 주소가 깨진다)
              redirectUrl: `${window.location.origin}/report/${data.consultation_id||id}`,
            }).then(async (rsp) => {
              if(rsp.success){
                try {
                  await axios.post(`${API}/api/v1/client/payment-verify`, {
                    consultation_id: data.consultation_id || Number(id),
                    imp_uid: rsp.imp_uid,
                    merchant_uid: rsp.merchant_uid,
                  }, { timeout: 120000 });
                  setDateUnlocked(true);
                  try { sessionStorage.removeItem("reportDatePending"); } catch { /* noop */ }
                } catch (e: any) {
                  alert((e?.response?.data?.detail || t("report.payProcessError")) +
                        `\n결제가 완료되셨다면 접수번호 ${data.consultation_id||id} 로 문의해 주세요.`);
                }
              } else {
                alert(t("report.payFailed", { msg: rsp.error_msg||t("report.tryAgain") }));
              }
            });
          };

          if(!bestDates.length) return null;
          return (
            <DateAnalysisWrap>
              <DateAnalysisHeader $color={color}>
                <DateAnalysisTitle $color={color}>
                  {story.orb} {svcType === "재회운"  ? t("report.dateTitleRejoin", { ptName })
                              : svcType === "결혼운"  ? t("report.dateTitleMarry")
                              : svcType === "사업운"  ? t("report.dateTitleBusiness")
                              :                        t("report.dateTitleCareer")}
                </DateAnalysisTitle>
                <DateAnalysisSub>
                  {svcType==="재회운" || svcType==="결혼운"
                    ? t("report.dateSubWithPartner", { name: data.name, useGod, ptName })
                    : t("report.dateSubSelf", { name: data.name, useGod })}
                </DateAnalysisSub>
              </DateAnalysisHeader>

              <DateAnalysisBody>
                {/* 재회운 위험일 */}
                {svcType==="재회운" && riskDates.length>0 && (
                  <div style={{marginBottom:14,padding:"10px 12px",background:"#FBE0D9",borderRadius:10,border:"1px solid #2B2A26"}}>
                    <div style={{fontSize:".72rem",fontWeight:800,color:"#D0654F",marginBottom:6}}>⚡ {t("report.emotionalConflictDays")}</div>
                    {riskDates.map((rd,i) => (
                      <div key={i} style={{fontSize:".72rem",color:"#7a2020",marginBottom:2}}>
                        · <DateBlurWrap $locked={!dateUnlocked}>
                            {t("report.dateGanzhi", { y: rd.year, m: rd.month, d: rd.day, gan: rd.gz.gan, ji: rd.gz.ji })}
                          </DateBlurWrap>
                        {!dateUnlocked && <span style={{marginLeft:4,fontSize:".65rem",color:"#B8862A"}}>🔒</span>}
                        {dateUnlocked && <span style={{marginLeft:6,fontSize:".65rem",color:"#8C8677"}}>{t("report.giChungDay", { avoidGod })}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* 최적일 TOP 3 */}
                <div style={{fontSize:".72rem",fontWeight:700,color:"#8C8677",marginBottom:8}}>
                  {svcType==="재회운" ? t("report.top3Rejoin")
                  : svcType==="결혼운" ? t("report.top3Marry")
                  : svcType==="사업운" ? t("report.top3Business")
                  :                     t("report.top3Career")}
                </div>
                {bestDates.map((bd,i) => (
                  <DateRow key={i}>
                    <DateRank $color={color}>{i+1}</DateRank>
                    <DateContent>
                      <DateLabel>
                        <DateBlurWrap $locked={!dateUnlocked}>
                          {t("report.dateYmd", { y: bd.year, m: bd.month, d: bd.day })}
                        </DateBlurWrap>
                        {!dateUnlocked && <span style={{marginLeft:6,fontSize:".65rem",color:"#B8862A"}}>🔒 {t("report.unlockAfterPayment")}</span>}
                      </DateLabel>
                      <DateSub>
                        {dateUnlocked
                          ? `${t("report.dateActiveDesc", { gan: bd.gz.gan, ji: bd.gz.ji, useGod })}${bd.hapPartner ? t("report.partnerDayHap", { ptName }) : ""}`
                          : t("report.monthScoreKeyDate", { score: bd.monthScore })}
                      </DateSub>
                    </DateContent>
                  </DateRow>
                ))}

                {/* 추천 직종/업종 */}
                {recomList.length > 0 && (
                  <RecomWrap>
                    <RecomTitle>
                      {svcType==="직업운" ? t("report.favorableJobs", { useGod }) : t("report.favorableBiz", { useGod })}
                    </RecomTitle>
                    <RecomChips>
                      {recomList.map((r,i) => (
                        <RecomChip key={i} $color={color}>{r}</RecomChip>
                      ))}
                    </RecomChips>
                  </RecomWrap>
                )}

                {/* 결제 CTA */}
                {!dateUnlocked && (
                  <PayCTA>
                    <img loading="lazy" decoding="async" src="/woo_y_3.webp" alt={t("report.altSealed")} style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 8, opacity: 0.85 }} />
                    <PayCTAText>
                      {t("report.payCtaQuestion")}<br />
                      {t("report.payCtaReveal")}
                    </PayCTAText>
                    <PayBtn $color={color} onClick={handlePayment}>
                      {t("report.payButton")}
                    </PayBtn>
                  </PayCTA>
                )}
              </DateAnalysisBody>
            </DateAnalysisWrap>
          );
        })()}

        {/* ── 궁합 리포트 ── */}
        {/* 옛 궁합 요약 섹션 제거(2026-08-03) — 궁합 점수·해설은 리포트 본문(iframe)에 이미 들어 있고,
            여기 점수는 예전 계산값이라 본문과 달라 혼란을 줬다. */}
        {false && isCompat && <CompatReport data={data}/>}

        {/* ── 대운별 리포트 (온라인·방문 공통, 관리자가 대운 비활성화한 경우만 제외) ── */}

        {/* ── 세운/대운 아코디언 제거 (쿠키사주 결과지와 디자인 통일) — false 가드로 비활성화 ── */}
        {false && !isCompat && svcType === "종합운세" && data.report_config?.saeun_enabled && (() => {
          const saeunSel: number[] = data.report_config?.saeun_selected ?? [];
          const wolunSel: number[] = data.report_config?.wolun_selected ?? [];
          const allSaeun = daeunScores.flatMap((d:any) =>
            (d.saeun_scores ?? []).filter((sy:any) =>
              saeunSel.length === 0 ? false : saeunSel.includes(sy.year)
            ).map((sy:any) => ({...sy, daeun_label: d.label}))
          );
          if (!allSaeun.length) return null;
          const ALL_SAEUN_KEYS = [
            {key:"love",label:t("report.themeLoveFull")},{key:"marry",label:t("report.themeMarryFull")},
            {key:"business",label:t("report.themeBusinessFull")},{key:"career",label:t("report.themeCareerFull")},
            {key:"health",label:t("report.themeHealthFull")},{key:"wealth",label:t("report.themeWealthFull")},
          ];
          const ALL_MONTH_KEYS = [
            {key:"love",label:t("report.themeLove")},{key:"marry",label:t("report.themeMarry")},
            {key:"business",label:t("report.themeBusiness")},{key:"career",label:t("report.themeCareer")},
            {key:"health",label:t("report.themeHealth")},{key:"wealth",label:t("report.themeWealth")},
          ];
          const themeConf: Record<string,string[]> = data.report_config?.daeun_theme_config ?? {};
          return (
            <SaeunWrap>
              <SaeunSectionTitle>{t("report.saeunDetail")}</SaeunSectionTitle>
              {allSaeun.map((sy:any) => {
                const enabledThemes: string[] | null = themeConf[sy.daeun_label] ?? null;
                const saeunKeys = enabledThemes
                  ? ALL_SAEUN_KEYS.filter(k => enabledThemes.includes(k.key))
                  : ALL_SAEUN_KEYS;
                const monthKeys = enabledThemes
                  ? ALL_MONTH_KEYS.filter(k => enabledThemes.includes(k.key))
                  : ALL_MONTH_KEYS;
                return (
                <SaeunCard2 key={sy.year} $yong={sy.is_yong&&!sy.is_gi} $gi={sy.is_gi&&!sy.is_yong}>
                  <SaeunYear2>{sy.year_label}</SaeunYear2>
                  {saeunKeys.map(({key,label}) => {
                    const sc: number = sy.scores?.[key] ?? 0;
                    const iconMap: Record<string,string> = {
                      love:"♥",marry:"",business:"💼",career:"🏆",health:"",wealth:"",
                    };
                    return (
                      <ThemeBlock key={key} style={{marginBottom:8}}>
                        <ThemeHeader2>
                          <span style={{fontSize:"1rem"}}>{iconMap[key]||"✦"}</span>
                          <span style={{flex:1,fontSize:".8rem",fontWeight:700,color:"#8C8677"}}>{label}</span>
                        </ThemeHeader2>
                        <CommentBox style={{fontSize:".8rem"}}>{makeNarrative(key, sc, [sy], undefined, lang)}</CommentBox>
                      </ThemeBlock>
                    );
                  })}
                  {/* 월운 */}
                  {data.report_config?.wolun_enabled && wolunSel.includes(sy.year) && sy.month_scores?.length && (() => {
                    const dayGanR = data.saju?.saju_palja?.day_pillar?.stem || "";
                    const useGodR = data.ai_scores?.detail?.use_god || "";
                    const avoidGodR = data.ai_scores?.detail?.avoid_god || "";
                    const johuGodR = data.ai_scores?.detail?.johu_god || "";
                    const _OE_ALL: Record<string,string> = {...STEM_OHAENG,...BRANCH_OHAENG};
                    return (
                    <div style={{marginTop:12}}>
                      <div style={{fontSize:".68rem",color:"#8C8677",fontWeight:700,marginBottom:6,letterSpacing:".08em"}}>
                        {t("report.wolunTitle")} <span style={{fontSize:".6rem",fontWeight:400,color:"#8C8677"}}>{t("report.wolunTapHint")}</span>
                      </div>
                      <WolunGrid>
                        {[...(sy.month_scores as any[])].sort((a:any,b:any)=>{
                          const _G=[2,3,4,5,6,7,8,9,10,11,12,1];
                          const ag=a.greg_month??_G[a.month-1];
                          const bg=b.greg_month??_G[b.month-1];
                          return ag-bg;
                        }).map((ms:any) => {
                          const mKey = `${sy.year}-${ms.month}`;
                          const isSelM = selectedWolunKey === mKey;
                          return (
                          <WolunCell
                            key={ms.month}
                            $yong={ms.is_yong&&!ms.is_gi}
                            $gi={ms.is_gi&&!ms.is_yong}
                            onClick={() => setSelectedWolunKey(p => p===mKey ? null : mKey)}
                            style={{cursor:"pointer",outline:isSelM?"2px solid #2B2A26":"none",outlineOffset:2}}
                          >
                            <WolunLabel2>{ms.month_label}</WolunLabel2>
                            <WolunScores>
                              {monthKeys.map(({key,label}) => (
                                <WolunScore key={key} $score={ms.scores?.[key]??0}>
                                  {label}
                                </WolunScore>
                              ))}
                            </WolunScores>
                          </WolunCell>
                          );
                        })}
                      </WolunGrid>
                      {/* 일운 달력 */}
                      {selectedWolunKey && selectedWolunKey.startsWith(`${sy.year}-`) && (() => {
                        const selSajuMonth = parseInt(selectedWolunKey.split('-')[1]);
                        const selMs = (sy.month_scores as any[]).find((m:any) => m.month === selSajuMonth);
                        if (!selMs) return null;
                        const _GREG = [2,3,4,5,6,7,8,9,10,11,12,1];
                        const calMonthRaw = selMs.greg_month ?? _GREG[selSajuMonth - 1];
                        const calYear = calMonthRaw === 1 && selSajuMonth === 12 ? sy.year + 1 : sy.year;
                        const calMonth = calMonthRaw;
                        const daysInMo = new Date(calYear, calMonth, 0).getDate();
                        const firstDow = new Date(calYear, calMonth - 1, 1).getDay();
                        const dayScoreMapR: Record<number, number> = {};
                        (selMs.day_scores || []).forEach((ds: {d: number; s: number}) => {
                          dayScoreMapR[ds.d] = ds.s;
                        });
                        const dayItems = Array.from({length:daysInMo},(_,i) => {
                          const d = i + 1;
                          const gz = getDayGanzhi(calYear, calMonth, d);
                          const gOe = _OE_ALL[gz.gan] || '';
                          const jOe = _OE_ALL[gz.ji]  || '';
                          const isYong = gOe===useGodR || jOe===useGodR;
                          const isJohu = johuGodR && (gOe===johuGodR || jOe===johuGodR);
                          const isGi   = gOe===avoidGodR || jOe===avoidGodR;
                          const kind = isYong&&!isGi ? 'yong' : isJohu&&!isGi ? 'johu' : isGi&&!isYong ? 'gi' : 'neutral';
                          const score = dayScoreMapR[d] ?? null;
                          return {d, gz, gOe, jOe, kind, score};
                        });
                        const weekLabels = [t("report.weekSun"),t("report.weekMon"),t("report.weekTue"),t("report.weekWed"),t("report.weekThu"),t("report.weekFri"),t("report.weekSat")];
                        const kindStyle: Record<string,{bg:string;border:string}> = {
                          yong:    {bg:'#E7F0E2',border:'#6dbf8a'},
                          johu:    {bg:'#FBF6EA',border:'#e8c44a'},
                          gi:      {bg:'#FBE0D9',border:'#f08080'},
                          neutral: {bg:'#FBF6EA',border:'#EAE3D2'},
                        };
                        const OE_COLOR_R: Record<string,string> = {목:"#2E86AB",화:"#D0654F",토:"#B8862A",금:"#888",수:"#1D3557"};
                        return (
                          <div style={{marginTop:12,padding:'12px 12px',background:'#FBF6EA',borderRadius:12,border:'1.5px solid #2B2A2650'}}>
                            <div style={{fontSize:12,fontWeight:800,color:'#8C8677',marginBottom:10,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                              <span>{t("report.ilunCalendar", { y: calYear, m: calMonth })}</span>
                              <span style={{fontSize:10,fontWeight:400,color:'#8C8677'}}>{t("report.ganziMonth", { gan: selMs.gan, ji: selMs.ji })}</span>
                            </div>
                            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
                              {weekLabels.map(w=>(
                                <div key={w} style={{textAlign:'center',fontSize:10,color:'#8C8677',padding:'2px 0',fontWeight:700}}>{w}</div>
                              ))}
                              {Array.from({length:firstDow},(_,i)=><div key={`ep-${i}`}/>)}
                              {dayItems.map(({d,gz,gOe,jOe,kind,score})=>{
                                const {bg,border}=kindStyle[kind];
                                const ts = dayGanR ? calcTenStar(dayGanR, gz.gan) : '';
                                const scoreColor = score === null ? '#aaa' : score >= 65 ? '#2a7a4a' : score >= 50 ? '#888' : '#c04040';
                                return(
                                  <div key={d} style={{background:bg,border:`1.5px solid ${border}`,borderRadius:6,padding:'4px 2px',textAlign:'center',fontSize:9,lineHeight:1.4}}>
                                    <div style={{fontWeight:700,color:'#2B2A26',fontSize:10}}>{d}</div>
                                    <div style={{color:OE_COLOR_R[gOe]||'#2B2A26'}}>{gz.gan}</div>
                                    <div style={{color:OE_COLOR_R[jOe]||'#2B2A26'}}>{gz.ji}</div>
                                    {ts && <div style={{color:'#8C8677',fontSize:8}}>{ts}</div>}
                                    {score !== null && (
                                      <div style={{color:scoreColor,fontSize:8,fontWeight:700,marginTop:1}}>{score}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{marginTop:8,display:'flex',gap:6,fontSize:10,flexWrap:'wrap'}}>
                              <span style={{background:'#E7F0E2',border:'1px solid #2B2A26',borderRadius:4,padding:'1px 6px'}}>● {t("report.legendYongil")}</span>
                              <span style={{background:'#FBF6EA',border:'1px solid #2B2A26',borderRadius:4,padding:'1px 6px'}}>● {t("report.legendJohuil")}</span>
                              <span style={{background:'#FBE0D9',border:'1px solid #2B2A26',borderRadius:4,padding:'1px 6px'}}>● {t("report.legendGiil")}</span>
                              <span style={{background:'#FBF6EA',border:'1px solid #2B2A26',borderRadius:4,padding:'1px 6px'}}>● {t("report.legendNeutral")}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })()}
                </SaeunCard2>
                );
              })}
            </SaeunWrap>
          );
        })()}

        {/* 상담사 코멘트 */}
        {data.counselor_notes && (
          <Card style={{marginTop:16}}>
            <CardLabel>{t("report.counselorComment")}</CardLabel>
            <CommentBox>{data.counselor_notes}</CommentBox>
          </Card>
        )}

        {/* ── 하단 네비게이션 버튼 (온라인사주 전용) ── */}
        {!rawSvcType.includes("퀵사주") && (
          <div style={{ display: "flex", gap: 10, padding: "8px 16px 32px" }}>
            <button
              onClick={() => window.location.href = "/"}
              style={{
                flex: 1, padding: "13px",
                background: "#fff", border: "1.5px solid #2B2A26",
                borderRadius: 50, fontSize: "0.88rem", fontWeight: 700,
                color: "#8C8677", cursor: "pointer", fontFamily: "inherit",
              }}>
              {t("report.toMain")}
            </button>
            <button
              onClick={() => window.location.href = "/client"}
              style={{
                flex: 2, padding: "13px",
                background: "#B8862A",
                border: "none", borderRadius: 50, fontSize: "0.88rem", fontWeight: 800,
                color: "#fff", cursor: "pointer", fontFamily: "inherit",
                boxShadow: "none",
              }}>
              {t("report.viewOtherReadings")}
            </button>
          </div>
        )}

        <SiteFooter />
      </Wrap>
      <PrintBtn className="no-print" onClick={() => saveReport(data.report_content, `우연사주_${data.name || "리포트"}`)}>📄 {t("report.savePdf")}</PrintBtn>
    </>
  );
}
